import PageGlow from "../components/PageGlow";
import Pager, { pageSlice } from "../components/Pager";
import LiveTraffic from "../components/LiveTraffic";
import { Ops } from "../components/ControlTowerOps";
import { SecurityAbuse } from "../components/ControlTowerSecurity";
import { PeopleAnalytics } from "../components/ControlTowerPeople";
import { ChevronDown } from "lucide-react";

// Control Tower sections grouped into departments (desktop nav). Tab ids are
// unchanged — this only organises the navigation.
const CT_DEPTS = [
  ["Main", ["overview"]],
  ["Operations", ["ops", "jobs", "verifications", "indexing", "syndication"]],
  ["People", ["users", "people", "talent", "cvarchive"]],
  ["Money", ["payments", "pricing", "promos"]],
  ["Comms", ["inbox", "messages", "chats", "emails", "blasts", "campaigns", "marketing", "reviews"]],
  ["Insight", ["analytics", "livetraffic", "traffic", "insights"]],
  ["Security", ["security"]],
];
import { MfaSetupCard } from "../components/MfaSetupCard";
import { TalentDirectory } from "../components/TalentDirectory";
import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Check, X, Pencil, Trash2, MailOpen, BadgeCheck, Send, MailPlus, Star, ShieldAlert, RefreshCw } from "lucide-react";
import { api, formatApiError, formatSalary, fileUrl, timeAgo } from "../lib/api";
import Seo from "../components/Seo";
import ImageLightbox from "../components/ImageLightbox";
import ProtectedRoute from "../components/ProtectedRoute";
import CandidatePanel from "../components/CandidatePanel";
import JobReport from "../components/JobReport";
import TrafficAnalytics from "../components/TrafficAnalytics";
import { MobileMenuHome, MobileBackHeader } from "../components/MobileDashMenu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const EmployerCardBody = ({ c }) => {
  const ageDays = c.created_at ? Math.max(0, Math.floor((Date.now() - new Date(c.created_at).getTime()) / 86400000)) : null;
  const lastSeen = c.last_seen ? new Date(c.last_seen).toLocaleDateString("en-GB") : "";
  return (
    <>
      <span className="block font-semibold">
        {c.company_name || c.name}
        <span className={c.verification === "verified" ? "text-green-400" : "text-amber-300"}>
          {" · "}{c.verification === "verified" ? "verified" : `verification: ${c.verification}`}
        </span>
      </span>
      <span className="block">{c.name}{c.email ? ` · ${c.email}` : ""}</span>
      <span className="block mt-1">
        Account: {ageDays === null ? "—" : `${ageDays} days old`}{lastSeen ? ` · last active ${lastSeen}` : ""}
      </span>
      <span className="block">Listings: {c.jobs_total} total · {c.jobs_live} live · {c.jobs_rejected} rejected</span>
      <span className="block">Team: {c.team_size} member{c.team_size === 1 ? "" : "s"}</span>
      {c.moderation_flags > 0 && (
        <span className="block mt-1 text-amber-300 font-semibold">
          {c.moderation_flags} auto-blocked attempt{c.moderation_flags === 1 ? "" : "s"} by content rules
        </span>
      )}
    </>
  );
};

const EmployerHoverCard = ({ name, jobId, cards, onEnter }) => (
  <span className="group relative inline-block cursor-help underline decoration-dotted decoration-zinc-300 underline-offset-2"
    onMouseEnter={onEnter} data-testid={`employer-card-${jobId}`}>
    {name}
    <span className="pointer-events-none absolute bottom-full left-0 z-30 mb-1.5 w-72 max-w-[calc(100vw-2rem)] rounded-md bg-zinc-950 px-3 py-2.5 text-xs leading-relaxed text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100">
      {cards[jobId] ? <EmployerCardBody c={cards[jobId]} /> : "Loading…"}
    </span>
  </span>
);

const JOB_STATUS = {
  live: { label: "Live", cls: "bg-green-50 text-green-700" },
  pending_review: { label: "In review", cls: "bg-blue-50 text-blue-700" },
  awaiting_payment: { label: "Awaiting payment", cls: "bg-amber-50 text-amber-700" },
  rejected: { label: "Rejected", cls: "bg-red-50 text-red-700" },
  expired: { label: "Expired", cls: "bg-zinc-100 text-zinc-500" },
  closed: { label: "Closed", cls: "bg-zinc-100 text-zinc-500" },
  archived: { label: "Archived", cls: "bg-zinc-100 text-zinc-400" },
};

function ControlTowerInner() {
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab")
    || (window.innerWidth < 640 ? "menu" : "overview"));
  const [openDept, setOpenDept] = useState("");
  const [stats, setStats] = useState(null);
  const [offerForm, setOfferForm] = useState({ enabled: false, text: "" });
  const [offerSaving, setOfferSaving] = useState(false);
  const [promos, setPromos] = useState([]);
  const [pricingForm, setPricingForm] = useState(null);
  const [subPlans, setSubPlans] = useState(null);
  const [subPlansSaving, setSubPlansSaving] = useState(false);
  const [subsList, setSubsList] = useState([]);
  const [subStatusFilter, setSubStatusFilter] = useState("");
  const subStatusOf = (s) => {
    const nowD = new Date();
    const end = s.credit_period_end ? new Date(s.credit_period_end) : null;
    if (s.status === "past_due") return { key: "payment_failed", label: "Payment failed", cls: "bg-red-100 text-red-700" };
    if (s.status === "cancelling") return { key: "cancelling", label: end ? `Cancels ${end.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : "Cancelling", cls: "bg-amber-100 text-amber-700" };
    if (s.status === "cancelled") return { key: "cancelled", label: "Cancelled", cls: "bg-zinc-100 text-zinc-400" };
    if (s.status === "active" && end && end < nowD) return { key: "expired", label: "Expired", cls: "bg-zinc-200 text-zinc-500" };
    if (s.status === "active" && s.starts_at && new Date(s.starts_at) > nowD) return { key: "upcoming", label: "Upcoming", cls: "bg-blue-100 text-blue-700" };
    if (s.status === "active") return { key: "active", label: "Active", cls: "bg-green-100 text-green-700" };
    return { key: s.status || "other", label: s.status || "—", cls: "bg-zinc-100 text-zinc-500" };
  };
  const [customForm, setCustomForm] = useState({ employer_email: "", plan_name: "High Volume", included_jobs: 30, monthly_price: 0, extra_job_price: 12 });
  const [customBusy, setCustomBusy] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsFilters, setAnalyticsFilters] = useState({ days: 30, job_id: "", employer_id: "", source: "" });
  const [mfaRequired, setMfaRequired] = useState(false);
  useEffect(() => {
    const onReq = () => setMfaRequired(true);
    const onOk = () => setMfaRequired(false);
    window.addEventListener("ht:mfa-required", onReq);
    window.addEventListener("ht:mfa-enabled", onOk);
    return () => {
      window.removeEventListener("ht:mfa-required", onReq);
      window.removeEventListener("ht:mfa-enabled", onOk);
    };
  }, []);
  const [analyticsJobs, setAnalyticsJobs] = useState([]);
  const [analyticsEmployers, setAnalyticsEmployers] = useState([]);
  const [manageAppId, setManageAppId] = useState(null);
  const [reportJobId, setReportJobId] = useState(null);
  const [chats, setChats] = useState(null);
  const [chatSearch, setChatSearch] = useState("");
  const [chatOpen, setChatOpen] = useState(null);
  const [chatThread, setChatThread] = useState(null);
  const [paySummary, setPaySummary] = useState(null);
  const [payFilters, setPayFilters] = useState({ q: "", kind: "", status: "", from: "", to: "", tier: "", promo: "", year: "", month: "", week: "" });
  const [inboxStatus, setInboxStatus] = useState(null);
  const [inboxMsgs, setInboxMsgs] = useState(null);
  const [inboxOpen, setInboxOpen] = useState(null);
  const [inboxReply, setInboxReply] = useState("");
  const [inboxSending, setInboxSending] = useState(false);
  const [inboxNew, setInboxNew] = useState(0);
  const [payPage, setPayPage] = useState(1);
  const [payDetail, setPayDetail] = useState(null);
  const [payEdit, setPayEdit] = useState({ note: "", excluded: false, payment_status: "" });
  const [showExcluded, setShowExcluded] = useState(false);
  const [jobsPage, setJobsPage] = useState(1);
  const [usersPage, setUsersPage] = useState(1);
  const [verifPage, setVerifPage] = useState(1);
  const [msgPage, setMsgPage] = useState(1);
  const [chatsPage, setChatsPage] = useState(1);
  const [inboxPage, setInboxPage] = useState(1);
  const [cvPage, setCvPage] = useState(1);
  const [enqPage, setEnqPage] = useState(1);
  const [subsPage, setSubsPage] = useState(1);
  const [idxPage, setIdxPage] = useState(1);
  useEffect(() => { setPayPage(1); }, [payFilters]);

  const loadAnalytics = useCallback(() => {
    setTabLoading(true);
    const params = { days: analyticsFilters.days };
    if (analyticsFilters.job_id) params.job_id = analyticsFilters.job_id;
    if (analyticsFilters.employer_id) params.employer_id = analyticsFilters.employer_id;
    if (analyticsFilters.source) params.source = analyticsFilters.source;
    api.get("/admin/analytics", { params }).then((r) => setAnalytics(r.data)).catch(() => {}).finally(() => setTabLoading(false));
  }, [analyticsFilters]);

  useEffect(() => {
    if (tab !== "analytics") return;
    loadAnalytics();
    api.get("/admin/jobs").then((r) => setAnalyticsJobs(r.data)).catch(() => {});
    api.get("/admin/users", { params: { role: "employer", limit: 500 } }).then((r) => setAnalyticsEmployers(Array.isArray(r.data) ? r.data : (r.data?.users || []))).catch(() => {});
  }, [tab, loadAnalytics]);
  const [pricingSaving, setPricingSaving] = useState("");
  const [enquiries, setEnquiries] = useState([]);

  const loadSubData = () => {
    api.get("/admin/subscription-plans").then((r) => setSubPlans(r.data)).catch(() => {});
    api.get("/admin/subscriptions").then((r) => setSubsList(r.data)).catch(() => {});
    api.get("/admin/enterprise-enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
  };
  useEffect(() => { if (tab === "pricing") loadSubData(); }, [tab]);

  const [insightSources, setInsightSources] = useState(null);
  const [insightItems, setInsightItems] = useState([]);
  const [insightHealth, setInsightHealth] = useState([]);
  const [sourceForm, setSourceForm] = useState({ name: "", feed_url: "", category: "Careers news" });
  const [originalForm, setOriginalForm] = useState({ title: "", summary: "", body: "", category: "HiredToday advice", featured: false });
  const [insightBusy, setInsightBusy] = useState("");
  const [talentCfg, setTalentCfg] = useState(null);
  const [chReview, setChReview] = useState(null);
  const [chCorrectFor, setChCorrectFor] = useState(null);
  const [chCorrectForm, setChCorrectForm] = useState({ field: "company_name", value: "", reason: "" });
  const [chJoins, setChJoins] = useState([]);
  const [chCompanies, setChCompanies] = useState(null);
  const [chBusy, setChBusy] = useState("");
  const [talentOverview, setTalentOverview] = useState(null);
  const [talentCfgSaving, setTalentCfgSaving] = useState(false);

  const loadInsights = () => {
    api.get("/admin/insight-sources").then((r) => setInsightSources(r.data)).catch(() => {});
    api.get("/admin/insights").then((r) => setInsightItems(r.data)).catch(() => {});
    api.get("/admin/insights/health").then((r) => setInsightHealth(r.data)).catch(() => {});
  };
  const loadTalentAdmin = () => {
    api.get("/admin/talent/settings").then((r) => setTalentCfg(r.data)).catch(() => {});
    api.get("/admin/talent/overview").then((r) => setTalentOverview(r.data)).catch(() => {});
  };
  useEffect(() => {
    if (tab === "insights") loadInsights();
    if (tab === "talent") loadTalentAdmin();
    if (tab === "verifications") {
      api.get("/admin/companies-house/review").then((r) => setChReview(r.data)).catch(() => setChReview(null));
      api.get("/admin/companies-house/join-requests").then((r) => setChJoins(r.data.requests || [])).catch(() => setChJoins([]));
      api.get("/admin/companies-house/companies").then((r) => setChCompanies(r.data)).catch(() => setChCompanies(null));
    }
  }, [tab]);

  const reloadCh = () => {
    api.get("/admin/companies-house/review").then((r) => setChReview(r.data)).catch(() => {});
    api.get("/admin/companies-house/join-requests").then((r) => setChJoins(r.data.requests || [])).catch(() => {});
    api.get("/admin/companies-house/companies").then((r) => setChCompanies(r.data)).catch(() => {});
  };
  const chJoinApprove = async (id) => {
    setChBusy(`join-${id}`);
    try {
      await api.post(`/admin/companies-house/join-requests/${id}/approve`, { reason: "Verified in Control Tower" });
      toast.success("Recruiter linked to the shared company record.");
      reloadCh();
    } catch (err) { toast.error(formatApiError(err)); } finally { setChBusy(""); }
  };
  const chJoinDecline = async (id) => {
    if (!window.confirm("Decline this access request? The account stays active but is not linked to the company.")) return;
    setChBusy(`join-${id}`);
    try {
      await api.post(`/admin/companies-house/join-requests/${id}/decline`, { reason: "Declined in Control Tower" });
      toast.success("Request declined and recorded.");
      reloadCh();
    } catch (err) { toast.error(formatApiError(err)); } finally { setChBusy(""); }
  };
  const chConsolidate = async () => {
    if (!window.confirm("Fold duplicate Companies House records into one company each? Users, jobs, applications and history are preserved.")) return;
    setChBusy("consolidate");
    try {
      const r = await api.post("/admin/companies-house/consolidate");
      toast.success(r.data.merged ? `${r.data.merged} duplicate account(s) consolidated.` : "No duplicates found, every company already appears once.");
      reloadCh();
    } catch (err) { toast.error(formatApiError(err)); } finally { setChBusy(""); }
  };

  const chRefresh = async (id) => {
    setChBusy(`refresh-${id}`);
    try {
      await api.post(`/admin/companies/${id}/ch-refresh`);
      toast.success("Official record refreshed from Companies House.");
      const r = await api.get("/admin/companies-house/review");
      setChReview(r.data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setChBusy(""); }
  };
  const chApprove = async (id) => {
    setChBusy(`approve-${id}`);
    try {
      await api.post(`/admin/companies/${id}/ch-approve-exception`, { reason: "" });
      toast.success("Exception approved and recorded in the audit history.");
      const r = await api.get("/admin/companies-house/review");
      setChReview(r.data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setChBusy(""); }
  };
  const chCorrect = async (id) => {
    setChBusy(`correct-${id}`);
    try {
      await api.post(`/admin/companies/${id}/ch-correct`, chCorrectForm);
      toast.success("Correction saved with full audit record.");
      setChCorrectFor(null);
      setChCorrectForm({ field: "company_name", value: "", reason: "" });
      const r = await api.get("/admin/companies-house/review");
      setChReview(r.data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setChBusy(""); }
  };
  const updateSubPlan = (idx, field, value) => {
    setSubPlans((sp) => ({ ...sp, plans: sp.plans.map((p, i) => (i === idx ? { ...p, [field]: value } : p)) }));
  };
  const saveSubPlans = async () => {
    setSubPlansSaving(true);
    try {
      const r = await api.put("/admin/subscription-plans", {
        annual_discount_pct: Number(subPlans.annual_discount_pct) || 0,
        retention: {
          enabled: !!subPlans.retention?.enabled,
          percent: Number(subPlans.retention?.percent) || 20,
          months: Number(subPlans.retention?.months) || 3,
          min_age_days: Number(subPlans.retention?.min_age_days) || 0,
        },
        plans: subPlans.plans.map((p) => ({
          ...p,
          monthly_price: Number(p.monthly_price) || 0,
          included_jobs: Number(p.included_jobs) || 0,
          extra_job_price: Number(p.extra_job_price) || 0,
          order: Number(p.order) || 99,
        })),
      });
      setSubPlans(r.data);
      toast.success("Subscription plans saved. The plans page uses them straight away.");
    } catch (err) { toast.error(formatApiError(err)); } finally { setSubPlansSaving(false); }
  };
  const assignCustom = async () => {
    setCustomBusy(true);
    try {
      await api.post("/admin/subscriptions/custom", {
        employer_email: customForm.employer_email.trim(),
        plan_name: customForm.plan_name.trim(),
        included_jobs: Number(customForm.included_jobs) || 0,
        monthly_price: Number(customForm.monthly_price) || 0,
        extra_job_price: Number(customForm.extra_job_price) || 0,
      });
      toast.success(`Custom plan assigned to ${customForm.employer_email}.`);
      loadSubData();
    } catch (err) { toast.error(formatApiError(err)); } finally { setCustomBusy(false); }
  };
  const endCustom = async (employerId) => {
    try {
      await api.delete(`/admin/subscriptions/custom/${employerId}`);
      toast.success("Custom plan ended.");
      loadSubData();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const setEnquiryStatus = async (id, status) => {
    try {
      await api.post(`/admin/enterprise-enquiries/${id}/status`, { status });
      loadSubData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const addSource = async () => {
    setInsightBusy("source");
    try {
      await api.post("/admin/insight-sources", sourceForm);
      toast.success("Source added, it joins the next daily check.");
      setSourceForm({ name: "", feed_url: "", category: "Careers news" });
      loadInsights();
    } catch (err) { toast.error(formatApiError(err)); } finally { setInsightBusy(""); }
  };
  const toggleSource = async (s) => {
    try {
      await api.patch(`/admin/insight-sources/${s.id}`, { active: !s.active });
      loadInsights();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const deleteSource = async (s) => {
    try {
      await api.delete(`/admin/insight-sources/${s.id}`);
      toast.success("Source and its items removed.");
      loadInsights();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const triggerSync = async () => {
    setInsightBusy("sync");
    try {
      await api.post("/admin/insights/sync");
      toast.success("Sync queued, check the health panel in a few seconds.");
      setTimeout(loadInsights, 6000);
    } catch (err) { toast.error(formatApiError(err)); } finally { setInsightBusy(""); }
  };
  const patchInsight = async (id, patch) => {
    try {
      await api.patch(`/admin/insights/${id}`, patch);
      loadInsights();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const deleteInsight = async (id) => {
    try {
      await api.delete(`/admin/insights/${id}`);
      loadInsights();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const publishOriginal = async () => {
    setInsightBusy("original");
    try {
      const r = await api.post("/admin/insights/original", originalForm);
      toast.success(`Published. It's live at /insights/${r.data.slug}`);
      setOriginalForm({ title: "", summary: "", body: "", category: "HiredToday advice", featured: false });
      loadInsights();
    } catch (err) { toast.error(formatApiError(err)); } finally { setInsightBusy(""); }
  };
  const saveTalentCfg = async () => {
    setTalentCfgSaving(true);
    try {
      const r = await api.put("/admin/talent/settings", talentCfg);
      setTalentCfg(r.data);
      toast.success("Talent settings saved.");
      loadTalentAdmin();
    } catch (err) { toast.error(formatApiError(err)); } finally { setTalentCfgSaving(false); }
  };

  const savePricing = async (tier) => {
    setPricingSaving(tier);
    try {
      await api.patch("/admin/pricing", {
        tier,
        price: pricingForm[tier].price,
        compare_at: pricingForm[tier].compare_at || null,
      });
      toast.success("Price updated. New checkouts use it straight away.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPricingSaving("");
    }
  };
  const [promoForm, setPromoForm] = useState({ code: "", percent: 10, max_uses: "" });
  const [promoSaving, setPromoSaving] = useState(false);

  const loadPromos = useCallback(() => {
    setTabLoading(true);
    api.get("/admin/promos").then((r) => setPromos(r.data)).catch(() => {}).finally(() => setTabLoading(false));
  }, []);

  const createPromo = async (e) => {
    e.preventDefault();
    setPromoSaving(true);
    try {
      await api.post("/admin/promos", {
        code: promoForm.code, percent: Number(promoForm.percent),
        max_uses: promoForm.max_uses === "" ? null : Number(promoForm.max_uses),
      });
      toast.success(`Code ${promoForm.code.trim().toUpperCase()} created`);
      setPromoForm({ code: "", percent: 10, max_uses: "" });
      loadPromos();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPromoSaving(false);
    }
  };

  const togglePromo = async (promo) => {
    try {
      await api.patch(`/admin/promos/${promo.id}`, { active: !promo.active });
      setPromos((list) => list.map((x) => (x.id === promo.id ? { ...x, active: !x.active } : x)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deletePromo = async (promo) => {
    if (!window.confirm(`Delete code ${promo.code}?`)) return;
    try {
      await api.delete(`/admin/promos/${promo.id}`);
      setPromos((list) => list.filter((x) => x.id !== promo.id));
      toast.success("Code deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const [jobs, setJobs] = useState([]);
  const [jobFilter, setJobFilter] = useState("");
  const [jobSearch, setJobSearch] = useState("");
  const [jobSearchLive, setJobSearchLive] = useState("");
  useEffect(() => { const t = setTimeout(() => setJobSearchLive(jobSearch), 300); return () => clearTimeout(t); }, [jobSearch]);
  const [reviewFilter, setReviewFilter] = useState({ q: "", rating: "" });
  const [editReview, setEditReview] = useState(null);
  const [editReviewForm, setEditReviewForm] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersLimit, setUsersLimit] = useState(25);
  const [roleCounts, setRoleCounts] = useState({});
  const [userSel, setUserSel] = useState({});
  const [userFilter, setUserFilter] = useState({ q: "", role: "", location: "" });
  const filteredUsers = users; // filtering + pagination are server-side now (no 1,000 cap)
  const [transactions, setTransactions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [campPage, setCampPage] = useState(1);
  const [campEdit, setCampEdit] = useState(null);
  const [campSaving, setCampSaving] = useState(false);
  const CAMPAIGN_EFFECTS = [
    { id: "none", label: "No effect, banner & countdown only" },
    { id: "balloons", label: "Balloons, launch & celebrations" },
    { id: "fireworks", label: "Fireworks, New Year & big moments" },
    { id: "confetti", label: "Confetti, promotions & wins" },
    { id: "snow", label: "Snow, Christmas & winter" },
    { id: "leaves", label: "Falling leaves, autumn" },
    { id: "halloween", label: "Halloween, bats & embers" },
  ];
  const loadCampaigns = () => { api.get("/admin/campaigns").then((r) => setCampaigns(Array.isArray(r.data) ? r.data : [])).catch(() => {}); };
  useEffect(() => { if (tab === "campaigns") loadCampaigns(); }, [tab]);
  const campStatus = (c) => {
    if (!c.active) return { label: "Off", cls: "bg-zinc-100 text-zinc-500" };
    const t = new Date();
    if (c.starts_at && new Date(c.starts_at) > t) return { label: "Scheduled", cls: "bg-blue-100 text-blue-700" };
    if (c.ends_at && new Date(c.ends_at) < t) return { label: "Ended", cls: "bg-zinc-200 text-zinc-600" };
    return { label: "Live now", cls: "bg-green-100 text-green-700" };
  };
  const campLocal = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };
  const toggleCampaign = async (c) => {
    try { await api.patch(`/admin/campaigns/${c.id}`, { active: !c.active }); toast.success(c.active ? "Campaign switched off" : "Campaign live on the homepage"); loadCampaigns(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const deleteCampaign = async (c) => {
    if (!window.confirm(`Delete the campaign "${c.name}" for good?`)) return;
    try { await api.delete(`/admin/campaigns/${c.id}`); toast.success("Campaign deleted"); loadCampaigns(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const saveCampaign = async (e) => {
    e.preventDefault();
    setCampSaving(true);
    const payload = { ...campEdit };
    for (const k of ["countdown_target", "starts_at", "ends_at"]) payload[k] = payload[k] ? new Date(payload[k]).toISOString() : null;
    delete payload.id; delete payload.created_at; delete payload.updated_at;
    try {
      if (campEdit.id) await api.patch(`/admin/campaigns/${campEdit.id}`, payload);
      else await api.post("/admin/campaigns", payload);
      toast.success(campEdit.id ? "Campaign updated" : "Campaign created");
      setCampEdit(null); loadCampaigns();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCampSaving(false); }
  };
  const [reviews, setReviews] = useState([]);
  const [reviewsError, setReviewsError] = useState(false);
  const [verifications, setVerifications] = useState([]);
  const [verifFilter, setVerifFilter] = useState({ q: "", status: "", role: "" });
  const [verifError, setVerifError] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addUserForm, setAddUserForm] = useState({ name: "", email: "", password: "", role: "candidate", company_name: "" });
  const [addUserSaving, setAddUserSaving] = useState(false);
  const [editVerif, setEditVerif] = useState(null);
  const [editVerifForm, setEditVerifForm] = useState(null);
  const [editVerifSaving, setEditVerifSaving] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState(null);
  const [editUserSaving, setEditUserSaving] = useState(false);

  const loadUsers = useCallback(() => {
    setTabLoading(true);
    api.get("/admin/users", { params: { page: usersPage, limit: usersLimit, q: userFilter.q, role: userFilter.role, loc: userFilter.location } })
      .then((r) => { setUsers(r.data.users); setUsersTotal(r.data.total); setRoleCounts(r.data.role_counts || {}); setUserSel({}); })
      .catch(() => {}).finally(() => setTabLoading(false));
  }, [usersPage, usersLimit, userFilter]);

  const bulkUserAction = async (kind) => {
    const ids = Object.keys(userSel).filter((k) => userSel[k]);
    if (!ids.length) return;
    if (kind === "delete" && !window.confirm(`Permanently delete ${ids.length} account(s) and their data? This cannot be undone.`)) return;
    let done = 0;
    for (const id of ids) {
      try {
        if (kind === "delete") await api.delete(`/admin/users/${id}`);
        else await api.patch(`/admin/users/${id}`, { is_active: false });
        done += 1;
      } catch (err) { toast.error(formatApiError(err)); }
    }
    toast.success(`${done} account(s) ${kind === "delete" ? "deleted" : "deactivated"}.`);
    setUserSel({});
    loadUsers();
  };

  const createUser = async (e) => {
    e.preventDefault();
    setAddUserSaving(true);
    try {
      await api.post("/admin/users", { ...addUserForm, password: addUserForm.password || undefined });
      toast.success(`Account created for ${addUserForm.email}. Invite email sent`);
      setAddUserOpen(false);
      setAddUserForm({ name: "", email: "", password: "", role: "candidate", company_name: "" });
      loadUsers();
      loadStats();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAddUserSaving(false);
    }
  };
  const [editJob, setEditJob] = useState(null);
  const [adminBoostInput, setAdminBoostInput] = useState("");
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);
  const [empCards, setEmpCards] = useState({});
  const loadEmpCard = (jobId) => {
    if (empCards[jobId]) return;
    api.get(`/admin/jobs/${jobId}/employer-summary`)
      .then((r) => setEmpCards((m) => ({ ...m, [jobId]: r.data })))
      .catch(() => {});
  };

  const loadStats = useCallback(() => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}), []);

  const [dataBusy, setDataBusy] = useState(false);
  const [importResult, setImportResult] = useState("");

  const exportData = async () => {
    setDataBusy(true);
    try {
      const r = await api.get("/admin/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "hiredtoday-export.json";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDataBusy(false);
    }
  };

  const importData = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDataBusy(true);
    setImportResult("");
    try {
      const payload = JSON.parse(await file.text());
      const r = await api.post("/admin/import", payload);
      setImportResult(`Imported ${r.data.jobs_added} jobs, ${r.data.users_added} accounts and ${r.data.transactions_added} payment records. ${r.data.skipped} existing items were skipped.`);
      toast.success("Import finished");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDataBusy(false);
      e.target.value = "";
    }
  };
  useEffect(() => {
    api.get("/admin/settings").then((r) => setOfferForm(r.data.launch_offer || { enabled: false, text: "" })).catch(() => {});
  }, []);

  const saveOffer = async (e) => {
    e.preventDefault();
    setOfferSaving(true);
    try {
      await api.patch("/admin/settings", { launch_offer: offerForm });
      toast.success(offerForm.enabled ? "Banner is live on the homepage" : "Banner switched off");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setOfferSaving(false);
    }
  };
  const loadJobs = useCallback(() => {
    api.get("/admin/jobs", { params: { ...(jobFilter ? { status: jobFilter } : {}), ...(jobSearchLive.trim() ? { q: jobSearchLive.trim() } : {}) } })
      .then((r) => setJobs(r.data)).catch(() => {});
  }, [jobFilter, jobSearchLive]);

  useEffect(() => { loadStats(); }, [loadStats]);
  // overview stats refresh automatically while Control Tower is open
  useEffect(() => {
    const t = setInterval(loadStats, 60000);
    return () => clearInterval(t);
  }, [loadStats]);
  useEffect(() => { if (tab === "jobs") loadJobs(); }, [tab, loadJobs]);
  const [modFlags, setModFlags] = useState(null);
  const [emailLog, setEmailLog] = useState(null);
  const [emailFilter, setEmailFilter] = useState("real");
  const [docViewer, setDocViewer] = useState(null);
  const [blastSubject, setBlastSubject] = useState("");
  const [blastBody, setBlastBody] = useState("");
  const [blastSending, setBlastSending] = useState(false);
  const [blastResult, setBlastResult] = useState("");

  const sendBlast = async (payload) => {
    setBlastSending(true);
    setBlastResult("");
    try {
      const r = await api.post("/admin/email-blast", payload);
      setBlastResult(r.data.detail || `Sent to ${r.data.sent} employer${r.data.sent === 1 ? "" : "s"}.`);
      if (payload.kind === "employer_marketing") { setBlastSubject(""); setBlastBody(""); }
    } catch (err) {
      setBlastResult(err.response?.data?.detail || "Send failed, please try again.");
    } finally {
      setBlastSending(false);
    }
  };

  const [emailTestSending, setEmailTestSending] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null);
  const sendTestEmail = async () => {
    setEmailTestSending(true);
    setEmailTestResult(null);
    try {
      const r = await api.post("/admin/email-test");
      setEmailTestResult(r.data);
    } catch {
      setEmailTestResult({ ok: false, logged_status: "request failed", error: "" });
    } finally {
      setEmailTestSending(false);
    }
    try {
      const r2 = await api.get("/admin/email-log", { params: { filter: emailFilter } });
      setEmailLog(r2.data);
    } catch { /* best-effort refresh */ }
  };
  useEffect(() => { if (tab === "jobs") api.get("/admin/moderation-flags").then((r) => setModFlags(r.data)).catch(() => {}); }, [tab]);
  useEffect(() => { if (tab === "users") loadUsers(); }, [tab, loadUsers]);
  useEffect(() => {
    if (tab !== "payments") return;
    const t = setTimeout(() => {
      setTabLoading(true);
      const params = {};
      if (payFilters.q) params.q = payFilters.q;
      if (payFilters.kind) params.kind = payFilters.kind;
      if (payFilters.status) params.status = payFilters.status;
      if (payFilters.from) params.date_from = payFilters.from;
      if (payFilters.to) params.date_to = payFilters.to;
      if (payFilters.tier) params.tier = payFilters.tier;
      if (payFilters.promo) params.promo = payFilters.promo;
      if (payFilters.year) params.year = payFilters.year;
      if (payFilters.month) params.month = payFilters.month;
      if (payFilters.week) params.week = payFilters.week;
      Promise.all([
        api.get("/admin/transactions", { params }),
        api.get("/admin/payments/summary", { params }),
      ]).then(([tx, s]) => { setTransactions(tx.data); setPaySummary(s.data); })
        .catch(() => {}).finally(() => setTabLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [tab, payFilters]);
  useEffect(() => { if (tab === "messages") { setTabLoading(true); api.get("/admin/messages").then((r) => setMessages(r.data)).catch(() => {}).finally(() => setTabLoading(false)); } }, [tab]);
  useEffect(() => {
    if (tab !== "reviews") return;
    const t = setTimeout(() => {
      setTabLoading(true); setReviewsError(false);
      const params = {};
      if (reviewFilter.q) params.q = reviewFilter.q;
      if (reviewFilter.rating) params.rating = reviewFilter.rating;
      api.get("/admin/reviews", { params }).then((r) => setReviews(r.data)).catch(() => setReviewsError(true)).finally(() => setTabLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [tab, reviewFilter]);
  useEffect(() => { if (tab === "promos") loadPromos(); }, [tab, loadPromos]);
  useEffect(() => {
    if (tab !== "pricing") return;
    api.get("/pricing").then((r) => {
      const f = {};
      for (const [k, v] of Object.entries(r.data)) {
        f[k] = { price: String(v.price), compare_at: v.compare_at ? String(v.compare_at) : "" };
      }
      setPricingForm(f);
    }).catch(() => {});
  }, [tab]);
  const [indexLog, setIndexLog] = useState([]);
  const [idxFilter, setIdxFilter] = useState({ action: "", status: "", q: "" });
  const loadIndexLog = (f) => {
    const flt = f || idxFilter;
    setTabLoading(true);
    const p = new URLSearchParams();
    if (flt.action) p.set("action", flt.action);
    if (flt.status) p.set("status", flt.status);
    if (flt.q) p.set("q", flt.q);
    api.get(`/admin/google-index/log?${p.toString()}`).then((r) => setIndexLog(Array.isArray(r.data) ? r.data : [])).catch(() => {}).finally(() => setTabLoading(false));
  };
  useEffect(() => { if (tab === "indexing") loadIndexLog(); }, [tab]);
  useEffect(() => {
    if (tab !== "emails") return;
    setTabLoading(true);
    api.get("/admin/email-log", { params: { filter: emailFilter } })
      .then((r) => setEmailLog(r.data)).catch(() => {}).finally(() => setTabLoading(false));
  }, [tab, emailFilter]);
  const [synd, setSynd] = useState(null);
  const loadSyndication = () => {
    setTabLoading(true);
    api.get("/admin/feeds/syndication").then((r) => setSynd(r.data)).catch(() => toast.error("Could not load syndication status")).finally(() => setTabLoading(false));
  };
  useEffect(() => { if (tab === "syndication") loadSyndication(); }, [tab]);
  const [cvArchive, setCvArchive] = useState(null);
  const loadCvArchive = () => {
    setTabLoading(true);
    api.get("/admin/cv-archive").then((r) => setCvArchive(r.data)).catch(() => toast.error("Could not load the CV archive")).finally(() => setTabLoading(false));
  };
  useEffect(() => { if (tab === "cvarchive") loadCvArchive(); }, [tab]);

  useEffect(() => {
    if (tab !== "chats") return;
    const t = setTimeout(() => {
      setTabLoading(true);
      api.get("/admin/chats", { params: { search: chatSearch } })
        .then((r) => setChats(r.data)).catch(() => toast.error("Could not load chats"))
        .finally(() => setTabLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [tab, chatSearch]);

  const openChat = (row) => {
    setChatOpen(row);
    setChatThread(null);
    api.get(`/admin/chats/${row.application_id}`)
      .then((r) => setChatThread(r.data))
      .catch(() => toast.error("Could not load this conversation"));
  };

  const gbp = (p) => `£${((p || 0) / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const visibleTxns = showExcluded ? transactions : transactions.filter((t) => !t.excluded);

  const openPayDetail = (t) => {
    setPayDetail(t);
    setPayEdit({ note: t.note || "", excluded: !!t.excluded, payment_status: t.payment_status || "" });
  };

  const savePayDetail = async () => {
    try {
      const id = payDetail.id;
      await api.patch(`/admin/transactions/${id}`, payEdit);
      toast.success("Payment updated");
      setTransactions((rows) => rows.map((r) => (r.id === id ? { ...r, ...payEdit } : r)));
      setPayDetail(null);
      setPayFilters((f) => ({ ...f }));
    } catch {
      toast.error("Could not update the payment");
    }
  };

  const downloadInvoice = async (id) => {
    try {
      const res = await api.get(`/admin/transactions/${id}/invoice.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `hiredtoday-invoice-${id.slice(-6).toUpperCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the invoice");
    }
  };

  const downloadPaymentsCsv = async () => {
    try {
      const params = {};
      if (payFilters.q) params.q = payFilters.q;
      if (payFilters.kind) params.kind = payFilters.kind;
      if (payFilters.status) params.status = payFilters.status;
      if (payFilters.from) params.date_from = payFilters.from;
      if (payFilters.to) params.date_to = payFilters.to;
      if (payFilters.tier) params.tier = payFilters.tier;
      if (payFilters.promo) params.promo = payFilters.promo;
      if (payFilters.year) params.year = payFilters.year;
      if (payFilters.month) params.month = payFilters.month;
      if (payFilters.week) params.week = payFilters.week;
      const res = await api.get("/admin/transactions.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "hiredtoday-payments.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the CSV");
    }
  };

  const loadInbox = useCallback(() => {
    setTabLoading(true);
    Promise.all([api.get("/admin/inbox/status"), api.get("/admin/inbox/messages")])
      .then(([s, m]) => {
        setInboxStatus(s.data);
        setInboxMsgs(m.data);
        localStorage.setItem("ct_inbox_seen", String(s.data.messages || 0));
        setInboxNew(0);
      })
      .catch(() => {}).finally(() => setTabLoading(false));
  }, []);
  useEffect(() => { if (tab === "inbox") loadInbox(); }, [tab, loadInbox]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("connected") === "1") toast.success("Inbox connected, press Sync now to pull your mail");
    if (p.get("inbox_error") === "exchange") {
      const reason = p.get("reason");
      toast.error(`The Google connection didn't complete${reason ? ` (Google said: ${reason})` : ""}, press Connect inbox and try again.`);
    }
    if (p.get("inbox_error") === "state") toast.error("That connection link had expired, press Connect inbox again and finish the Google sign-in straight away.");
    if (p.get("inbox_error") === "google") toast.error("Google didn't finish the sign-in, press Connect inbox and approve the access request.");
    if (p.get("connected") || p.get("inbox_error")) window.history.replaceState({}, "", "/control-tower?tab=inbox");
  }, []);

  const inboxConnect = () => {
    api.get("/admin/inbox/connect")
      .then((r) => { window.location.href = r.data.url; })
      .catch((err) => toast.error(formatApiError(err)));
  };
  const inboxSyncNow = () => {
    api.post("/admin/inbox/sync")
      .then((r) => {
        toast.success(r.data.new ? `${r.data.new} new message${r.data.new === 1 ? "" : "s"}` : "Inbox is up to date");
        loadInbox();
      })
      .catch((err) => toast.error(formatApiError(err)));
  };
  const inboxDisconnect = () => {
    api.post("/admin/inbox/disconnect")
      .then(() => { toast.success("Inbox disconnected"); setInboxOpen(null); loadInbox(); })
      .catch((err) => toast.error(formatApiError(err)));
  };
  const openInboxMessage = (m) => {
    setInboxOpen(null);
    setInboxReply("");
    api.get(`/admin/inbox/messages/${m.gmail_id}`)
      .then((r) => setInboxOpen(r.data))
      .catch(() => toast.error("Could not load this message"));
  };
  const sendInboxReply = async () => {
    if (!inboxReply.trim() || inboxSending) return;
    setInboxSending(true);
    try {
      await api.post("/admin/inbox/reply", { message_id: inboxOpen.message.gmail_id, body: inboxReply });
      toast.success("Reply sent");
      setInboxReply("");
      openInboxMessage(inboxOpen.message);
      api.get("/admin/inbox/messages").then((r) => setInboxMsgs(r.data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setInboxSending(false);
    }
  };

  const playInboxPing = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      [880, 1174.66].forEach((f, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = f;
        const t0 = ctx.currentTime + i * 0.12;
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(0.05, t0 + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.25);
        o.connect(g).connect(ctx.destination);
        o.start(t0);
        o.stop(t0 + 0.3);
      });
      setTimeout(() => ctx.close(), 800);
    } catch { /* inbox ping is best-effort */ }
  };

  const inboxPrevRef = useRef(null);
  useEffect(() => {
    const check = () => {
      api.get("/admin/inbox/status").then((r) => {
        const seen = parseInt(localStorage.getItem("ct_inbox_seen") || "0", 10);
        const fresh = Math.max(0, (r.data.messages || 0) - seen);
        if (inboxPrevRef.current !== null && fresh > inboxPrevRef.current) playInboxPing();
        inboxPrevRef.current = fresh;
        setInboxNew(fresh);
      }).catch(() => {});
    };
    check();
    const t = setInterval(check, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const downloadArchivedCv = async (uid, name) => {
    try {
      const r = await api.get(`/admin/cv-archive/${uid}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(name || "cv").split(" ")[0].toLowerCase()}-hiredtoday-cv.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download that CV");
    }
  };
  const pingAgain = async (url) => {
    try {
      const r = await api.post("/admin/google-index/ping", { url });
      const res = (r.data?.results || [])[0];
      if (r.data?.ok && res?.status === 200) toast.success("Google accepted, 200");
      else if (res) toast.error(`Google responded ${res.status}: ${String(res.detail || "").replace(/\s+/g, " ").slice(0, 160)}`);
      else toast.error(r.data?.error || "Ping failed, check the Google service-account key in environment settings.");
      loadIndexLog();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const [mkt, setMkt] = useState({ audience: "employers", emails: "", notes: "", subject: "", body: "", personalize: true, promoPercent: "", promoUnique: true });
  const [mktBusy, setMktBusy] = useState(false);
  const [mktResult, setMktResult] = useState(null);
  const [mktHistory, setMktHistory] = useState([]);
  const [mktPage, setMktPage] = useState(1);
  const [mktTotal, setMktTotal] = useState(0);
  const [mktFilter, setMktFilter] = useState({ search: "", status: "", audience: "", archived: "0" });
  const [mktView, setMktView] = useState(null);
  const loadMktHistory = (page = mktPage, f = mktFilter) => {
    api.get("/admin/marketing/sends", { params: { page, limit: 25, search: f.search, status: f.status, audience: f.audience, archived: f.archived } })
      .then((r) => { setMktHistory(r.data.items || []); setMktTotal(r.data.total || 0); setMktPage(r.data.page || 1); })
      .catch(() => {});
  };
  useEffect(() => { if (tab === "marketing") loadMktHistory(1); }, [tab]);
  const mktPatch = async (id, body, msg) => {
    try { await api.patch(`/admin/marketing/sends/${id}`, body); toast.success(msg); loadMktHistory(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const mktDelete = async (row) => {
    if (!window.confirm(`Delete the record of the email to ${row.email}? This only removes the Control Tower history entry, the email itself was already delivered.`)) return;
    try { await api.delete(`/admin/marketing/sends/${row.id}`); toast.success("Record deleted"); loadMktHistory(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const mktOpenView = async (row) => {
    try { const r = await api.get(`/admin/marketing/sends/${row.id}`); setMktView(r.data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const mktEditSubject = async (row) => {
    const subject = window.prompt("Edit the stored subject line (history record only, the delivered email does not change):", row.subject);
    if (subject && subject.trim() && subject.trim() !== row.subject) mktPatch(row.id, { subject: subject.trim() }, "Subject updated");
  };
  const mktEmails = (mkt.emails.match(/[^\s,;]+@[^\s,;]+\.[^\s,;]+/g) || []);
  const generateMarketing = async () => {
    setMktBusy(true);
    try {
      const r = await api.post("/admin/marketing/generate", { audience: mkt.audience, notes: mkt.notes });
      setMkt((m) => ({ ...m, subject: r.data.subject, body: r.data.body }));
      toast.success("Draft ready. Edit it before sending.");
    } catch (err) { toast.error(formatApiError(err)); } finally { setMktBusy(false); }
  };
  const sendMarketing = async () => {
    setMktBusy(true); setMktResult(null);
    try {
      const r = await api.post("/admin/marketing/send", {
        audience: mkt.audience, emails: mktEmails, subject: mkt.subject, body: mkt.body,
        personalize: mkt.personalize,
        promo_percent: mkt.promoPercent ? parseInt(mkt.promoPercent, 10) : null,
        promo_unique: mkt.promoUnique,
      });
      setMktResult(r.data);
      toast.success(`${r.data.queued} email${r.data.queued === 1 ? "" : "s"} queued, watch Recent sends for live delivery status`);
      loadMktHistory();
      setTimeout(loadMktHistory, 8000);
      setTimeout(loadMktHistory, 20000);
    } catch (err) { toast.error(formatApiError(err)); } finally { setMktBusy(false); }
  };
  const loadVerifications = (f = verifFilter) => {
    setTabLoading(true); setVerifError(false);
    api.get("/admin/verifications", { params: { search: f.q, status: f.status, role: f.role } })
      .then((r) => { setVerifications(r.data); setVerifPage(1); })
      .catch(() => setVerifError(true)).finally(() => setTabLoading(false));
  };
  useEffect(() => { if (tab === "verifications") loadVerifications(); }, [tab]);

  const chaseVerification = async (u) => {
    try {
      const r = await api.post(`/admin/verification/${u.id}/chase`);
      if (r.data?.queued) {
        toast.warning(`The email service is busy. The reminder to ${r.data.to || u.email} is queued and will send automatically.`);
      } else {
        toast.success(`Reminder emailed to ${r.data?.to || u.email}`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sendInvite = async (u) => {
    try {
      const r = await api.post(`/admin/users/${u.id}/invite`);
      if (r.data?.queued) {
        toast.warning(`The email service is busy. The invite to ${r.data.to || u.email} is queued and will send automatically.`);
      } else {
        toast.success(`Invite email sent to ${r.data?.to || u.email}`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.email} and all their jobs, applications and reviews? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      loadUsers();
      loadStats();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const startEditVerif = (u) => {
    const v = u.verification || {};
    setEditVerif(u);
    setEditVerifForm({
      company_number: v.company_number || "",
      registered_address: v.registered_address || "",
      director_name: v.director_name || "",
      note: v.note || "",
    });
  };

  const saveEditVerif = async (e) => {
    e.preventDefault();
    setEditVerifSaving(true);
    try {
      await api.patch(`/admin/verification/${editVerif.id}`, editVerifForm);
      toast.success("Verification details updated");
      setEditVerif(null);
      setTabLoading(true);
      loadVerifications();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEditVerifSaving(false);
    }
  };

  const startEditUser = (u) => {
    setEditUser(u);
    setEditUserForm({ name: u.name, email: u.email, company_name: u.company_name || "", location: u.location || "", role: u.role, is_active: u.is_active, parent_employer_email: u.parent_employer_id ? "" : null });
  };

  const saveEditUser = async (e) => {
    e.preventDefault();
    setEditUserSaving(true);
    try {
      const payload = { ...editUserForm };
      if (payload.parent_employer_email == null) delete payload.parent_employer_email;
      await api.patch(`/admin/users/${editUser.id}`, payload);
      toast.success("User updated");
      setEditUser(null);
      loadUsers();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEditUserSaving(false);
    }
  };

  const saveReviewEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/reviews/${editReview.id}`, editReviewForm);
      toast.success("Review updated");
      setReviews((rows) => rows.map((r) => (r.id === editReview.id ? { ...r, ...editReviewForm } : r)));
      setEditReview(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const reviewVerification = async (userId, status) => {
    const note = status === "rejected" ? (window.prompt("Reason (the user will see this):") || "Documents unclear. Please resubmit") : "";
    try {
      await api.patch(`/admin/verification/${userId}`, { status, note });
      toast.success(status === "verified" ? "Verified. Their jobs can now go live" : status === "rejected" ? "Marked as rejected" : `Status set to ${status === "none" ? "not submitted" : status}`);
      setVerifications((v) => v.map((x) => (x.id === userId ? { ...x, verification: { ...x.verification, status } } : x)));
      loadStats();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await api.delete(`/admin/reviews/${reviewId}`);
      toast.success("Review deleted");
      setReviews((r) => r.filter((x) => x.id !== reviewId));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const patchJob = async (jobId, updates, successMsg) => {
    try {
      await api.patch(`/admin/jobs/${jobId}`, updates);
      toast.success(successMsg);
      loadJobs();
      loadStats();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteJob = async (job) => {
    if (!window.confirm(`Delete "${job.title}" permanently?`)) return;
    try {
      await api.delete(`/admin/jobs/${job.id}`);
      toast.success("Job deleted");
      loadJobs();
      loadStats();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const startEdit = (job) => {
    setEditJob(job);
    setEditForm({
      title: job.title, company_name: job.company_name, location: job.location,
      category: job.category, tier: job.tier, status: job.status,
      admin_note: job.admin_note || "", description: job.description,
      workplace: job.workplace || "", job_type: job.job_type || "",
      salary_min: job.salary_min ?? "", salary_max: job.salary_max ?? "",
      salary_period: job.salary_period || "year", show_salary: !!job.show_salary,
      company_website: job.company_website || "", company_description: job.company_description || "",
      application_email: job.application_email || "",
      apply_method: job.apply_method || "platform", external_apply_url: job.external_apply_url || "",
      urgent: !!job.urgent, featured: !!job.featured,
      partner_distribution: !!job.partner_distribution,
      boost_locations: job.boost_locations || [],
    });
    setAdminBoostInput("");
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/admin/jobs/${editJob.id}`, {
        ...editForm,
        salary_min: editForm.salary_min === "" ? null : Number(editForm.salary_min),
        salary_max: editForm.salary_max === "" ? null : Number(editForm.salary_max),
        external_apply_url: editForm.external_apply_url || null,
      });
      toast.success("Job updated");
      setEditJob(null);
      loadJobs();
      loadStats();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const patchUser = async (userId, updates) => {
    try {
      await api.patch(`/admin/users/${userId}`, updates);
      toast.success("User updated");
      const res = await api.get("/admin/users");
      setUsers(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
      api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    }
  };

  const markRead = async (msgId) => {
    await api.patch(`/admin/messages/${msgId}`).catch(() => {});
    setMessages((m) => m.map((x) => (x.id === msgId ? { ...x, read: true } : x)));
  };

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "ops", label: "Operations" },
    { id: "jobs", label: "Jobs" },
    { id: "users", label: "Users" },
    { id: "people", label: "People analytics" },
    { id: "security", label: "Security" },
    { id: "verifications", label: "Verifications" },
    { id: "payments", label: "Payments" },
    { id: "promos", label: "Promo codes" },
    { id: "pricing", label: "Pricing" },
    { id: "analytics", label: "Analytics" },
    { id: "livetraffic", label: "Live traffic" },
    { id: "traffic", label: "Traffic & partners" },
    { id: "reviews", label: "Reviews" },
    { id: "marketing", label: "Marketing" },
    { id: "insights", label: "Insights" },
    { id: "talent", label: "Talent DB" },
    { id: "indexing", label: "Indexing" },
    { id: "syndication", label: "Job Syndication" },
    { id: "cvarchive", label: "CV Archive" },
    { id: "chats", label: "Chats" },
    { id: "messages", label: "Messages" },
    { id: "emails", label: "Emails" },
    { id: "blasts", label: "Email blasts" },
    { id: "campaigns", label: "Campaigns" },
    { id: "inbox", label: inboxNew > 0 ? (<>Inbox <span className="ml-1 inline-block h-2 w-2 rounded-full bg-red-600 align-middle" data-testid="inbox-tab-dot" /></>) : "Inbox" },
  ];

  return (
    <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="control-tower">
      <PageGlow subtle />
      <Seo title="Control Tower | HiredToday" description="Site administration." path="/control-tower" />
      <h1 className="hidden sm:block font-display text-3xl sm:text-4xl tracking-tight font-bold text-zinc-950">Control Tower</h1>

      <div className="hidden sm:flex flex-wrap gap-1 mt-8 border-b border-zinc-200" data-testid="admin-tabs">
        {CT_DEPTS.map(([dept, ids]) => {
          const active = ids.includes(tab);
          const open = openDept === dept;
          return (
            <div key={dept} className="relative" onMouseLeave={() => setOpenDept("")}>
              <button onClick={() => setOpenDept(dept)} onMouseEnter={() => setOpenDept(dept)}
                data-testid={`admin-dept-${dept.toLowerCase()}`} aria-expanded={open}
                className={`whitespace-nowrap px-4 py-3 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-1 transition-colors duration-200 ${
                  active ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}>
                {dept} <ChevronDown className="h-3 w-3" />
              </button>
              {open && (
                <div className="absolute left-0 top-full z-30 min-w-44 bg-white border border-zinc-200 rounded-md shadow-lg py-1"
                  data-testid={`admin-dept-menu-${dept.toLowerCase()}`}>
                  {ids.map((id) => {
                    const t = tabs.find((x) => x.id === id);
                    if (!t) return null;
                    return (
                      <button key={id} onClick={() => { setTab(id); setOpenDept(""); }} data-testid={`admin-tab-${id}`}
                        className={`block w-full text-left px-4 py-2 text-sm transition-colors duration-150 ${
                          tab === id ? "font-semibold text-zinc-950 bg-zinc-50" : "text-zinc-600 hover:bg-zinc-50"}`}>
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {tab === "menu" && (
        <MobileMenuHome
          name="Control Tower"
          stat={null}
          items={tabs.map((t) => ({ id: t.id, label: typeof t.label === "string" ? t.label : "Inbox", badge: t.id === "inbox" ? inboxNew : 0 }))}
          onSelect={(id) => setTab(id)}
        />
      )}
      {tab !== "menu" && (
        <MobileBackHeader
          title={(() => { const l = tabs.find((t) => t.id === tab)?.label; return typeof l === "string" ? l : "Inbox"; })()}
          onBack={() => setTab("menu")}
        />
      )}

      <div className="mt-6 sm:mt-8">
        {tab === "overview" && (
          <form onSubmit={saveOffer} className="mb-6 border border-zinc-200 rounded-md p-5 bg-white" data-testid="launch-offer-card">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="font-display font-bold text-zinc-950">Homepage banner</p>
                <p className="text-xs text-zinc-500 mt-0.5">A slim bar at the top of the homepage. Good for launch offers.</p>
              </div>
              <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 cursor-pointer" data-testid="launch-offer-toggle">
                <input type="checkbox" checked={offerForm.enabled}
                  onChange={(e) => setOfferForm({ ...offerForm, enabled: e.target.checked })} className="h-4 w-4 accent-zinc-900" />
                {offerForm.enabled ? "On" : "Off"}
              </label>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <input value={offerForm.text} maxLength={160}
                onChange={(e) => setOfferForm({ ...offerForm, text: e.target.value })}
                placeholder="e.g. Launch offer: your first listing is featured free this month"
                data-testid="launch-offer-text"
                className="flex-1 h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              <button type="submit" disabled={offerSaving} data-testid="launch-offer-save"
                className="h-11 px-6 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                {offerSaving ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        )}
        {tab === "marketing" && (
          <div className="mt-6 max-w-3xl" data-testid="marketing-tab">
            <h2 className="hidden sm:block font-display text-xl font-bold text-zinc-950">Marketing campaigns</h2>
            <p className="text-sm text-zinc-500 mt-1 mb-4">Write once with AI, personalise each email, attach offer codes and send. Up to 50 addresses per send.</p>
            <div className="glass rounded-lg p-6 space-y-4">
              <div className="flex flex-col sm:flex-row gap-2">
                <select value={mkt.audience} onChange={(e) => setMkt({ ...mkt, audience: e.target.value })}
                  data-testid="marketing-audience" className="sm:w-56 h-10 px-2 border border-zinc-300 rounded-md text-sm bg-white">
                  <option value="employers">Employers (win clients)</option>
                  <option value="candidates">Candidates (attract jobseekers)</option>
                </select>
                <input value={mkt.notes} onChange={(e) => setMkt({ ...mkt, notes: e.target.value })}
                  placeholder="What is this campaign about? (offer, angle, news)" data-testid="marketing-notes"
                  className="flex-1 h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                <button onClick={generateMarketing} disabled={mktBusy} data-testid="marketing-generate"
                  className="h-10 px-5 bg-blue-600 text-white text-xs font-semibold rounded-md hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap">
                  {mktBusy ? "Working…" : "Write with AI"}
                </button>
              </div>
              <div>
                <textarea value={mkt.emails} onChange={(e) => setMkt({ ...mkt, emails: e.target.value })} rows={3}
                  placeholder="Paste email addresses, separated by commas or new lines" data-testid="marketing-emails"
                  className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm" />
                <p className="text-xs text-zinc-400 mt-1" data-testid="marketing-emails-count">{mktEmails.length} valid address{mktEmails.length === 1 ? "" : "es"} detected</p>
              </div>
              <input value={mkt.subject} onChange={(e) => setMkt({ ...mkt, subject: e.target.value })}
                placeholder="Subject" data-testid="marketing-subject"
                className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm" />
              <div>
                <textarea value={mkt.body} onChange={(e) => setMkt({ ...mkt, body: e.target.value })} rows={8}
                  placeholder="Email body. Tokens: {{name}}, {{company}}, {{promo_code}}" data-testid="marketing-body"
                  className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm" />
                <p className="text-xs text-zinc-400 mt-1">Tokens are replaced per recipient: {"{{name}}"} {"{{company}}"} {"{{promo_code}}"}</p>
              </div>
              <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer" data-testid="marketing-personalize-label">
                <input type="checkbox" checked={mkt.personalize} onChange={(e) => setMkt({ ...mkt, personalize: e.target.checked })}
                  data-testid="marketing-personalize" className="h-4 w-4" />
                Personalise each email with an AI opening line (based on the recipient's address)
              </label>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 border-t border-zinc-100 pt-4">
                <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
                  <input type="checkbox" checked={!!mkt.promoPercent} onChange={(e) => setMkt({ ...mkt, promoPercent: e.target.checked ? "20" : "" })}
                    data-testid="marketing-promo-enable" className="h-4 w-4" />
                  Include an offer code
                </label>
                {mkt.promoPercent && (
                  <>
                    <div className="flex items-center gap-1.5">
                      <input type="number" min="1" max="100" value={mkt.promoPercent} onChange={(e) => setMkt({ ...mkt, promoPercent: e.target.value })}
                        data-testid="marketing-promo-percent" className="w-20 h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                      <span className="text-sm text-zinc-500">% off</span>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
                      <input type="checkbox" checked={mkt.promoUnique} onChange={(e) => setMkt({ ...mkt, promoUnique: e.target.checked })}
                        data-testid="marketing-promo-unique" className="h-4 w-4" />
                      Unique code per recipient (single use)
                    </label>
                  </>
                )}
              </div>
              <div className="flex items-center gap-3 border-t border-zinc-100 pt-4">
                <button onClick={sendMarketing} disabled={mktBusy || !mktEmails.length || !mkt.subject.trim() || mkt.body.trim().length < 20}
                  data-testid="marketing-send"
                  className="h-11 px-8 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50">
                  {mktBusy ? "Sending…" : `Send to ${mktEmails.length || 0} recipient${mktEmails.length === 1 ? "" : "s"}`}
                </button>
                {mktResult && (
                  <p className="text-sm text-green-700" data-testid="marketing-result">
                    Queued {mktResult.queued} email{mktResult.queued === 1 ? "" : "s"}{mktResult.codes_created ? ` · ${mktResult.codes_created} offer code${mktResult.codes_created === 1 ? "" : "s"} created` : ""}{mktResult.personalised ? ` · ${mktResult.personalised} personalised` : ""}, delivery status appears below as each send completes
                  </p>
                )}
              </div>
            </div>
            <div className="mt-6" data-testid="marketing-manager">
              <p className="font-display font-bold text-zinc-950 mb-2">Send history</p>
              <div className="flex flex-col sm:flex-row gap-2 mb-3" data-testid="marketing-history-filters">
                <input value={mktFilter.search} onChange={(e) => setMktFilter({ ...mktFilter, search: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") loadMktHistory(1); }}
                  placeholder="Search recipient, subject or code" data-testid="marketing-filter-search"
                  className="flex-1 h-9 px-3 border border-zinc-300 rounded-md text-sm" />
                <select value={mktFilter.status} onChange={(e) => { const f = { ...mktFilter, status: e.target.value }; setMktFilter(f); loadMktHistory(1, f); }}
                  data-testid="marketing-filter-status" className="sm:w-36 h-9 px-2 border border-zinc-300 rounded-md text-sm bg-white">
                  <option value="">All statuses</option>
                  <option value="sent">Sent</option>
                  <option value="sending">Sending</option>
                  <option value="failed">Failed</option>
                </select>
                <select value={mktFilter.audience} onChange={(e) => { const f = { ...mktFilter, audience: e.target.value }; setMktFilter(f); loadMktHistory(1, f); }}
                  data-testid="marketing-filter-audience" className="sm:w-40 h-9 px-2 border border-zinc-300 rounded-md text-sm bg-white">
                  <option value="">All audiences</option>
                  <option value="employers">Employers</option>
                  <option value="candidates">Candidates</option>
                </select>
                <label className="flex items-center gap-2 text-xs font-medium text-zinc-500 cursor-pointer whitespace-nowrap">
                  <input type="checkbox" checked={mktFilter.archived === "1"} onChange={(e) => { const f = { ...mktFilter, archived: e.target.checked ? "1" : "0" }; setMktFilter(f); loadMktHistory(1, f); }}
                    data-testid="marketing-filter-archived" className="h-4 w-4 accent-zinc-900" />
                  Archived only
                </label>
                <button onClick={() => loadMktHistory(1)} data-testid="marketing-filter-apply"
                  className="h-9 px-4 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200">Apply</button>
              </div>
              {mktHistory.length === 0 ? (
                <p className="text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-md p-8 text-center" data-testid="marketing-history-empty">No sends match these filters.</p>
              ) : (
                <div className="border border-zinc-200 rounded-md overflow-hidden" data-testid="marketing-history">
                  {mktHistory.map((h, i) => (
                    <div key={h.id || `${h.email}-${h.created_at || ""}`} className={`px-4 py-2.5 bg-white flex items-center justify-between gap-3 flex-wrap ${i > 0 ? "border-t border-zinc-100" : ""}`} data-testid={`marketing-history-row-${i}`}>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-zinc-900 truncate">{h.email}{h.archived ? " · archived" : ""}</p>
                        <p className="text-xs text-zinc-500 truncate">{h.subject}{h.promo_code ? ` · code ${h.promo_code}` : ""}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 flex-wrap">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                          (h.status || "sent") === "sent" ? "bg-green-100 text-green-700" :
                          h.status === "failed" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}
                          data-testid={`marketing-status-${i}`}>
                          {h.status === "failed" ? "Failed" : h.status === "sending" ? "Sending…" : "Sent"}
                        </span>
                        <p className="text-xs text-zinc-400" data-testid={`marketing-time-${i}`}>
                          {h.status === "sent" && h.delivered_at
                            ? `Sent ${new Date(h.delivered_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}`
                            : h.created_at ? `Queued ${new Date(h.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}
                        </p>
                        <button onClick={() => mktOpenView(h)} data-testid={`marketing-view-${i}`}
                          className="h-7 px-2.5 border border-zinc-300 text-[11px] font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">View</button>
                        <button onClick={() => mktEditSubject(h)} data-testid={`marketing-edit-${i}`}
                          className="h-7 px-2.5 border border-zinc-300 text-[11px] font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">Edit</button>
                        <button onClick={() => mktPatch(h.id, { archived: !h.archived }, h.archived ? "Back in history" : "Archived")} data-testid={`marketing-archive-${i}`}
                          className="h-7 px-2.5 border border-zinc-300 text-[11px] font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                          {h.archived ? "Unarchive" : "Archive"}
                        </button>
                        {h.archived && (
                          <button onClick={() => mktDelete(h)} data-testid={`marketing-delete-${i}`}
                            className="h-7 px-2.5 border border-red-200 text-red-600 text-[11px] font-semibold rounded-md hover:bg-red-50 transition-colors duration-200">Delete</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <Pager page={mktPage} total={mktTotal} onChange={(p) => loadMktHistory(p)} testid="marketing-pager" />
            </div>
            <Dialog open={!!mktView} onOpenChange={() => setMktView(null)}>
              <DialogContent className="bg-white sm:max-w-2xl" data-testid="marketing-view-dialog">
                <DialogHeader>
                  <DialogTitle className="font-display text-xl font-bold">{mktView?.subject || "Sent email"}</DialogTitle>
                </DialogHeader>
                {mktView && (
                  <div className="space-y-3 text-sm">
                    <p className="text-xs text-zinc-500" data-testid="marketing-view-meta">
                      To {mktView.email} · {mktView.audience}
                      {mktView.promo_code ? ` · code ${mktView.promo_code}` : ""}
                      {mktView.delivered_at ? ` · sent ${new Date(mktView.delivered_at).toLocaleString("en-GB")}` : (mktView.created_at ? ` · queued ${new Date(mktView.created_at).toLocaleString("en-GB")}` : "")}
                      {mktView.created_by ? ` · by ${mktView.created_by}` : ""}
                    </p>
                    {mktView.html ? (
                      <iframe title="Sent email preview" srcDoc={mktView.html} data-testid="marketing-view-frame"
                        className="w-full h-96 border border-zinc-200 rounded-md bg-white" />
                    ) : (
                      <p className="text-xs text-zinc-400 border border-dashed border-zinc-300 rounded-md p-6 text-center" data-testid="marketing-view-no-html">
                        This send predates stored previews, only the details above are on record.
                      </p>
                    )}
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        )}

        {tab === "indexing" && (
          <div className="mt-6" data-testid="indexing-tab">
            <div className="mb-4">
              <h2 className="hidden sm:block font-display text-xl font-bold text-zinc-950">Google indexing</h2>
              <p className="text-sm text-zinc-500 mt-1">Every approval, edit, close and expiry automatically notifies Google. Failures retry up to 3 times in the daily sweep.</p>
            </div>
            <div className="flex flex-col lg:flex-row gap-2 mb-4">
              <input value={idxFilter.q} onChange={(e) => setIdxFilter({ ...idxFilter, q: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") loadIndexLog(); }}
                placeholder="Search URL, job, company or email" data-testid="indexing-filter-q"
                className="flex-1 h-10 px-3 border border-zinc-300 rounded-md text-sm" />
              <select value={idxFilter.action} onChange={(e) => { const f = { ...idxFilter, action: e.target.value }; setIdxFilter(f); loadIndexLog(f); }}
                data-testid="indexing-filter-action" className="lg:w-48 h-10 px-2 border border-zinc-300 rounded-md text-sm bg-white">
                <option value="">All actions</option>
                <option value="URL_UPDATED">Updated / published</option>
                <option value="URL_DELETED">Removed</option>
              </select>
              <select value={idxFilter.status} onChange={(e) => { const f = { ...idxFilter, status: e.target.value }; setIdxFilter(f); loadIndexLog(f); }}
                data-testid="indexing-filter-status" className="lg:w-40 h-10 px-2 border border-zinc-300 rounded-md text-sm bg-white">
                <option value="">All statuses</option>
                <option value="sent">Sent</option>
                <option value="failed">Not sent</option>
              </select>
              <button onClick={() => loadIndexLog()} data-testid="indexing-filter-apply"
                className="h-10 px-5 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200">Apply</button>
              <button onClick={() => loadIndexLog()} data-testid="indexing-refresh"
                className="h-10 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">Refresh</button>
            </div>
            {tabLoading ? (
              <div className="border border-zinc-200 rounded-md p-10 animate-pulse bg-zinc-50" />
            ) : indexLog.length === 0 ? (
              <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center" data-testid="indexing-empty">
                <p className="font-display text-xl font-bold text-zinc-900">No indexing activity yet.</p>
                <p className="mt-2 text-sm text-zinc-500">Entries appear automatically when jobs are approved, edited, closed or expire.</p>
              </div>
            ) : (
              <div className="border border-zinc-200 rounded-md overflow-hidden" data-testid="indexing-log">
                {pageSlice(indexLog, idxPage).map((r, i) => (
                  <div key={`${r.url}-${r.created_at || ""}`} className={`p-3.5 bg-white flex items-center justify-between gap-3 flex-wrap ${i > 0 ? "border-t border-zinc-100" : ""}`} data-testid={`indexing-row-${i}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded" data-testid={`indexing-kind-${i}`}>
                          {r.kind === "job" ? "Job" : r.kind === "town" ? "Town page" : r.kind === "company" ? "Company" : "Page"}
                        </span>
                        <a href={r.url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-blue-700 hover:underline truncate max-w-md" data-testid={`indexing-url-${i}`}>
                          {r.ref?.title || (r.ref?.town ? `Jobs near ${r.ref.town}` : "") || r.ref?.company || r.url}
                        </a>
                      </div>
                      <p className="text-xs text-zinc-500 mt-1">
                        {r.ref?.company ? `${r.ref.company} · ` : ""}{r.ref?.posted_by ? `posted by ${r.ref.posted_by} · ` : ""}
                        {r.action === "URL_DELETED" ? "removal" : "update"}{r.retry_count ? ` · attempt ${r.retry_count + 1}` : ""}{r.detail ? ` · ${r.detail}` : ""}
                        {r.created_at ? ` · ${new Date(r.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}
                      </p>
                      <p className="text-[11px] text-zinc-400 truncate">{r.url}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                        r.retry_cancelled ? "bg-zinc-100 text-zinc-500" :
                        r.status === 200 ? "bg-green-50 text-green-700" :
                        r.failed && (r.retry_count || 0) < 3 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}`} data-testid={`indexing-status-${i}`}>
                        {r.retry_cancelled ? "Cancelled, sitemap only" :
                         r.status === 200 ? "Google accepted, 200" :
                         r.failed && (r.retry_count || 0) < 3 ? `Retrying, Google said ${r.status || "error"}` : `Not accepted, Google said ${r.status || "error"}`}
                      </span>
                      {r.kind === "job" && !r.retry_cancelled && (
                        <button onClick={() => pingAgain(r.url)} data-testid={`indexing-ping-${i}`}
                          className="h-8 px-3 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                          Ping again
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Pager page={idxPage} total={indexLog.length} onChange={setIdxPage} testid="indexing-pager" />
          </div>
        )}

        {tab === "syndication" && (
          <div className="mt-6" data-testid="syndication-tab">
            <div className="mb-4">
              <h2 className="hidden sm:block font-display text-xl font-bold text-zinc-950">Job syndication</h2>
              <p className="text-sm text-zinc-500 mt-1">Outbound Adzuna XML feed. Only live, verified-employer vacancies whose employer explicitly opted in to partner-board distribution are included. Partner-imported jobs (Adzuna, Reed, GOV/DWP, apprenticeships) are never re-syndicated.</p>
            </div>
            {tabLoading || !synd ? (
              <div className="border border-zinc-200 rounded-md p-10 animate-pulse bg-zinc-50" />
            ) : (
              <>
                <div className="border border-zinc-200 rounded-md p-4 bg-white mb-4 flex flex-col lg:flex-row lg:items-center gap-3" data-testid="syndication-feed-card">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">Public feed URL</p>
                    <p className="text-sm font-mono text-zinc-900 truncate mt-1" data-testid="syndication-feed-url">{synd.feed_url}</p>
                    <p className="text-xs text-zinc-500 mt-1">
                      Status: <span className={`font-semibold ${synd.status === "active" ? "text-green-700" : "text-amber-700"}`} data-testid="syndication-status">{synd.status === "active" ? "Active" : "Waiting for consented vacancies"}</span>
                      {" · "}Last generated: <span data-testid="syndication-last-generated">{synd.last_generated_at ? new Date(synd.last_generated_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "not yet requested"}</span>
                      {" · "}XML: <span className={`font-semibold ${synd.xml_valid ? "text-green-700" : "text-zinc-500"}`} data-testid="syndication-xml-valid">{synd.xml_valid == null ? "pending first generation" : synd.xml_valid ? "valid" : `invalid, ${synd.xml_error}`}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => loadSyndication()} data-testid="syndication-refresh"
                      className="h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">Refresh</button>
                    <a href={synd.feed_url.replace(/^https:\/\/hiredtoday\.co\.uk/, window.location.origin)} target="_blank" rel="noreferrer" data-testid="syndication-open-feed"
                      className="h-9 px-4 inline-flex items-center border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">Preview</a>
                    <a href={synd.feed_url.replace(/^https:\/\/hiredtoday\.co\.uk/, window.location.origin)} download="adzuna.xml" data-testid="syndication-download"
                      className="h-9 px-4 inline-flex items-center bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200">Download XML</a>
                  </div>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                  <div className="border border-zinc-200 rounded-md p-4 bg-white" data-testid="syndication-included">
                    <p className="text-2xl font-bold text-zinc-950">{synd.vacancies_included}</p>
                    <p className="text-xs text-zinc-500 mt-1">Included in feed</p>
                  </div>
                  <div className="border border-zinc-200 rounded-md p-4 bg-white" data-testid="syndication-excluded">
                    <p className="text-2xl font-bold text-zinc-950">{synd.vacancies_excluded}</p>
                    <p className="text-xs text-zinc-500 mt-1">Native vacancies excluded</p>
                  </div>
                  <div className="border border-zinc-200 rounded-md p-4 bg-white" data-testid="syndication-native-total">
                    <p className="text-2xl font-bold text-zinc-950">{synd.native_vacancies_total}</p>
                    <p className="text-xs text-zinc-500 mt-1">Native vacancies total</p>
                  </div>
                  <div className="border border-zinc-200 rounded-md p-4 bg-white" data-testid="syndication-partner-pool">
                    <p className="text-2xl font-bold text-zinc-950">{synd.partner_imported_pool_excluded}</p>
                    <p className="text-xs text-zinc-500 mt-1">Partner-imported jobs always excluded</p>
                  </div>
                </div>
                <div className="border border-zinc-200 rounded-md p-4 bg-white" data-testid="syndication-reasons">
                  <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Exclusion reasons</p>
                  {Object.keys(synd.exclusion_reasons || {}).length === 0 ? (
                    <p className="text-sm text-zinc-500">No exclusions, every native vacancy is currently in the feed.</p>
                  ) : (
                    Object.entries(synd.exclusion_reasons).map(([reason, count], i) => (
                      <div key={reason} className={`flex items-center justify-between py-2 ${i > 0 ? "border-t border-zinc-100" : ""}`} data-testid={`syndication-reason-${i}`}>
                        <span className="text-sm text-zinc-700">{reason}</span>
                        <span className="text-sm font-semibold text-zinc-950">{count}</span>
                      </div>
                    ))
                  )}
                  <p className="text-[11px] text-zinc-400 mt-3">{synd.consent_note} Feed follows the Adzuna XML specification and regenerates on every request; expired or closed vacancies drop out automatically.</p>
                </div>
              </>
            )}
          </div>
        )}

        {tab === "cvarchive" && (
          <div className="mt-6" data-testid="cv-archive-tab">
            <div className="mb-4">
              <h2 className="hidden sm:block font-display text-xl font-bold text-zinc-950">CV archive</h2>
              <p className="text-sm text-zinc-500 mt-1">Every CV generated on HiredToday is kept here permanently, even if the candidate later deletes their account. Download any of them at any time.</p>
            </div>
            {tabLoading || !cvArchive ? (
              <div className="border border-zinc-200 rounded-md p-10 animate-pulse bg-zinc-50" />
            ) : cvArchive.length === 0 ? (
              <div className="border border-dashed border-zinc-300 rounded-md p-10 text-center" data-testid="cv-archive-empty">
                <p className="text-sm font-semibold text-zinc-700">No archived CVs yet</p>
                <p className="text-xs text-zinc-500 mt-1">They appear here the first time a candidate saves their profile or downloads their PDF CV.</p>
              </div>
            ) : (
              <div className="border border-zinc-200 rounded-md overflow-x-auto bg-white" data-testid="cv-archive-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50 text-left">
                      <th className="px-4 py-3 font-semibold text-zinc-600">Candidate</th>
                      <th className="px-4 py-3 font-semibold text-zinc-600">Email</th>
                      <th className="px-4 py-3 font-semibold text-zinc-600">First archived</th>
                      <th className="px-4 py-3 font-semibold text-zinc-600">Last updated</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageSlice(cvArchive, cvPage).map((r, i) => (
                      <tr key={r.user_id} className="border-b border-zinc-100 last:border-0" data-testid={`cv-archive-row-${i}`}>
                        <td className="px-4 py-3 font-medium text-zinc-900">{r.name || "—"}</td>
                        <td className="px-4 py-3 text-zinc-600">{r.email}</td>
                        <td className="px-4 py-3 text-zinc-500 text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}</td>
                        <td className="px-4 py-3 text-zinc-500 text-xs">{r.updated_at ? new Date(r.updated_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => downloadArchivedCv(r.user_id, r.name)} data-testid={`cv-archive-download-${i}`}
                            className="h-8 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                            Download PDF
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Pager page={cvPage} total={(cvArchive || []).length} onChange={setCvPage} testid="cv-archive-pager" />
          </div>
        )}

        {tab === "chats" && (
          <div className="mt-6" data-testid="admin-chats-tab">
            <div className="mb-4">
              <h2 className="hidden sm:block font-display text-xl font-bold text-zinc-950">Application chats</h2>
              <p className="text-sm text-zinc-500 mt-1">Every conversation between employers and jobseekers after an application. Read-only oversight.</p>
            </div>
            <input value={chatSearch} onChange={(e) => { setChatOpen(null); setChatSearch(e.target.value); setChatsPage(1); }}
              placeholder="Search job, company or candidate…" data-testid="admin-chats-search"
              className="mb-4 w-full sm:w-96 h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600" />
            {chatOpen ? (
              <div className="border border-zinc-200 rounded-md bg-white max-w-3xl" data-testid="admin-chat-thread">
                <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-zinc-100">
                  <div className="min-w-0">
                    <p className="font-display font-bold text-zinc-950 truncate">{chatOpen.job_title}</p>
                    <p className="text-xs text-zinc-500 truncate">{chatOpen.company_name} · {chatOpen.candidate_name} ({chatOpen.candidate_email})</p>
                  </div>
                  <button onClick={() => { setChatOpen(null); setChatThread(null); }} data-testid="admin-chat-back"
                    className="shrink-0 h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                    Back to all chats
                  </button>
                </div>
                <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto">
                  {!chatThread ? (
                    <div className="h-24 rounded-md bg-zinc-50 animate-pulse" />
                  ) : chatThread.length === 0 ? (
                    <p className="text-sm text-zinc-500 text-center py-8" data-testid="admin-chat-empty">No messages in this conversation yet.</p>
                  ) : chatThread.map((m, i) => (
                    <div key={m.id} data-testid={`admin-chat-msg-${i}`}
                      className={`max-w-[80%] rounded-md px-4 py-2.5 ${m.sender_role === "employer" ? "ml-auto bg-blue-600 text-white" : "bg-zinc-100 text-zinc-900"}`}>
                      <p className={`text-[11px] font-semibold mb-0.5 ${m.sender_role === "employer" ? "text-blue-100" : "text-zinc-500"}`}>
                        {m.sender_name || (m.sender_role === "employer" ? chatOpen.company_name : chatOpen.candidate_name)} · {m.sender_role}
                      </p>
                      <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                      <p className={`text-[10px] mt-1 ${m.sender_role === "employer" ? "text-blue-200" : "text-zinc-400"}`}>
                        {m.created_at ? new Date(m.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                        {m.read_at ? " · read" : " · unread"}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ) : (tabLoading && !chats) || !chats ? (
              <div className="border border-zinc-200 rounded-md p-10 animate-pulse bg-zinc-50" />
            ) : chats.length === 0 ? (
              <div className="border border-dashed border-zinc-300 rounded-md p-10 text-center" data-testid="admin-chats-empty">
                <p className="text-sm font-semibold text-zinc-700">No chats found</p>
                <p className="text-xs text-zinc-500 mt-1">Conversations appear here once an employer and jobseeker message each other after an application.</p>
              </div>
            ) : (
              <div className="border border-zinc-200 rounded-md bg-white divide-y divide-zinc-100" data-testid="admin-chats-list">
                {pageSlice(chats, chatsPage).map((c, i) => (
                  <button key={c.application_id} onClick={() => openChat(c)} data-testid={`admin-chat-row-${i}`}
                    className="w-full text-left px-5 py-4 hover:bg-zinc-50 transition-colors duration-150 flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-zinc-950 truncate">{c.job_title}</p>
                      <p className="text-xs text-zinc-500 truncate">{c.company_name} · {c.candidate_name}</p>
                      <p className="text-xs text-zinc-400 truncate mt-0.5">{c.last_sender === "employer" ? "Employer" : "Candidate"}: {c.last_message}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-[11px] text-zinc-400">{c.last_at ? new Date(c.last_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</p>
                      <div className="flex justify-end gap-1.5 mt-1">
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">{c.messages} msg{c.messages === 1 ? "" : "s"}</span>
                        {c.unread_employer > 0 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" data-testid={`admin-chat-unread-employer-${i}`}>{c.unread_employer} unread by employer</span>}
                        {c.unread_candidate > 0 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-800" data-testid={`admin-chat-unread-candidate-${i}`}>{c.unread_candidate} unread by candidate</span>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            <Pager page={chatsPage} total={(chats || []).length} onChange={setChatsPage} testid="admin-chats-pager" />
          </div>
        )}

        {tab === "inbox" && (
          <div className="mt-6" data-testid="admin-inbox-tab">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="hidden sm:block font-display text-xl font-bold text-zinc-950">Inbox</h2>
                <p className="text-sm text-zinc-500 mt-1">
                  Mail received at {inboxStatus?.address || "office@hiredtoday.co.uk"}. Read-only mirror, the real mailbox is never changed.
                </p>
              </div>
              {inboxStatus?.connected && (
                <div className="flex gap-2">
                  {inboxStatus.account_mismatch && (
                    <span className="text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5" data-testid="inbox-account-mismatch">
                      Connected as {inboxStatus.email || "another account"}, expected {inboxStatus.expected_account}
                    </span>
                  )}
                  <span className="self-center text-[11px] text-zinc-400" data-testid="inbox-last-sync">
                    {inboxStatus.last_sync ? `Last synced ${timeAgo(inboxStatus.last_sync)}` : "Not synced yet"}
                  </span>
                  <button onClick={inboxSyncNow} data-testid="inbox-sync"
                    className="h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                    Sync now
                  </button>
                  <button onClick={inboxDisconnect} data-testid="inbox-disconnect"
                    className="h-9 px-4 border border-red-200 text-red-600 text-xs font-semibold rounded-md hover:bg-red-50 transition-colors duration-200">
                    Disconnect
                  </button>
                </div>
              )}
            </div>
            {!inboxStatus ? (
              <div className="border border-zinc-200 rounded-md p-10 animate-pulse bg-zinc-50" />
            ) : !inboxStatus.connected ? (
              <div className="border border-zinc-200 rounded-md p-8 bg-white max-w-xl" data-testid="inbox-connect-card">
                <p className="font-display font-bold text-zinc-950">Connect with office@centego.co.uk</p>
                <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                  office@hiredtoday.co.uk forwards into your Google Workspace mailbox <span className="font-semibold text-zinc-900">office@centego.co.uk</span>, choose that account when Google asks.
                  One-click connection with read-only access: only mail addressed to office@hiredtoday.co.uk is
                  mirrored here, nothing else in the mailbox is touched, and nothing is ever marked read, moved or
                  deleted. You can disconnect anytime.
                </p>
                <button onClick={inboxConnect} data-testid="inbox-connect"
                  className="mt-5 h-11 px-6 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200">
                  Connect inbox
                </button>
                {inboxStatus.last_attempt && (
                  <p className="text-xs text-red-600 mt-3 leading-relaxed" data-testid="inbox-last-attempt">
                    Last attempt: {inboxStatus.last_attempt.reason}
                    {inboxStatus.last_attempt.description ? `, ${inboxStatus.last_attempt.description}` : ""}
                    {" "}(redirect used: {inboxStatus.last_attempt.redirect_uri})
                  </p>
                )}
                {inboxStatus.messages > 0 && (
                  <p className="text-xs text-zinc-400 mt-3">{inboxStatus.messages} messages are mirrored from a previous connection.</p>
                )}
              </div>
            ) : inboxOpen ? (
              <div className="border border-zinc-200 rounded-md bg-white max-w-3xl" data-testid="inbox-open">
                <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-zinc-100">
                  <div className="min-w-0">
                    <p className="font-display font-bold text-zinc-950 truncate">{inboxOpen.message.subject}</p>
                    <p className="text-xs text-zinc-500 truncate">{inboxOpen.message.from} · {inboxOpen.message.date}</p>
                  </div>
                  <button onClick={() => setInboxOpen(null)} data-testid="inbox-back"
                    className="shrink-0 h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                    Back to inbox
                  </button>
                </div>
                <div className="p-5 space-y-4 max-h-[55vh] overflow-y-auto">
                  {inboxOpen.thread.map((t) => (
                    <div key={t.id} data-testid={`inbox-thread-${t.id}`}
                      className={`rounded-md px-4 py-3 ${t.direction === "out" ? "bg-blue-50 border border-blue-100" : "bg-zinc-50 border border-zinc-100"}`}>
                      <p className="text-[11px] font-semibold text-zinc-500 mb-1">
                        {t.direction === "out" ? "You (office@hiredtoday.co.uk)" : t.from}
                      </p>
                      <p className="text-sm text-zinc-800 whitespace-pre-wrap">{t.body || t.snippet}</p>
                    </div>
                  ))}
                </div>
                <div className="border-t border-zinc-100 p-4">
                  <textarea value={inboxReply} onChange={(e) => setInboxReply(e.target.value)} rows={3}
                    placeholder={`Reply as ${inboxStatus.address}…`} data-testid="inbox-reply-input"
                    className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                  <div className="flex justify-end mt-2">
                    <button onClick={sendInboxReply} disabled={inboxSending || !inboxReply.trim()} data-testid="inbox-reply-send"
                      className="h-10 px-5 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50 transition-colors duration-200">
                      {inboxSending ? "Sending…" : "Send reply"}
                    </button>
                  </div>
                </div>
              </div>
            ) : !inboxMsgs || inboxMsgs.length === 0 ? (
              <div className="border border-dashed border-zinc-300 rounded-md p-10 text-center" data-testid="inbox-empty">
                <p className="text-sm font-semibold text-zinc-700">No mail yet</p>
                <p className="text-xs text-zinc-500 mt-1">New mail to {inboxStatus.address} appears here within 5 minutes, or press Sync now.</p>
              </div>
            ) : (
              <div className="border border-zinc-200 rounded-md bg-white divide-y divide-zinc-100" data-testid="inbox-list">
                {pageSlice(inboxMsgs, inboxPage).map((m, i) => (
                  <button key={m.id} onClick={() => openInboxMessage(m)} data-testid={`inbox-msg-${i}`}
                    className="w-full text-left px-5 py-3.5 hover:bg-zinc-50 transition-colors duration-150 flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-zinc-950 truncate">{m.direction === "out" ? `To: ${m.to}` : m.from}</p>
                        {m.replied && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-green-50 text-green-700">replied</span>}
                      </div>
                      <p className="text-sm text-zinc-800 truncate mt-0.5">{m.subject}</p>
                      <p className="text-xs text-zinc-400 truncate mt-0.5">{m.snippet}</p>
                    </div>
                    <p className="shrink-0 text-[11px] text-zinc-400">
                      {m.internal_date ? new Date(m.internal_date).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : ""}
                    </p>
                  </button>
                ))}
              </div>
            )}
            <Pager page={inboxPage} total={(inboxMsgs || []).length} onChange={setInboxPage} testid="inbox-pager" />
          </div>
        )}

        {tab === "overview" && !stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats-loading">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="border border-zinc-200 rounded-md p-5 bg-white animate-pulse">
                <div className="h-7 w-16 bg-zinc-100 rounded" />
                <div className="h-3 w-24 bg-zinc-100 rounded mt-2" />
              </div>
            ))}
          </div>
        )}
        {tab === "overview" && stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
            {[
              { label: "Jobs awaiting review", value: stats.jobs_pending, tone: stats.jobs_pending > 0 ? "amber" : "green", tip: "Listings waiting for your decision", go: () => { setJobFilter("pending_review"); setJobsPage(1); setTab("jobs"); } },
              { label: "Live jobs", value: stats.jobs_live, tone: "green", tip: "Currently published listings", go: () => { setJobFilter("live"); setJobsPage(1); setTab("jobs"); } },
              { label: "Awaiting payment", value: stats.jobs_awaiting_payment, tone: stats.jobs_awaiting_payment > 0 ? "amber" : "", tip: "Checkout started but not completed", go: () => { setJobFilter("awaiting_payment"); setJobsPage(1); setTab("jobs"); } },
              { label: "Total users", value: stats.users_total, tip: "Every account on the platform", go: () => { setUserFilter((f) => ({ ...f, q: "", location: "", role: "" })); setUsersPage(1); setTab("users"); } },
              { label: "Candidates", value: stats.candidates, tip: "Jobseeker accounts", go: () => { setUserFilter((f) => ({ ...f, q: "", location: "", role: "candidate" })); setUsersPage(1); setTab("users"); } },
              { label: "Employers", value: stats.employers, tip: "Employer accounts, including team members", go: () => { setUserFilter((f) => ({ ...f, q: "", location: "", role: "employer" })); setUsersPage(1); setTab("users"); } },
              { label: "Applications sent", value: stats.applications, tip: "Applications candidates have submitted" },
              { label: "Sign-ups via Google", value: stats.auth_google ?? 0, tip: "Accounts created with Google sign-in", go: () => setTab("users") },
              { label: "Sign-ups via email", value: stats.auth_email ?? 0, tip: "Accounts created with email and password", go: () => setTab("users") },
              { label: "Revenue", value: `£${stats.revenue_gbp.toLocaleString("en-GB")}`, tone: "green", tip: "Total collected after discounts", go: () => setTab("payments") },
              { label: "Partner jobs live", value: stats.external_active ?? 0, tip: "Listings mirrored from partner boards", go: () => setTab("syndication") },
              { label: "Last partner sync", value: stats.adzuna_last_sync ? (stats.adzuna_last_sync.status === "ok" ? `OK · ${stats.adzuna_last_sync.upserted} jobs` : stats.adzuna_last_sync.status) : "never", tone: stats.adzuna_last_sync && stats.adzuna_last_sync.status === "failed" ? "red" : "", tip: "Most recent partner feed sync result", go: () => setTab("syndication") },
            ].map((s) => (
              <button key={s.label} type="button" onClick={s.go} disabled={!s.go}
                className={`group relative text-left border rounded-md p-5 bg-white transition-colors duration-200 ${s.go ? "cursor-pointer hover:border-zinc-400" : "cursor-default"} ${
                  s.tone === "amber" ? "border-amber-300 bg-amber-50/40" : s.tone === "red" ? "border-red-300 bg-red-50/40" : "border-zinc-200"}`}
                data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
                <p className={`font-display text-2xl sm:text-3xl font-black ${
                  s.tone === "green" ? "text-green-700" : s.tone === "amber" ? "text-amber-700" : s.tone === "red" ? "text-red-700" : "text-zinc-950"}`}>{s.value}</p>
                <p className="text-xs sm:text-sm text-zinc-500 mt-1">{s.label}</p>
                <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 z-30 mb-1.5 w-52 rounded-md bg-zinc-950 px-3 py-2 text-[11px] leading-relaxed text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100 text-center">
                  {s.tip}{s.go ? " · click to open" : ""}
                </span>
              </button>
            ))}
          </div>
        )}

        {tab === "jobs" && (
          <div>
            <div className="flex gap-2 mb-5 flex-wrap items-center" data-testid="admin-job-filters">
              <input value={jobSearch} onChange={(e) => { setJobSearch(e.target.value); setJobsPage(1); }}
                placeholder="Search title, company, location…" data-testid="admin-job-search"
                className="h-9 px-3 border border-zinc-300 rounded-md text-sm bg-white w-full sm:w-72 focus:outline-none focus:ring-2 focus:ring-blue-600" />
              {[["", "All"], ["pending_review", "In review"], ["live", "Live"], ["awaiting_payment", "Awaiting payment"], ["rejected", "Rejected"], ["expired", "Expired"], ["closed", "Closed"], ["archived", "Archived"]].map(([v, l]) => (
                <button key={v} onClick={() => { setJobFilter(v); setJobsPage(1); }} data-testid={`filter-${v || "all"}`}
                  className={`px-4 py-2 text-xs font-semibold rounded-full border transition-colors duration-200 ${
                    jobFilter === v ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 text-zinc-600 hover:border-zinc-500"}`}>
                  {l}
                </button>
              ))}
            </div>
            {modFlags && modFlags.recent_7d > 0 && (
              <div className="mb-5 border border-amber-300 bg-amber-50/50 rounded-md p-4" data-testid="moderation-flags-panel">
                <div className="flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4 text-amber-600 shrink-0" />
                  <p className="text-sm font-semibold text-zinc-950" data-testid="moderation-flags-count">
                    {modFlags.recent_7d} listing{modFlags.recent_7d === 1 ? "" : "s"} auto-blocked by content rules in the last 7 days
                  </p>
                </div>
                <div className="mt-3 space-y-2.5">
                  {modFlags.items.slice(0, 5).map((f, i) => (
                    <div key={f.id} className="text-xs text-zinc-600 leading-relaxed" data-testid={`moderation-flag-${i}`}>
                      <span className="font-semibold text-zinc-800">{f.company_name || f.employer_email}</span>
                      {" · "}{f.job_title || "Untitled listing"}{" · "}{f.action === "update" ? "edit attempt" : "new listing"}{" · "}
                      {new Date(f.created_at).toLocaleDateString("en-GB")}
                      <span className="block text-amber-800 mt-0.5">{f.issues.join(" ")}</span>
                      <button onClick={() => api.post(`/admin/moderation-flags/${f.id}/dismiss`).then(() => setModFlags({ ...modFlags, items: modFlags.items.filter((x) => x.id !== f.id), recent_7d: modFlags.recent_7d - 1 })).catch(() => {})}
                        data-testid={`moderation-dismiss-${i}`}
                        className="mt-1 text-[11px] font-semibold text-zinc-400 hover:text-zinc-700 underline">Dismiss</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {jobs.length === 0 ? (
              <p className="text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-md p-10 text-center" data-testid="admin-no-jobs">
                Nothing here right now.
              </p>
            ) : (
              <div className="border border-zinc-200 rounded-md" data-testid="admin-jobs-list">
                {pageSlice(jobs, jobsPage).map((job, i) => {
                  const st = JOB_STATUS[job.status] || JOB_STATUS.pending_review;
                  return (
                    <div key={job.id} data-testid={`admin-job-${job.slug}`}
                      className={`p-5 bg-white flex flex-col lg:flex-row lg:items-center gap-4 justify-between first:rounded-t-md last:rounded-b-md ${i > 0 ? "border-t border-zinc-100" : ""}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <p className="font-display font-bold text-zinc-950">{job.title}</p>
                          <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                          <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">{job.tier}</span>
                        </div>
                        <p className="text-sm text-zinc-500 mt-1">
                          <EmployerHoverCard name={job.company_name} jobId={job.id} cards={empCards} onEnter={() => loadEmpCard(job.id)} />
                          {" · "}{job.location}{formatSalary(job) ? ` · ${formatSalary(job)}` : ""} · posted {new Date(job.created_at).toLocaleDateString("en-GB")}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap shrink-0">
                        {job.status === "pending_review" && (
                          <>
                            <button onClick={() => patchJob(job.id, { status: "live" }, "Job approved and live")} data-testid={`approve-${job.slug}`}
                              className="inline-flex items-center gap-1.5 h-9 px-4 bg-green-600 text-white text-xs font-semibold rounded-md hover:bg-green-700 transition-colors duration-200">
                              <Check className="h-3.5 w-3.5" /> Approve
                            </button>
                            <button onClick={() => {
                              const note = window.prompt("Reason for rejection (the employer will see this):") || "Does not meet our listing guidelines";
                              patchJob(job.id, { status: "rejected", admin_note: note }, "Job rejected");
                            }} data-testid={`reject-${job.slug}`}
                              className="inline-flex items-center gap-1.5 h-9 px-4 bg-red-600 text-white text-xs font-semibold rounded-md hover:bg-red-700 transition-colors duration-200">
                              <X className="h-3.5 w-3.5" /> Reject
                            </button>
                          </>
                        )}
                        {job.status === "live" && (
                          <button onClick={() => patchJob(job.id, { status: "expired" }, "Job taken down")} data-testid={`takedown-${job.slug}`}
                            className="inline-flex items-center gap-1.5 h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                            Take down
                          </button>
                        )}
                        <button onClick={() => startEdit(job)} data-testid={`admin-edit-${job.slug}`}
                          className="inline-flex items-center gap-1.5 h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                          <Pencil className="h-3.5 w-3.5" /> Edit
                        </button>
                        <button onClick={() => deleteJob(job)} data-testid={`admin-delete-${job.slug}`}
                          className="inline-flex items-center gap-1.5 h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md text-red-600 hover:bg-red-50 transition-colors duration-200">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <Pager page={jobsPage} total={jobs.length} onChange={setJobsPage} testid="admin-jobs-pager" />
          </div>
        )}

        {tab === "people" && <PeopleAnalytics />}
        {tab === "users" && (
          <div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3" data-testid="users-count-bar">
              <p className="text-sm font-semibold text-zinc-900" data-testid="users-total-count">
                {usersTotal.toLocaleString()} user{usersTotal === 1 ? "" : "s"} on the platform
              </p>
              <p className="text-xs text-zinc-500" data-testid="users-role-counts">
                {(roleCounts.candidate || 0).toLocaleString()} candidates ·{" "}
                {(roleCounts.employer || 0).toLocaleString()} employers ·{" "}
                {(roleCounts.admin || 0).toLocaleString()} admins
              </p>
              {(userFilter.q || userFilter.location || userFilter.role) && (
                <p className="text-xs font-medium text-blue-700" data-testid="users-filtered-count">
                  {usersTotal.toLocaleString()} match your filters
                </p>
              )}
            </div>
            <div className="flex flex-col lg:flex-row gap-2 mb-4">
              <input value={userFilter.q} onChange={(e) => { setUserFilter({ ...userFilter, q: e.target.value }); setUsersPage(1); }}
                placeholder="Search name, email or company" data-testid="users-filter-q"
                className="flex-1 h-10 px-3 border border-zinc-300 rounded-md text-sm" />
              <input value={userFilter.location} onChange={(e) => { setUserFilter({ ...userFilter, location: e.target.value }); setUsersPage(1); }}
                placeholder="Filter by location" data-testid="users-filter-location"
                className="lg:w-48 h-10 px-3 border border-zinc-300 rounded-md text-sm" />
              <select value={userFilter.role} onChange={(e) => { setUserFilter({ ...userFilter, role: e.target.value }); setUsersPage(1); }}
                data-testid="users-filter-role" className="lg:w-40 h-10 px-2 border border-zinc-300 rounded-md text-sm bg-white">
                <option value="">All roles</option>
                <option value="candidate">Candidates</option>
                <option value="employer">Employers</option>
                <option value="admin">Admins</option>
              </select>
              <button onClick={() => setAddUserOpen(true)} data-testid="add-user-button"
                className="h-10 px-5 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 whitespace-nowrap">
                + Add user
              </button>
            </div>
            {Object.values(userSel).some(Boolean) && (
              <div className="flex items-center gap-2 mb-4 border border-zinc-200 rounded-md px-4 py-2.5 bg-zinc-50" data-testid="users-bulk-bar">
                <p className="text-xs font-semibold text-zinc-800">{Object.values(userSel).filter(Boolean).length} selected</p>
                <button onClick={() => bulkUserAction("deactivate")} data-testid="users-bulk-deactivate"
                  className="h-8 px-3 text-xs font-semibold border border-zinc-300 rounded-md hover:border-zinc-500">Deactivate selected</button>
                <button onClick={() => bulkUserAction("delete")} data-testid="users-bulk-delete"
                  className="h-8 px-3 text-xs font-semibold border border-red-300 text-red-700 rounded-md hover:bg-red-50">Delete selected</button>
                <button onClick={() => setUserSel({})} data-testid="users-bulk-clear"
                  className="h-8 px-3 text-xs text-zinc-500 hover:text-zinc-800">Clear</button>
              </div>
            )}
          <div className="border border-zinc-200 rounded-md overflow-x-auto" data-testid="admin-users-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50 text-left">
                  <th className="px-3 py-3 w-8">
                    <input type="checkbox" data-testid="users-select-all"
                      checked={users.length > 0 && users.every((u) => userSel[u.id])}
                      onChange={(e) => setUserSel(e.target.checked ? Object.fromEntries(users.map((u) => [u.id, true])) : {})} />
                  </th>
                  <th className="px-4 py-3 font-semibold text-zinc-600">Name</th>
                  <th className="px-4 py-3 font-semibold text-zinc-600">Email</th>
                  <th className="px-4 py-3 font-semibold text-zinc-600">Company</th>
                  <th className="px-4 py-3 font-semibold text-zinc-600">Role</th>
                  <th className="px-4 py-3 font-semibold text-zinc-600">Verified</th>
                  <th className="px-4 py-3 font-semibold text-zinc-600">Status</th>
                  <th className="px-4 py-3 font-semibold text-zinc-600">Last active</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-zinc-100 last:border-0" data-testid={`admin-user-${u.id}`}>
                    <td className="px-3 py-3">
                      <input type="checkbox" data-testid={`user-select-${u.id}`} checked={!!userSel[u.id]}
                        onChange={(e) => setUserSel({ ...userSel, [u.id]: e.target.checked })} />
                    </td>
                    <td className="px-4 py-3 font-medium text-zinc-900">{u.name}</td>
                    <td className="px-4 py-3 text-zinc-600">{u.email}</td>
                    <td className="px-4 py-3 text-zinc-600">{u.company_name || "-"}</td>
                    <td className="px-4 py-3">
                      <select value={u.role} onChange={(e) => patchUser(u.id, { role: e.target.value })}
                        data-testid={`user-role-${u.id}`}
                        className="h-8 px-2 border border-zinc-300 rounded-md text-xs bg-white">
                        <option value="candidate">Candidate</option>
                        <option value="employer">Employer</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td className="px-4 py-3" data-testid={`user-verified-${u.id}`}>
                      <div className="flex gap-1.5">
                        <span title={u.email_verified ? "Email verified" : "Email not verified"}
                          className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${u.email_verified ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>
                          Email
                        </span>
                        <span title={u.phone_verified ? `Mobile verified (${u.phone_masked})` : "Mobile not verified"}
                          className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${u.phone_verified ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>
                          SMS
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${u.is_active ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                        {u.is_active ? "Active" : "Deactivated"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-zinc-500 text-xs" data-testid={`user-last-seen-${u.id}`}>
                      {u.last_seen ? timeAgo(u.last_seen) : "never"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {u.role === "employer" && (
                          <button onClick={() => patchUser(u.id, { spotlight: !u.spotlight })} data-testid={`user-spotlight-${u.id}`}
                            title={u.spotlight ? "Remove from homepage spotlight" : "Feature on the homepage"} aria-label="Toggle homepage spotlight"
                            className={`h-8 w-8 inline-flex items-center justify-center border rounded-md transition-colors duration-200 ${
                              u.spotlight ? "border-yellow-400 bg-yellow-50 text-yellow-500" : "border-zinc-300 text-zinc-400 hover:text-yellow-500"}`}>
                            <Star className={`h-3.5 w-3.5 ${u.spotlight ? "fill-yellow-400" : ""}`} />
                          </button>
                        )}
                        <button onClick={() => sendInvite(u)} data-testid={`user-invite-${u.id}`} title="Email a link to set a new password" aria-label="Send invite"
                          className="h-8 w-8 inline-flex items-center justify-center text-zinc-600 hover:text-zinc-950 border border-zinc-300 rounded-md transition-colors duration-200">
                          <MailPlus className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => startEditUser(u)} data-testid={`user-edit-${u.id}`} title="Edit user" aria-label="Edit user"
                          className="h-8 w-8 inline-flex items-center justify-center text-zinc-600 hover:text-zinc-950 border border-zinc-300 rounded-md transition-colors duration-200">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => patchUser(u.id, { is_active: !u.is_active })} data-testid={`user-toggle-${u.id}`}
                          title={u.is_active ? "Deactivate" : "Reactivate"}
                          className="h-8 px-3 text-xs font-semibold text-zinc-600 hover:text-zinc-950 border border-zinc-300 rounded-md transition-colors duration-200 whitespace-nowrap">
                          {u.is_active ? "Deactivate" : "Reactivate"}
                        </button>
                        <button onClick={() => deleteUser(u)} data-testid={`user-delete-${u.id}`} title="Delete account and all its data" aria-label="Delete user"
                          className="h-8 w-8 inline-flex items-center justify-center text-red-600 border border-zinc-300 rounded-md hover:bg-red-50 transition-colors duration-200">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-zinc-500" data-testid="users-empty">No users match these filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Pager page={usersPage} total={usersTotal} per={usersLimit} onChange={setUsersPage} testid="admin-users-pager" />
            <select value={usersLimit} onChange={(e) => { setUsersLimit(parseInt(e.target.value, 10)); setUsersPage(1); }}
              data-testid="users-page-size" className="h-8 px-2 border border-zinc-300 rounded-md text-xs bg-white ml-auto">
              <option value="25">25 / page</option>
              <option value="50">50 / page</option>
              <option value="100">100 / page</option>
              <option value="200">200 / page</option>
            </select>
          </div>
          </div>
        )}

        {tab === "payments" && (
          <div className="space-y-6" data-testid="payments-tab">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="payments-summary">
              {paySummary ? (
                [
                  { label: "Total collected", value: gbp(paySummary.total_paid), sub: `${paySummary.count_paid} payments · after discounts`, tid: "pay-total" },
                  ...((payFilters.year || payFilters.month || payFilters.week) ? [] : [
                    { label: "Last 7 days", value: gbp(paySummary.last_7d), sub: "paid payments", tid: "pay-week" },
                    { label: "This month", value: gbp(paySummary.this_month), sub: `last 30 days: ${gbp(paySummary.last_30d)}`, tid: "pay-month" },
                  ]),
                  { label: "Pending checkouts", value: gbp(paySummary.pending_amount), sub: `${paySummary.pending_count} unfinished`, tid: "pay-pending" },
                  { label: "Refunded", value: gbp(paySummary.refunded_amount), sub: `${paySummary.refunded_count} payments`, tid: "pay-refunded" },
                ].map((c) => (
                  <div key={c.tid} className="border border-zinc-200 rounded-md p-4 bg-white" data-testid={c.tid}>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">{c.label}</p>
                    <p className="font-display text-xl font-bold text-zinc-950 mt-1">{c.value}</p>
                    <p className="text-[11px] text-zinc-400 mt-0.5">{c.sub}</p>
                  </div>
                ))
              ) : (
                <div className="col-span-full h-24 rounded-md bg-zinc-50 animate-pulse" />
              )}
            </div>

            {paySummary && (
              <div className="grid md:grid-cols-2 gap-3">
                <div className="border border-zinc-200 rounded-md p-5 bg-white" data-testid="pay-breakdown">
                  <p className="font-display font-bold text-zinc-950 mb-3">What's been sold</p>
                  <div className="space-y-1.5 text-sm">
                    {[["listing", "Job listings"], ["subscription", "Subscriptions"], ["topup", "Credit top-ups"]].map(([k, label]) => (
                      <div key={k} className="flex justify-between" data-testid={`pay-kind-${k}`}>
                        <span className="text-zinc-600">{label}</span>
                        <span className="font-semibold text-zinc-950">{gbp(paySummary.by_kind[k]?.amount || 0)} <span className="text-zinc-400 font-normal">({paySummary.by_kind[k]?.count || 0})</span></span>
                      </div>
                    ))}
                    <div className="flex justify-between pt-1.5 border-t border-zinc-100" data-testid="pay-promo-discount">
                      <span className="text-zinc-600">Promo discounts given</span>
                      <span className="font-semibold text-red-600">−{gbp(paySummary.promo_discount)}</span>
                    </div>
                    <div className="flex justify-between" data-testid="pay-free-listings">
                      <span className="text-zinc-600">Free listings published</span>
                      <span className="font-semibold text-zinc-950">{paySummary.free_listings || 0}</span>
                    </div>
                  </div>
                  {Object.keys(paySummary.by_tier || {}).length > 0 && (
                    <div className="mt-4 pt-3 border-t border-zinc-100">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 mb-2">Listings by tier</p>
                      <div className="space-y-1 text-sm">
                        {Object.entries(paySummary.by_tier).map(([tier, v]) => (
                          <div key={tier} className="flex justify-between" data-testid={`pay-tier-${tier}`}>
                            <span className="text-zinc-600 capitalize">{tier}</span>
                            <span className="text-zinc-900">{gbp(v.amount)} <span className="text-zinc-400">({v.count})</span></span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="border border-zinc-200 rounded-md p-5 bg-white" data-testid="pay-subs">
                  <p className="font-display font-bold text-zinc-950 mb-3">Subscriptions</p>
                  <div className="flex items-baseline gap-6">
                    <div>
                      <p className="font-display text-2xl font-bold text-zinc-950" data-testid="pay-subs-active">{paySummary.subs_active}</p>
                      <p className="text-[11px] text-zinc-400">active plans</p>
                    </div>
                    <div>
                      <p className="font-display text-2xl font-bold text-zinc-950" data-testid="pay-subs-mrr">{gbp(paySummary.subs_mrr)}</p>
                      <p className="text-[11px] text-zinc-400">monthly recurring (annual ÷ 12)</p>
                    </div>
                  </div>
                  <p className="text-xs text-zinc-500 mt-3">Renewal invoices are recorded automatically as payments, so these totals reconcile with Stripe billing.</p>
                </div>
              </div>
            )}

            {paySummary && (
              <div className="grid md:grid-cols-2 gap-3">
                <div className="border border-zinc-200 rounded-md bg-white overflow-hidden" data-testid="pay-weekly">
                  <p className="px-4 py-3 font-display font-bold text-zinc-950 border-b border-zinc-100">By week</p>
                  <table className="w-full text-sm">
                    <tbody>
                      {paySummary.weekly.slice(-8).reverse().map((w) => (
                        <tr key={w.week_start} className="border-b border-zinc-50 last:border-0">
                          <td className="px-4 py-2 text-zinc-600">w/c {new Date(w.week_start).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</td>
                          <td className="px-4 py-2 text-right font-medium text-zinc-900">{gbp(w.amount)} <span className="text-zinc-400 font-normal">({w.count})</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="border border-zinc-200 rounded-md bg-white overflow-hidden" data-testid="pay-monthly">
                  <p className="px-4 py-3 font-display font-bold text-zinc-950 border-b border-zinc-100">By month</p>
                  <table className="w-full text-sm">
                    <tbody>
                      {paySummary.monthly.slice(-6).reverse().map((m) => (
                        <tr key={m.month} className="border-b border-zinc-50 last:border-0">
                          <td className="px-4 py-2 text-zinc-600">{new Date(m.month + "-02").toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</td>
                          <td className="px-4 py-2 text-right font-medium text-zinc-900">{gbp(m.amount)} <span className="text-zinc-400 font-normal">({m.count})</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2 items-center" data-testid="payments-filters">
              <input value={payFilters.q} onChange={(e) => setPayFilters({ ...payFilters, q: e.target.value })}
                placeholder="Search job, company, email, code, session…" data-testid="payments-search"
                className="w-full sm:w-72 h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600" />
              <select value={payFilters.kind} onChange={(e) => setPayFilters({ ...payFilters, kind: e.target.value })}
                data-testid="payments-kind" className="h-10 px-2 border border-zinc-300 rounded-md text-sm bg-white">
                <option value="">All types</option>
                <option value="listing">Listings</option>
                <option value="subscription">Subscriptions</option>
                <option value="topup">Top-ups</option>
              </select>
              <select value={payFilters.status} onChange={(e) => setPayFilters({ ...payFilters, status: e.target.value })}
                data-testid="payments-status" className="h-10 px-2 border border-zinc-300 rounded-md text-sm bg-white">
                <option value="">All statuses</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
                <option value="expired">Expired</option>
                <option value="refunded">Refunded</option>
              </select>
              <select value={payFilters.tier} onChange={(e) => setPayFilters({ ...payFilters, tier: e.target.value })}
                data-testid="payments-tier" className="h-10 px-2 border border-zinc-300 rounded-md text-sm bg-white">
                <option value="">All tiers</option>
                <option value="standard">Standard</option>
                <option value="featured">Featured</option>
                <option value="free">Free</option>
              </select>
              <select value={payFilters.promo} onChange={(e) => setPayFilters({ ...payFilters, promo: e.target.value })}
                data-testid="payments-promo" className="h-10 px-2 border border-zinc-300 rounded-md text-sm bg-white">
                <option value="">All pricing</option>
                <option value="yes">Discount applied</option>
                <option value="no">Full price only</option>
              </select>
              <select value={payFilters.year} onChange={(e) => setPayFilters({ ...payFilters, year: e.target.value })}
                data-testid="payments-year" className="h-10 px-2 border border-zinc-300 rounded-md text-sm bg-white">
                <option value="">All years</option>
                {[2026, 2025, 2024].map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              <select value={payFilters.month} onChange={(e) => setPayFilters({ ...payFilters, month: e.target.value })}
                data-testid="payments-month" className="h-10 px-2 border border-zinc-300 rounded-md text-sm bg-white">
                <option value="">All months</option>
                {["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
              <select value={payFilters.week} onChange={(e) => setPayFilters({ ...payFilters, week: e.target.value })}
                data-testid="payments-week" className="h-10 px-2 border border-zinc-300 rounded-md text-sm bg-white">
                <option value="">All weeks</option>
                {Array.from({ length: 53 }, (_, i) => { const w = i + 1; return <option key={w} value={w}>{`Week ${w}`}</option>; })}
              </select>
              <label className="flex items-center gap-1.5 text-xs text-zinc-600">
                <input type="checkbox" checked={showExcluded} onChange={(e) => setShowExcluded(e.target.checked)} data-testid="payments-show-excluded" />
                Show excluded
              </label>
              <input type="date" value={payFilters.from} onChange={(e) => setPayFilters({ ...payFilters, from: e.target.value })}
                data-testid="payments-from" className="h-10 px-2 border border-zinc-300 rounded-md text-sm bg-white" aria-label="From date" />
              <input type="date" value={payFilters.to} onChange={(e) => setPayFilters({ ...payFilters, to: e.target.value })}
                data-testid="payments-to" className="h-10 px-2 border border-zinc-300 rounded-md text-sm bg-white" aria-label="To date" />
              <button onClick={downloadPaymentsCsv} data-testid="payments-download-csv"
                className="h-10 px-4 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200">
                Download CSV
              </button>
            </div>

            {visibleTxns.length === 0 && !tabLoading ? (
              <p className="text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-md p-10 text-center" data-testid="no-transactions">
                No payments match these filters.
              </p>
            ) : (
              <div className="border border-zinc-200 rounded-md overflow-x-auto bg-white" data-testid="admin-transactions-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50 text-left">
                      <th className="px-4 py-3 font-semibold text-zinc-600">Date</th>
                      <th className="px-4 py-3 font-semibold text-zinc-600">Company</th>
                      <th className="px-4 py-3 font-semibold text-zinc-600">Item</th>
                      <th className="px-4 py-3 font-semibold text-zinc-600">Type</th>
                      <th className="px-4 py-3 font-semibold text-zinc-600">Amount</th>
                      <th className="px-4 py-3 font-semibold text-zinc-600">Discount</th>
                      <th className="px-4 py-3 font-semibold text-zinc-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleTxns.slice((payPage - 1) * 25, payPage * 25).map((t) => (
                      <tr key={t.id} onClick={() => openPayDetail(t)}
                        className={`border-b border-zinc-100 last:border-0 cursor-pointer hover:bg-zinc-50 transition-colors duration-150 ${t.excluded ? "opacity-50" : ""}`}
                        data-testid={`transaction-${t.id}`}>
                        <td className="px-4 py-3 text-zinc-500 whitespace-nowrap">{new Date(t.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-zinc-900">{t.company_name || "-"}</p>
                          <p className="text-[11px] text-zinc-400">{t.employer_email}</p>
                        </td>
                        <td className="px-4 py-3 font-medium text-zinc-900">{t.job_title || t.plan_name || "-"}</td>
                        <td className="px-4 py-3 text-zinc-600 capitalize whitespace-nowrap">
                          {t.kind}{t.kind === "listing" && t.tier ? ` · ${t.tier}` : ""}{t.urgent ? " · urgent" : ""}{t.annual ? " · annual" : ""}
                        </td>
                        <td className="px-4 py-3 text-zinc-900 font-medium whitespace-nowrap">
                          {gbp(t.amount)}
                          {t.promo_code && t.amount_gross != null && t.amount_gross !== t.amount && (
                            <span className="block text-[11px] font-normal text-zinc-400" data-testid={`transaction-promo-${t.id}`}>
                              was {gbp(t.amount_gross)} · {t.promo_code}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {t.amount_gross != null && t.amount_gross > t.amount ? (
                            <span className="text-xs font-semibold text-red-600" data-testid={`transaction-discount-${t.id}`}>−{gbp(t.amount_gross - t.amount)}</span>
                          ) : <span className="text-xs text-zinc-300">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                            t.payment_status === "paid" ? "bg-green-50 text-green-700" :
                            t.payment_status === "pending" ? "bg-amber-50 text-amber-700" :
                            t.payment_status === "refunded" ? "bg-red-50 text-red-700" : "bg-zinc-100 text-zinc-500"}`}>
                            {t.payment_status}
                          </span>
                          {t.excluded && <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500" data-testid={`transaction-excluded-${t.id}`}>excluded</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {visibleTxns.length > 25 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-100" data-testid="payments-pagination">
                    <p className="text-xs text-zinc-500">
                      Page {payPage} of {Math.ceil(visibleTxns.length / 25)} · {visibleTxns.length} payments
                    </p>
                    <div className="flex gap-2">
                      <button onClick={() => setPayPage((p) => Math.max(1, p - 1))} disabled={payPage === 1}
                        data-testid="payments-prev"
                        className="h-8 px-3 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 disabled:opacity-40 transition-colors duration-200">
                        Previous
                      </button>
                      <button onClick={() => setPayPage((p) => Math.min(Math.ceil(visibleTxns.length / 25), p + 1))}
                        disabled={payPage >= Math.ceil(visibleTxns.length / 25)}
                        data-testid="payments-next"
                        className="h-8 px-3 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 disabled:opacity-40 transition-colors duration-200">
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {payDetail && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setPayDetail(null)} data-testid="payment-detail-modal">
            <div className="bg-white rounded-md max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-display text-lg font-bold text-zinc-950">Payment detail</p>
                  <p className="text-xs text-zinc-400 mt-0.5">Invoice {`HT-${payDetail.created_at ? new Date(payDetail.created_at).toISOString().slice(0, 10).replaceAll("-", "") : "00000000"}-${payDetail.id.slice(-6).toUpperCase()}`}</p>
                </div>
                <button onClick={() => setPayDetail(null)} className="text-zinc-400 hover:text-zinc-700" data-testid="payment-detail-close"><X className="h-5 w-5" /></button>
              </div>
              <dl className="mt-4 space-y-2 text-sm">
                {[
                  ["Date", payDetail.created_at ? new Date(payDetail.created_at).toLocaleString("en-GB") : "—"],
                  ["Company", payDetail.company_name || "—"],
                  ["Employer email", payDetail.employer_email || "—"],
                  ["Item", payDetail.job_title || payDetail.plan_name || "—"],
                  ["Type", `${payDetail.kind}${payDetail.tier ? ` · ${payDetail.tier}` : ""}${payDetail.urgent ? " · urgent" : ""}${payDetail.annual ? " · annual" : ""}`],
                  ["Amount charged", gbp(payDetail.amount)],
                  ...(payDetail.amount_gross != null && payDetail.amount_gross !== payDetail.amount
                    ? [["List price", gbp(payDetail.amount_gross)], ["Promo", `${payDetail.promo_code} (−${gbp(payDetail.amount_gross - payDetail.amount)})`]] : []),
                  ["Stripe session", payDetail.session_id || "—"],
                  ["Stripe invoice", payDetail.stripe_invoice_id || "—"],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4">
                    <dt className="text-zinc-500 shrink-0">{k}</dt>
                    <dd className="text-zinc-900 font-medium text-right break-all">{v}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-5 pt-4 border-t border-zinc-100 space-y-3">
                <label className="block">
                  <span className="text-xs font-semibold text-zinc-600">Accounting note</span>
                  <textarea value={payEdit.note} onChange={(e) => setPayEdit({ ...payEdit, note: e.target.value })} rows={2}
                    data-testid="payment-note-input" placeholder="e.g. reconciled with Stripe payout…"
                    className="mt-1 w-full px-3 py-2 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                </label>
                <div className="flex flex-wrap items-center gap-4">
                  <label className="block">
                    <span className="text-xs font-semibold text-zinc-600">Status</span>
                    <select value={payEdit.payment_status} onChange={(e) => setPayEdit({ ...payEdit, payment_status: e.target.value })}
                      data-testid="payment-status-edit" className="mt-1 block h-10 px-2 border border-zinc-300 rounded-md text-sm bg-white">
                      {["paid", "pending", "failed", "expired", "refunded"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </label>
                  <label className="flex items-center gap-2 mt-4 text-sm text-zinc-700">
                    <input type="checkbox" checked={payEdit.excluded} onChange={(e) => setPayEdit({ ...payEdit, excluded: e.target.checked })} data-testid="payment-excluded-toggle" />
                    Exclude from totals (test / not a real sale)
                  </label>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  <button onClick={savePayDetail} data-testid="payment-save"
                    className="h-10 px-5 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200">Save changes</button>
                  <button onClick={() => downloadInvoice(payDetail.id)} data-testid="payment-invoice-pdf"
                    className="h-10 px-5 border border-zinc-300 text-sm font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">Download invoice PDF</button>
                </div>
                <p className="text-[11px] text-zinc-400">Amounts always come from Stripe records and can't be edited here. Every change is audit-logged.</p>
              </div>
            </div>
          </div>
        )}

        {tab === "promos" && (
          <div>
            <form onSubmit={createPromo} className="border border-zinc-200 rounded-md p-5 bg-white mb-5" data-testid="promo-create-form">
              <p className="font-display font-bold text-zinc-950">Create a code</p>
              <p className="text-xs text-zinc-500 mt-0.5">Employers enter it at checkout. Advertise it on the homepage banner (Overview tab).</p>
              <div className="grid sm:grid-cols-4 gap-3 mt-4">
                <input required data-testid="promo-code" value={promoForm.code}
                  onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
                  placeholder="CODE (e.g. LAUNCH25)"
                  className="h-11 px-3 border border-zinc-300 rounded-md text-sm uppercase focus:outline-none focus:ring-2 focus:ring-blue-600" />
                <div className="relative">
                  <input required type="number" min="1" max="100" data-testid="promo-percent" value={promoForm.percent}
                    onChange={(e) => setPromoForm({ ...promoForm, percent: e.target.value })}
                    className="w-full h-11 px-3 pr-8 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-zinc-400">%</span>
                </div>
                <input type="number" min="1" data-testid="promo-max-uses" value={promoForm.max_uses}
                  onChange={(e) => setPromoForm({ ...promoForm, max_uses: e.target.value })}
                  placeholder="Max uses (blank = unlimited)"
                  className="h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                <button type="submit" disabled={promoSaving} data-testid="promo-create-submit"
                  className="h-11 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                  {promoSaving ? "Creating…" : "Create code"}
                </button>
              </div>
            </form>
            {promos.length === 0 && !tabLoading ? (
              <p className="text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-md p-10 text-center" data-testid="no-promos">
                No codes yet.
              </p>
            ) : (
              <div className="border border-zinc-200 rounded-md overflow-x-auto" data-testid="promos-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50 text-left">
                      <th className="px-4 py-3 font-semibold text-zinc-600">Code</th>
                      <th className="px-4 py-3 font-semibold text-zinc-600">Discount</th>
                      <th className="px-4 py-3 font-semibold text-zinc-600">Used</th>
                      <th className="px-4 py-3 font-semibold text-zinc-600">Status</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {promos.map((promo) => (
                      <tr key={promo.id} className="border-b border-zinc-100 last:border-0" data-testid={`promo-${promo.code}`}>
                        <td className="px-4 py-3 font-mono font-semibold text-zinc-900">{promo.code}</td>
                        <td className="px-4 py-3 text-zinc-700">{promo.percent}% off</td>
                        <td className="px-4 py-3 text-zinc-600">{promo.uses}{promo.max_uses ? ` / ${promo.max_uses}` : ""}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${promo.active ? "bg-green-50 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>
                            {promo.active ? "Active" : "Off"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button onClick={() => togglePromo(promo)} data-testid={`promo-toggle-${promo.code}`}
                              className="h-8 px-3 text-xs font-semibold text-zinc-600 hover:text-zinc-950 border border-zinc-300 rounded-md transition-colors duration-200">
                              {promo.active ? "Switch off" : "Switch on"}
                            </button>
                            <button onClick={() => deletePromo(promo)} data-testid={`promo-delete-${promo.code}`} aria-label="Delete code"
                              className="h-8 w-8 inline-flex items-center justify-center text-red-600 border border-zinc-300 rounded-md hover:bg-red-50 transition-colors duration-200">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === "pricing" && (
          <div>
            <p className="text-xs text-zinc-500 mb-4">
              Change what employers pay. New checkouts use the new price straight away. Set a higher "was" price to show it struck through, like a sale.
            </p>
            {!pricingForm ? (
              <p className="text-sm text-zinc-500">Loading…</p>
            ) : (
              <div className="grid sm:grid-cols-3 gap-4" data-testid="pricing-manager">
                {[["standard", "Standard listing"], ["featured", "Featured listing"], ["urgent", "Urgent label add-on"]].map(([tier, label]) => (
                  <div key={tier} className="border border-zinc-200 rounded-md p-5 bg-white" data-testid={`pricing-edit-${tier}`}>
                    <p className="font-display font-bold text-zinc-950">{label}</p>
                    <label className="text-xs font-medium text-zinc-600 block mt-4 mb-1">Price (£)</label>
                    <input type="number" min="1" step="0.01" data-testid={`pricing-price-${tier}`}
                      value={pricingForm[tier].price}
                      onChange={(e) => setPricingForm({ ...pricingForm, [tier]: { ...pricingForm[tier], price: e.target.value } })}
                      className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                    <label className="text-xs font-medium text-zinc-600 block mt-3 mb-1">"Was" price (£, optional)</label>
                    <input type="number" min="1" step="0.01" data-testid={`pricing-compare-${tier}`}
                      value={pricingForm[tier].compare_at} placeholder="Leave blank for none"
                      onChange={(e) => setPricingForm({ ...pricingForm, [tier]: { ...pricingForm[tier], compare_at: e.target.value } })}
                      className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                    <button onClick={() => savePricing(tier)} disabled={pricingSaving === tier} data-testid={`pricing-save-${tier}`}
                      className="mt-4 w-full h-10 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                      {pricingSaving === tier ? "Saving…" : "Save"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          <div className="mt-10 border-t border-zinc-200 pt-8" data-testid="sub-plans-manager">
            <h3 className="font-display text-lg font-bold text-zinc-950">Subscription plans</h3>
            <p className="text-xs text-zinc-500 mt-1 mb-4">
              Plans for regular hirers, shown on the /plans page. Every price here feeds that page automatically.
              Credits release monthly (also on annual billing) and never roll over.
            </p>
            {!subPlans ? (
              <p className="text-sm text-zinc-500">Loading…</p>
            ) : (
              <>
                <div className="grid sm:grid-cols-3 gap-4">
                  {subPlans.plans.map((p, idx) => (
                    <div key={p.key} className="border border-zinc-200 rounded-md p-5 bg-white" data-testid={`sub-plan-edit-${p.key}`}>
                      <label className="text-xs font-medium text-zinc-600 block mb-1">Plan name</label>
                      <input value={p.name} data-testid={`sub-plan-name-${p.key}`}
                        onChange={(e) => updateSubPlan(idx, "name", e.target.value)}
                        className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                      <label className="text-xs font-medium text-zinc-600 block mt-3 mb-1">Monthly price (£){p.custom_only ? ", set per company" : ""}</label>
                      <input type="number" min="0" step="0.01" value={p.monthly_price} disabled={p.custom_only} data-testid={`sub-plan-price-${p.key}`}
                        onChange={(e) => updateSubPlan(idx, "monthly_price", e.target.value)}
                        className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm disabled:bg-zinc-50 disabled:text-zinc-400" />
                      <div className="grid grid-cols-2 gap-3 mt-3">
                        <div>
                          <label className="text-xs font-medium text-zinc-600 block mb-1">Jobs / month</label>
                          <input type="number" min="1" value={p.included_jobs} data-testid={`sub-plan-jobs-${p.key}`}
                            onChange={(e) => updateSubPlan(idx, "included_jobs", e.target.value)}
                            className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-zinc-600 block mb-1">Extra listing (£)</label>
                          <input type="number" min="0" step="0.01" value={p.extra_job_price} data-testid={`sub-plan-extra-${p.key}`}
                            onChange={(e) => updateSubPlan(idx, "extra_job_price", e.target.value)}
                            className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                        </div>
                      </div>
                      <label className="text-xs font-medium text-zinc-600 block mt-3 mb-1">Display order</label>
                      <input type="number" min="1" value={p.order} data-testid={`sub-plan-order-${p.key}`}
                        onChange={(e) => updateSubPlan(idx, "order", e.target.value)}
                        className="w-24 h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                      <div className="flex flex-wrap gap-x-4 gap-y-2 mt-3">
                        <label className="flex items-center gap-1.5 text-xs font-medium text-zinc-600">
                          <input type="checkbox" checked={!!p.active} onChange={(e) => updateSubPlan(idx, "active", e.target.checked)}
                            className="h-4 w-4 accent-zinc-900" data-testid={`sub-plan-active-${p.key}`} /> Visible
                        </label>
                        <label className="flex items-center gap-1.5 text-xs font-medium text-zinc-600">
                          <input type="checkbox" checked={!!p.recommended} onChange={(e) => updateSubPlan(idx, "recommended", e.target.checked)}
                            className="h-4 w-4 accent-zinc-900" data-testid={`sub-plan-recommended-${p.key}`} /> Recommended
                        </label>
                        <label className="flex items-center gap-1.5 text-xs font-medium text-zinc-600">
                          <input type="checkbox" checked={!!p.custom_only} onChange={(e) => updateSubPlan(idx, "custom_only", e.target.checked)}
                            className="h-4 w-4 accent-zinc-900" data-testid={`sub-plan-custom-${p.key}`} /> Custom (admin-assigned)
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap items-end gap-3 mt-4">
                  <div>
                    <label className="text-xs font-medium text-zinc-600 block mb-1">Annual discount (%)</label>
                    <input type="number" min="0" max="50" step="0.5" value={subPlans.annual_discount_pct} data-testid="sub-annual-discount"
                      onChange={(e) => setSubPlans({ ...subPlans, annual_discount_pct: e.target.value })}
                      className="w-28 h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                  </div>
                  <button onClick={saveSubPlans} disabled={subPlansSaving} data-testid="sub-plans-save"
                    className="h-10 px-6 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                    {subPlansSaving ? "Saving…" : "Save subscription plans"}
                  </button>
                </div>

                <div className="border border-zinc-200 rounded-md p-5 bg-white mt-6 max-w-2xl" data-testid="retention-settings">
                  <p className="font-display font-bold text-zinc-950">Cancellation retention offer</p>
                  <p className="text-xs text-zinc-500 mt-1 mb-3">
                    Shown once, just before an employer confirms cancellation. Each company can only ever use it once.
                  </p>
                  {(() => {
                    const rt = subPlans.retention || { enabled: true, percent: 20, months: 3, min_age_days: 30 };
                    const setRt = (f, v) => setSubPlans({ ...subPlans, retention: { ...rt, [f]: v } });
                    return (
                      <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
                        <label className="flex items-center gap-1.5 text-xs font-medium text-zinc-600">
                          <input type="checkbox" checked={!!rt.enabled} onChange={(e) => setRt("enabled", e.target.checked)}
                            className="h-4 w-4 accent-zinc-900" data-testid="retention-enabled" /> Offer enabled
                        </label>
                        <div>
                          <label className="text-xs font-medium text-zinc-600 block mb-1">Discount (%)</label>
                          <input type="number" min="5" max="50" value={rt.percent} data-testid="retention-percent"
                            onChange={(e) => setRt("percent", e.target.value)}
                            className="w-24 h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-zinc-600 block mb-1">For how many monthly payments</label>
                          <input type="number" min="1" max="12" value={rt.months} data-testid="retention-months"
                            onChange={(e) => setRt("months", e.target.value)}
                            className="w-24 h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-zinc-600 block mb-1">Only after plan age (days)</label>
                          <input type="number" min="0" max="365" value={rt.min_age_days} data-testid="retention-min-age"
                            onChange={(e) => setRt("min_age_days", e.target.value)}
                            className="w-24 h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                        </div>
                        <p className="text-xs text-zinc-400 w-full">Saved together with the subscription plans button above.</p>
                      </div>
                    );
                  })()}
                </div>

                <div className="border border-zinc-200 rounded-md p-5 bg-white mt-6 max-w-2xl" data-testid="custom-plan-card">
                  <p className="font-display font-bold text-zinc-950">Custom company plan</p>
                  <p className="text-xs text-zinc-500 mt-1 mb-3">
                    Assign a tailored plan (for example High Volume) to one company. Credits start straight away and renew monthly. No Stripe billing is created.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input value={customForm.employer_email} placeholder="employer@company.co.uk" data-testid="custom-plan-email"
                      onChange={(e) => setCustomForm({ ...customForm, employer_email: e.target.value })}
                      className="h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                    <input value={customForm.plan_name} placeholder="Plan name" data-testid="custom-plan-name"
                      onChange={(e) => setCustomForm({ ...customForm, plan_name: e.target.value })}
                      className="h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                    <input type="number" min="1" value={customForm.included_jobs} placeholder="Jobs per month" data-testid="custom-plan-jobs"
                      onChange={(e) => setCustomForm({ ...customForm, included_jobs: e.target.value })}
                      className="h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                    <input type="number" min="0" step="0.01" value={customForm.extra_job_price} placeholder="Extra listing (£)" data-testid="custom-plan-extra"
                      onChange={(e) => setCustomForm({ ...customForm, extra_job_price: e.target.value })}
                      className="h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                  </div>
                  <button onClick={assignCustom} disabled={customBusy || !customForm.employer_email.trim()} data-testid="custom-plan-assign"
                    className="mt-4 h-10 px-6 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                    {customBusy ? "Assigning…" : "Assign custom plan"}
                  </button>
                </div>

                <div className="mt-6" data-testid="enquiries-list">
                  <p className="font-display font-bold text-zinc-950 mb-2">Enterprise enquiries</p>
                  {enquiries.length === 0 ? (
                    <p className="text-sm text-zinc-500">None yet. The "Request a custom plan" form on the plans page lands here.</p>
                  ) : (
                    <ul className="space-y-3">
                      {pageSlice(enquiries, enqPage).map((q) => (
                        <li key={q.id} className="border border-zinc-200 rounded-md p-4 bg-white" data-testid={`enquiry-${q.id}`}>
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-sm font-semibold text-zinc-900">{q.company_name}
                              <span className="ml-2 text-xs font-normal text-zinc-500">{q.contact_name} · {q.email}{q.phone ? ` · ${q.phone}` : ""}</span>
                            </p>
                            <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                              q.status === "done" ? "bg-green-100 text-green-700" : q.status === "in_progress" ? "bg-blue-50 text-blue-700" : "bg-amber-100 text-amber-700"}`}
                              data-testid={`enquiry-status-${q.id}`}>
                              {q.status === "in_progress" ? "In progress" : q.status}
                            </span>
                          </div>
                          <p className="text-xs text-zinc-500 mt-1">
                            Expects ~{q.monthly_volume || "?"} jobs a month · {q.created_at ? new Date(q.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ""}
                          </p>
                          {q.message && <p className="text-sm text-zinc-600 mt-2 border-l-2 border-zinc-200 pl-3">{q.message}</p>}
                          <div className="flex gap-2 mt-3">
                            {q.status !== "in_progress" && (
                              <button onClick={() => setEnquiryStatus(q.id, "in_progress")} data-testid={`enquiry-progress-${q.id}`}
                                className="h-8 px-3 text-xs font-semibold border border-zinc-300 rounded-md hover:border-zinc-500 transition-colors duration-200">
                                Mark in progress
                              </button>
                            )}
                            {q.status !== "done" && (
                              <button onClick={() => setEnquiryStatus(q.id, "done")} data-testid={`enquiry-done-${q.id}`}
                                className="h-8 px-3 text-xs font-semibold border border-zinc-300 rounded-md hover:border-zinc-500 transition-colors duration-200">
                                Mark done
                              </button>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  <Pager page={enqPage} total={enquiries.length} onChange={setEnqPage} testid="enquiries-pager" />
                </div>

                <div className="mt-6" data-testid="subs-list">
                  <p className="font-display font-bold text-zinc-950 mb-2">Subscriptions</p>
                  <div className="flex items-center gap-2 mb-3">
                    <select value={subStatusFilter} onChange={(e) => { setSubStatusFilter(e.target.value); setSubsPage(1); }}
                      data-testid="subs-status-filter" className="h-9 px-2 border border-zinc-300 rounded-md text-sm bg-white">
                      <option value="">All statuses</option>
                      <option value="active">Active</option>
                      <option value="upcoming">Upcoming</option>
                      <option value="payment_failed">Payment failed</option>
                      <option value="cancelling">Cancelling</option>
                      <option value="expired">Expired</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  {subsList.length === 0 ? (
                    <p className="text-sm text-zinc-500">None yet.</p>
                  ) : (
                    <div className="border border-zinc-200 rounded-md overflow-hidden bg-white">
                      {pageSlice(subsList.filter((s) => !subStatusFilter || subStatusOf(s).key === subStatusFilter), subsPage).map((s) => (
                        <div key={s.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 border-b border-zinc-100 last:border-0 text-sm" data-testid={`sub-row-${s.id}`}>
                          <span className="font-semibold text-zinc-900">{s.company}</span>
                          <span className="text-zinc-600">{s.plan_name}{s.annual ? " (annual)" : ""}</span>
                          {(() => { const st = subStatusOf(s); return (
                            <span data-testid={`sub-status-${s.id}`} className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${st.cls}`}>{st.label}</span>
                          ); })()}
                          <span className="text-xs text-zinc-500">{s.credits_used}/{s.credits_total} used</span>
                          {s.credit_period_end && (
                            <span className="text-xs text-zinc-500">credits renew {new Date(s.credit_period_end).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
                          )}
                          {s.custom && s.status !== "cancelled" && (
                            <button onClick={() => endCustom(s.employer_id)} data-testid={`sub-end-custom-${s.id}`}
                              className="ml-auto text-xs font-semibold text-red-600 hover:text-red-800">End plan</button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <Pager page={subsPage} total={subsList.length} onChange={setSubsPage} testid="subs-pager" />
                </div>
              </>
            )}
          </div>
          </div>
        )}

        {tab === "insights" && (
          <div data-testid="insights-admin">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <p className="text-xs text-zinc-500 max-w-xl">
                Official feeds are checked once a day. Items are link cards, the publisher's title, their own short summary,
                and a link out. Only original HiredToday pieces get their own indexable page.
              </p>
              <button onClick={triggerSync} disabled={insightBusy === "sync"} data-testid="insights-sync-now"
                className="h-9 px-4 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                {insightBusy === "sync" ? "Queuing…" : "Check for updates now"}
              </button>
            </div>

            <div className="grid lg:grid-cols-2 gap-6 items-start">
              <div>
                <p className="font-display font-bold text-zinc-950 mb-2">Sources</p>
                {!insightSources ? (
                  <p className="text-sm text-zinc-500">Loading…</p>
                ) : (
                  <ul className="space-y-2" data-testid="insight-sources">
                    {insightSources.map((s) => (
                      <li key={s.id} className="border border-zinc-200 rounded-md p-4 bg-white" data-testid={`source-${s.key}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-zinc-900">{s.name}
                            {s.official && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">Official</span>}
                          </p>
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                            s.last_status === "ok" ? "bg-green-100 text-green-700" : s.last_status === "error" ? "bg-red-100 text-red-700" : "bg-zinc-100 text-zinc-500"}`}
                            data-testid={`source-status-${s.key}`}>
                            {s.last_status === "never" ? "not checked yet" : s.last_status}
                          </span>
                        </div>
                        <p className="text-xs text-zinc-500 mt-1 break-all">{s.feed_url}</p>
                        {s.last_error && <p className="text-xs text-red-600 mt-1" data-testid={`source-error-${s.key}`}>{s.last_error}</p>}
                        <p className="text-xs text-zinc-400 mt-1">
                          {s.category} · {s.total_fetched} items collected
                          {s.last_fetched_at && ` · last checked ${new Date(s.last_fetched_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}`}
                        </p>
                        <div className="flex gap-2 mt-2.5">
                          <button onClick={() => toggleSource(s)} data-testid={`source-toggle-${s.key}`}
                            className="h-8 px-3 text-xs font-semibold border border-zinc-300 rounded-md hover:border-zinc-500 transition-colors duration-200">
                            {s.active ? "Pause" : "Resume"}
                          </button>
                          <button onClick={() => deleteSource(s)} data-testid={`source-delete-${s.key}`}
                            className="h-8 px-3 text-xs font-semibold text-red-600 border border-zinc-300 rounded-md hover:bg-red-50 transition-colors duration-200">
                            Remove
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="border border-zinc-200 rounded-md p-4 bg-white mt-4" data-testid="source-add">
                  <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Add an authorised source</p>
                  <div className="space-y-2">
                    <input value={sourceForm.name} placeholder="Source name" data-testid="source-name"
                      onChange={(e) => setSourceForm({ ...sourceForm, name: e.target.value })}
                      className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                    <input value={sourceForm.feed_url} placeholder="https://… RSS or Atom feed URL" data-testid="source-url"
                      onChange={(e) => setSourceForm({ ...sourceForm, feed_url: e.target.value })}
                      className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                    <div className="flex gap-2">
                      <select value={sourceForm.category} onChange={(e) => setSourceForm({ ...sourceForm, category: e.target.value })}
                        data-testid="source-category" className="flex-1 h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white">
                        {["Careers news", "Labour market data", "Skills & training", "Policy & benefits", "Employer guidance"].map((c) => <option key={c}>{c}</option>)}
                      </select>
                      <button onClick={addSource} disabled={insightBusy === "source" || !sourceForm.name.trim() || !sourceForm.feed_url.trim()}
                        data-testid="source-add-btn"
                        className="h-10 px-5 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50">
                        Add source
                      </button>
                    </div>
                  </div>
                </div>
                {insightHealth.length > 0 && (
                  <div className="border border-zinc-200 rounded-md p-4 bg-white mt-4" data-testid="insights-health">
                    <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Sync health</p>
                    <ul className="space-y-1.5 text-xs text-zinc-600">
                      {insightHealth.map((r, i) => (
                        <li key={`${r.started_at}-${r.trigger}`} data-testid={`sync-run-${i}`}>
                          <span className="font-semibold text-zinc-900">{new Date(r.started_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                          {" · "}{r.trigger} · {r.new_items} new · {r.errors} source error{r.errors === 1 ? "" : "s"} · {r.duration_s}s
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div>
                <p className="font-display font-bold text-zinc-950 mb-2">Latest items</p>
                <ul className="space-y-2 max-h-[480px] overflow-y-auto pr-1" data-testid="insight-items">
                  {insightItems.slice(0, 30).map((it) => (
                    <li key={it.id} className="border border-zinc-200 rounded-md p-3.5 bg-white" data-testid={`insight-row-${it.id}`}>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded">{it.category}</span>
                        {it.origin === "original" && <span className="text-[10px] font-bold uppercase tracking-wider bg-violet-50 text-violet-700 px-1.5 py-0.5 rounded">Original</span>}
                        {it.featured && <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Featured</span>}
                        {it.status === "hidden" && <span className="text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-600 px-1.5 py-0.5 rounded">Hidden</span>}
                      </div>
                      <p className="text-sm font-semibold text-zinc-900 mt-1.5 leading-snug">{it.title}</p>
                      <p className="text-[11px] text-zinc-400 mt-1">{it.source_name}{it.published_at && ` · ${new Date(it.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`}</p>
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => patchInsight(it.id, { featured: !it.featured })} data-testid={`insight-feature-${it.id}`}
                          className="h-7 px-2.5 text-[11px] font-semibold border border-zinc-300 rounded-md hover:border-zinc-500 transition-colors duration-200">
                          {it.featured ? "Unfeature" : "Feature"}
                        </button>
                        <button onClick={() => patchInsight(it.id, { status: it.status === "hidden" ? "published" : "hidden" })} data-testid={`insight-hide-${it.id}`}
                          className="h-7 px-2.5 text-[11px] font-semibold border border-zinc-300 rounded-md hover:border-zinc-500 transition-colors duration-200">
                          {it.status === "hidden" ? "Show" : "Hide"}
                        </button>
                        <button onClick={() => deleteInsight(it.id)} data-testid={`insight-delete-${it.id}`}
                          className="h-7 px-2.5 text-[11px] font-semibold text-red-600 border border-zinc-300 rounded-md hover:bg-red-50 transition-colors duration-200">
                          Delete
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="border border-zinc-200 rounded-md p-4 bg-white mt-4" data-testid="original-add">
                  <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">Write an original HiredToday piece</p>
                  <p className="text-[11px] text-zinc-400 mb-3">Substantial pieces get their own indexable page at /insights/… and appear in the sitemap. Publish unlocks once the title is 8+ characters and the body 200+.</p>
                  <div className="space-y-2">
                    <input value={originalForm.title} placeholder="Title" data-testid="original-title"
                      onChange={(e) => setOriginalForm({ ...originalForm, title: e.target.value })}
                      className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                    <input value={originalForm.summary} placeholder="One-line summary (shown on cards and search results)" data-testid="original-summary"
                      onChange={(e) => setOriginalForm({ ...originalForm, summary: e.target.value })}
                      className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                    <textarea value={originalForm.body} rows={6} placeholder="The full piece. Blank line between paragraphs." data-testid="original-body"
                      onChange={(e) => setOriginalForm({ ...originalForm, body: e.target.value })}
                      className="w-full px-3 py-2.5 border border-zinc-300 rounded-md text-sm" />
                    <div className="flex items-center justify-between gap-2">
                      <label className="flex items-center gap-1.5 text-xs font-medium text-zinc-600">
                        <input type="checkbox" checked={originalForm.featured}
                          onChange={(e) => setOriginalForm({ ...originalForm, featured: e.target.checked })}
                          className="h-4 w-4 accent-zinc-900" data-testid="original-featured" /> Featured
                      </label>
                      <button onClick={publishOriginal} disabled={insightBusy === "original" || originalForm.title.trim().length < 8 || originalForm.body.trim().length < 200}
                        data-testid="original-publish"
                        className="h-10 px-5 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50">
                        {insightBusy === "original" ? "Publishing…" : "Publish"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "talent" && (
          <div data-testid="talent-admin">
            <p className="text-xs text-zinc-500 mb-4 max-w-xl">
              The talent database is consent-based: candidates opt in, approve each contact request, and can withdraw any time.
              These controls set the guardrails; every action is logged below.
            </p>
            {!talentCfg || !talentOverview ? (
              <p className="text-sm text-zinc-500">Loading…</p>
            ) : (
              <div className="grid lg:grid-cols-2 gap-6 items-start">
                <div>
                  <div className="border border-zinc-200 rounded-md p-5 bg-white" data-testid="talent-settings">
                    <p className="font-display font-bold text-zinc-950 mb-3">Access & limits</p>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="flex items-end gap-1.5 text-xs font-medium text-zinc-600 pb-2">
                        <input type="checkbox" checked={!!talentCfg.enabled}
                          onChange={(e) => setTalentCfg({ ...talentCfg, enabled: e.target.checked })}
                          className="h-4 w-4 accent-zinc-900" data-testid="talent-enabled"
                          title="Master switch, off pauses all employer talent searches and contact requests site-wide" /> Talent pool open
                      </label>
                      <div>
                        <label className="text-xs font-medium text-zinc-600 block mb-1">Searches / company / day</label>
                        <input type="number" min="1" max="1000" value={talentCfg.searches_per_day} data-testid="talent-searches-limit"
                          onChange={(e) => setTalentCfg({ ...talentCfg, searches_per_day: Number(e.target.value) })}
                          className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-zinc-600 block mb-1">Contact requests / day</label>
                        <input type="number" min="1" max="500" value={talentCfg.access_requests_per_day} data-testid="talent-requests-limit"
                          onChange={(e) => setTalentCfg({ ...talentCfg, access_requests_per_day: Number(e.target.value) })}
                          className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-zinc-600 block mb-1">Stale profile reminder after (days)</label>
                        <input type="number" min="30" max="730" value={talentCfg.stale_profile_days} data-testid="talent-stale-days"
                          onChange={(e) => setTalentCfg({ ...talentCfg, stale_profile_days: Number(e.target.value) })}
                          className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                      </div>
                      <label className="flex items-end gap-1.5 text-xs font-medium text-zinc-600 pb-2">
                        <input type="checkbox" checked={!!talentCfg.require_subscription}
                          onChange={(e) => setTalentCfg({ ...talentCfg, require_subscription: e.target.checked })}
                          className="h-4 w-4 accent-zinc-900" data-testid="talent-require-sub" /> Requires an active plan
                      </label>
                    </div>
                    <button onClick={saveTalentCfg} disabled={talentCfgSaving} data-testid="talent-settings-save"
                      className="mt-4 h-10 px-6 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                      {talentCfgSaving ? "Saving…" : "Save settings"}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-4" data-testid="talent-stats">
                    {[
                      ["Discoverable candidates", talentOverview.discoverable_candidates],
                      ["Pending contact requests", talentOverview.pending_requests],
                      ["Requests (30 days)", talentOverview.requests_last_30d],
                      ["Stale profiles", talentOverview.stale_profiles],
                      ["Searches today", talentOverview.searches_today],
                      ["Contact requests today", talentOverview.access_requests_today],
                    ].map(([label, val]) => (
                      <div key={label} className="border border-zinc-200 rounded-md p-4 bg-white">
                        <p className="font-display text-2xl font-black text-zinc-950">{val}</p>
                        <p className="text-[11px] font-semibold text-zinc-500 mt-0.5">{label}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="border border-zinc-200 rounded-md p-4 bg-white" data-testid="talent-usage-top">
                    <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Busiest companies today (abuse watch)</p>
                    {talentOverview.usage_today_top.length === 0 ? (
                      <p className="text-xs text-zinc-400">No talent searches yet today.</p>
                    ) : (
                      <ul className="space-y-1.5 text-sm">
                        {talentOverview.usage_today_top.map((u) => (
                          <li key={u.company} className="flex justify-between"><span className="text-zinc-700">{u.company}</span>
                            <span className={`font-semibold ${u.actions > talentCfg.searches_per_day * 0.8 ? "text-red-600" : "text-zinc-900"}`}>{u.actions}</span></li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="border border-zinc-200 rounded-md p-4 bg-white" data-testid="talent-consent-log">
                    <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Recent consent records</p>
                    {talentOverview.recent_consent.length === 0 ? (
                      <p className="text-xs text-zinc-400">No consent changes recorded yet.</p>
                    ) : (
                      <ul className="space-y-1.5 text-xs">
                        {talentOverview.recent_consent.map((c) => (
                          <li key={`${c.user}-${c.at}`}>
                            <span className="font-semibold text-zinc-900">{c.user}</span>
                            <span className={c.granted ? "text-green-700" : "text-zinc-500"}> {c.granted ? "opted in" : "withdrew"} </span>
                            <span className="text-zinc-400">{new Date(c.at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="border border-zinc-200 rounded-md p-4 bg-white" data-testid="talent-requests-log">
                    <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Recent access requests</p>
                    {talentOverview.recent_requests.length === 0 ? (
                      <p className="text-xs text-zinc-400">None yet.</p>
                    ) : (
                      <ul className="space-y-1.5 text-xs">
                        {talentOverview.recent_requests.map((r) => (
                          <li key={`${r.company_name}-${r.candidate_name}-${r.created_at}`}>
                            <span className="font-semibold text-zinc-900">{r.company_name}</span>
                            <span className="text-zinc-500"> → {r.candidate_name} · </span>
                            <span className={r.status === "approved" ? "text-green-700 font-semibold" : r.status === "pending" ? "text-amber-700 font-semibold" : "text-zinc-500"}>{r.status}</span>
                            <span className="text-zinc-400"> · {new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}
            <TalentDirectory />
          </div>
        )}

        {tab === "analytics" && (
          <div data-testid="analytics-tab">
            <div className="flex flex-wrap gap-2 mb-5">
              <select value={analyticsFilters.days} onChange={(e) => setAnalyticsFilters({ ...analyticsFilters, days: Number(e.target.value) })}
                data-testid="analytics-days" className="h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white">
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </select>
              <select value={analyticsFilters.job_id} onChange={(e) => setAnalyticsFilters({ ...analyticsFilters, job_id: e.target.value })}
                data-testid="analytics-job-filter" className="h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white max-w-[220px]">
                <option value="">All jobs</option>
                {analyticsJobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
              </select>
              <select value={analyticsFilters.employer_id} onChange={(e) => setAnalyticsFilters({ ...analyticsFilters, employer_id: e.target.value })}
                data-testid="analytics-employer-filter" className="h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white max-w-[220px]">
                <option value="">All employers</option>
                {analyticsEmployers.map((u) => <option key={u.id} value={u.id}>{u.company_name || u.email}</option>)}
              </select>
              <select value={analyticsFilters.source} onChange={(e) => setAnalyticsFilters({ ...analyticsFilters, source: e.target.value })}
                data-testid="analytics-source-filter" className="h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white">
                <option value="">All sources</option>
                {(analytics?.sources || []).map((src) => <option key={src.source} value={src.source}>{src.source}</option>)}
              </select>
            </div>
            {!analytics ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[...Array(8)].map((_, i) => <div key={`analytics-skel-${i}`} className="h-24 bg-zinc-100 rounded-md animate-pulse" />)}</div>
            ) : (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="analytics-kpis">
                  {[["Views", analytics.views], ["Unique visitors", analytics.unique_visitors],
                    ["Apply starts", analytics.apply_starts], ["Applications", analytics.applications],
                    ["Conversion", `${analytics.conversion}%`], ["Saves", analytics.saves],
                    ["Hired", analytics.hired], ["In interview", analytics.funnel.interview || 0]].map(([label, val]) => (
                    <button key={label} onClick={() => {}} data-testid={`analytics-kpi-${label.toLowerCase().replace(/ /g, "-")}`}
                      className="border border-zinc-200 rounded-md p-5 bg-white text-left hover:border-zinc-400 transition-colors duration-200">
                      <p className="font-display text-3xl font-black text-zinc-950">{val}</p>
                      <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mt-1">{label}</p>
                    </button>
                  ))}
                </div>

                <h3 className="font-display text-lg font-bold text-zinc-950 mt-8 mb-3">Jobs by performance</h3>
                <div className="border border-zinc-200 rounded-md overflow-x-auto" data-testid="analytics-jobs-table">
                  <table className="w-full text-sm min-w-[560px]">
                    <thead>
                      <tr className="border-b border-zinc-200 bg-zinc-50 text-left">
                        <th className="px-4 py-3 font-semibold text-zinc-600">Job</th>
                        <th className="px-4 py-3 font-semibold text-zinc-600">Company</th>
                        <th className="px-4 py-3 font-semibold text-zinc-600">Views</th>
                        <th className="px-4 py-3 font-semibold text-zinc-600">Applications</th>
                        <th className="px-4 py-3 font-semibold text-zinc-600">Conversion</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.jobs.length === 0 ? (
                        <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-zinc-500">No activity in this period.</td></tr>
                      ) : analytics.jobs.map((j) => (
                        <tr key={j.id} className="border-b border-zinc-100 last:border-0" data-testid={`analytics-job-${j.id}`}>
                          <td className="px-4 py-3 font-medium text-zinc-900">{j.title}</td>
                          <td className="px-4 py-3 text-zinc-600">{j.company_name}</td>
                          <td className="px-4 py-3 text-zinc-600">{j.views}</td>
                          <td className="px-4 py-3 text-zinc-600">{j.applications}</td>
                          <td className="px-4 py-3 text-zinc-600">{j.conversion}%</td>
                          <td className="px-4 py-3 text-right">
                            <button onClick={() => setReportJobId(j.id)} data-testid={`analytics-report-${j.id}`}
                              className="text-xs font-semibold text-blue-700 hover:text-blue-900">Open report</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h3 className="font-display text-lg font-bold text-zinc-950 mt-8 mb-3">Latest applications</h3>
                <div className="border border-zinc-200 rounded-md overflow-x-auto" data-testid="analytics-applications-table">
                  <table className="w-full text-sm min-w-[560px]">
                    <thead>
                      <tr className="border-b border-zinc-200 bg-zinc-50 text-left">
                        <th className="px-4 py-3 font-semibold text-zinc-600">Candidate</th>
                        <th className="px-4 py-3 font-semibold text-zinc-600">Job</th>
                        <th className="px-4 py-3 font-semibold text-zinc-600">Source</th>
                        <th className="px-4 py-3 font-semibold text-zinc-600">Status</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.recent_applications.length === 0 ? (
                        <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-zinc-500">No applications yet.</td></tr>
                      ) : analytics.recent_applications.map((a) => (
                        <tr key={a.id} className="border-b border-zinc-100 last:border-0" data-testid={`analytics-app-${a.id}`}>
                          <td className="px-4 py-3 font-medium text-zinc-900">{a.candidate_name}</td>
                          <td className="px-4 py-3 text-zinc-600">{a.job_title} · {a.company_name}</td>
                          <td className="px-4 py-3 text-zinc-600">{a.source}</td>
                          <td className="px-4 py-3"><span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-700">{a.status}</span></td>
                          <td className="px-4 py-3 text-right">
                            <button onClick={() => setManageAppId(a.id)} data-testid={`analytics-manage-${a.id}`}
                              className="text-xs font-semibold text-blue-700 hover:text-blue-900">Manage</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {tab === "traffic" && <TrafficAnalytics />}

        {tab === "blasts" && (
          <div className="mt-6 max-w-2xl" data-testid="blasts-tab">
            <h2 className="hidden sm:block font-display text-xl font-bold text-zinc-950">Email blasts</h2>
            <p className="text-sm text-zinc-500 mt-1 mb-5">You send emails yourself, whenever you want — nothing automatic goes out.</p>
            <div className="border border-zinc-200 rounded-lg bg-white p-5 mb-4" data-testid="blast-candidates-card">
              <h3 className="text-sm font-bold text-zinc-950">Job picks for candidates</h3>
              <p className="text-xs text-zinc-500 mt-1 mb-4">Fresh listings matched to each candidate's saved searches, locations and preferences. Only goes to candidates with job alerts switched on.</p>
              <button onClick={() => sendBlast({ kind: "candidate_job_picks" })} disabled={blastSending}
                data-testid="blast-candidates-btn"
                className="h-10 px-5 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                {blastSending ? "Sending…" : "Send job picks now"}
              </button>
            </div>
            <div className="border border-zinc-200 rounded-lg bg-white p-5" data-testid="blast-employers-card">
              <h3 className="text-sm font-bold text-zinc-950">Marketing to employers</h3>
              <p className="text-xs text-zinc-500 mt-1 mb-4">Goes to every active employer account (owners only, not team members).</p>
              <input value={blastSubject} onChange={(e) => setBlastSubject(e.target.value)} placeholder="Subject"
                data-testid="blast-subject-input" maxLength={150}
                className="w-full h-10 border border-zinc-300 rounded-md px-3 text-sm mb-3 focus:outline-none focus:border-zinc-500" />
              <textarea value={blastBody} onChange={(e) => setBlastBody(e.target.value)} placeholder="Your message…" rows={6}
                data-testid="blast-body-input" maxLength={5000}
                className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm mb-3 focus:outline-none focus:border-zinc-500" />
              <button onClick={() => sendBlast({ kind: "employer_marketing", subject: blastSubject, body: blastBody })}
                disabled={blastSending || !blastSubject.trim() || !blastBody.trim()}
                data-testid="blast-employers-btn"
                className="h-10 px-5 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                {blastSending ? "Sending…" : "Send to all employers"}
              </button>
            </div>
            {blastResult && <p className="mt-3 text-sm font-medium text-zinc-700" data-testid="blast-result">{blastResult}</p>}
          </div>
        )}
        {tab === "emails" && (
          <div className="mt-6 max-w-4xl" data-testid="emails-tab">
            <h2 className="hidden sm:block font-display text-xl font-bold text-zinc-950">Email log</h2>
            <p className="text-sm text-zinc-500 mt-1 mb-4">Every email the platform sends, as it happens. Failures are highlighted so you can spot a problem in seconds.</p>
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <button onClick={sendTestEmail} disabled={emailTestSending} data-testid="email-test-btn"
                className="h-9 px-4 text-xs font-semibold rounded-md bg-zinc-950 text-white hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                {emailTestSending ? "Sending…" : "Send test email to office"}
              </button>
              {emailTestResult && (
                <span className={`text-xs font-medium ${emailTestResult.ok ? "text-emerald-700" : "text-red-700"}`} data-testid="email-test-result">
                  {emailTestResult.ok
                    ? `Sent to ${emailTestResult.to} — log says "${emailTestResult.logged_status}". Check the office inbox.`
                    : `Not sent — log says "${emailTestResult.logged_status}"${emailTestResult.error ? ` — ${emailTestResult.error}` : ""}`}
                </span>
              )}
            </div>
            {emailLog && (
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="border border-zinc-200 rounded-md p-4 bg-white" data-testid="email-sent-24h">
                  <p className="text-xs font-bold uppercase tracking-wider text-zinc-400">Sent · 24h</p>
                  <p className="font-display text-2xl font-bold text-zinc-950 mt-1">{emailLog.sent_24h}</p>
                </div>
                <div className={`border rounded-md p-4 ${emailLog.failed_24h > 0 ? "border-red-300 bg-red-50" : "border-zinc-200 bg-white"}`} data-testid="email-failed-24h">
                  <p className="text-xs font-bold uppercase tracking-wider text-zinc-400">Failed · 24h</p>
                  <p className={`font-display text-2xl font-bold mt-1 ${emailLog.failed_24h > 0 ? "text-red-700" : "text-zinc-950"}`}>{emailLog.failed_24h}</p>
                </div>
                <div className={`border rounded-md p-4 ${emailLog.failed_7d > 0 ? "border-amber-300 bg-amber-50" : "border-zinc-200 bg-white"}`} data-testid="email-failed-7d">
                  <p className="text-xs font-bold uppercase tracking-wider text-zinc-400">Failed · 7 days</p>
                  <p className={`font-display text-2xl font-bold mt-1 ${emailLog.failed_7d > 0 ? "text-amber-700" : "text-zinc-950"}`}>{emailLog.failed_7d}</p>
                </div>
              </div>
            )}
            <div className="flex gap-2 mb-4">
              {[["real", "Real users"], ["failed", "Failures only"], ["all", "Everything incl. test"]].map(([v, l]) => (
                <button key={v} onClick={() => setEmailFilter(v)} data-testid={`email-filter-${v}`}
                  className={`h-9 px-4 text-xs font-semibold rounded-md border transition-colors duration-200 ${emailFilter === v ? "bg-zinc-950 border-zinc-950 text-white" : "bg-white border-zinc-300 text-zinc-600 hover:border-zinc-500"}`}>
                  {l}
                </button>
              ))}
            </div>
            <div className="border border-zinc-200 rounded-lg overflow-hidden bg-white" data-testid="email-log-list">
              {!emailLog && <p className="p-6 text-sm text-zinc-500">Loading…</p>}
              {emailLog && emailLog.items.length === 0 && (
                <p className="p-6 text-sm text-zinc-500" data-testid="email-log-empty">No emails match this view.</p>
              )}
              {emailLog && emailLog.items.map((m) => (
                <div key={m.id} data-testid="email-log-item"
                  className={`px-4 py-3 border-b border-zinc-100 last:border-0 ${m.status === "failed" ? "bg-red-50" : ""}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-zinc-900 truncate">{m.subject || "(no subject)"}</p>
                      <p className="text-xs text-zinc-500 mt-0.5 truncate">to {m.to}</p>
                      {m.status === "failed" && m.error && (
                        <p className="text-xs text-red-700 mt-1 break-words" data-testid="email-log-error">{m.error}</p>
                      )}
                      {m.status === "failed" && (
                        <>
                        <button onClick={() => api.post(`/admin/email-failures/${m.id}/retry`).then(() => { toast.success("Retry sent"); setEmailLog({ ...emailLog, items: emailLog.items.map((x) => x.id === m.id ? { ...x, status: "resolved" } : x) }); }).catch((e) => toast.error(formatApiError(e, "This email can't be retried safely")))}
                          data-testid={`email-retry-${m.id}`}
                          className="mt-1.5 mr-3 text-[11px] font-semibold text-zinc-950 hover:text-zinc-700 underline">Retry send</button>
                        <button onClick={() => api.post(`/admin/email-failures/${m.id}/resolve`).then(() => { toast.success("Marked as resolved"); setEmailLog({ ...emailLog, items: emailLog.items.map((x) => x.id === m.id ? { ...x, status: "resolved" } : x) }); }).catch((e) => toast.error(formatApiError(e)))}
                          data-testid={`email-resolve-${m.id}`}
                          className="mt-1.5 text-[11px] font-semibold text-blue-700 hover:text-blue-900 underline">Mark as resolved</button>
                        </>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${m.status === "failed" ? "bg-red-600 text-white" : "bg-emerald-100 text-emerald-800"}`}
                        data-testid="email-log-status">
                        {m.status || "unknown"}
                      </span>
                      <p className="text-[11px] text-zinc-400 mt-1">
                        {m.created_at ? new Date(m.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {tab === "reviews" && (
          <div>
          <div className="flex flex-col sm:flex-row gap-2 mb-4" data-testid="reviews-filters">
            <input value={reviewFilter.q} onChange={(e) => setReviewFilter({ ...reviewFilter, q: e.target.value })}
              placeholder="Search text, company or reviewer" data-testid="reviews-search"
              className="flex-1 h-10 px-3 border border-zinc-300 rounded-md text-sm" />
            <select value={reviewFilter.rating} onChange={(e) => setReviewFilter({ ...reviewFilter, rating: e.target.value })}
              data-testid="reviews-filter-rating" className="sm:w-40 h-10 px-2 border border-zinc-300 rounded-md text-sm bg-white">
              <option value="">All ratings</option>
              {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} star{r === 1 ? "" : "s"}</option>)}
            </select>
          </div>
          {reviewsError ? (
            <div className="border border-red-200 bg-red-50/50 rounded-md p-10 text-center" data-testid="reviews-error">
              <p className="text-sm font-semibold text-red-700">Couldn't load reviews</p>
              <p className="text-xs text-red-600 mt-1">Refresh the page to try again.</p>
            </div>
          ) : reviews.length === 0 && !tabLoading ? (
            <p className="text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-md p-10 text-center" data-testid="no-reviews-admin">
              No reviews yet.
            </p>
          ) : (
            <div className="space-y-3" data-testid="admin-reviews-list">
              {reviews.map((r) => (
                <div key={r.id} className="border border-zinc-200 rounded-md p-5 bg-white flex items-start justify-between gap-4"
                  data-testid={`admin-review-${r.id}`}>
                  <div>
                    <p className="text-sm font-semibold text-zinc-950">
                      {r.company_name || "Unknown company"} <span className="font-normal text-zinc-500">· reviewed by {r.candidate_name || "Candidate"} · {r.rating}/5</span>
                    </p>
                    <p className="mt-2 text-sm text-zinc-700 whitespace-pre-line">{r.text}</p>
                    <p className="mt-2 text-xs text-zinc-400">{new Date(r.created_at).toLocaleString("en-GB")}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => { setEditReview(r); setEditReviewForm({ rating: r.rating, text: r.text }); }} data-testid={`edit-review-${r.id}`}
                      className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold border border-zinc-300 rounded-md px-3 py-1.5 text-zinc-700 hover:border-zinc-500 transition-colors duration-200">
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </button>
                    <button onClick={() => deleteReview(r.id)} data-testid={`delete-review-${r.id}`}
                      className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold border border-zinc-300 rounded-md px-3 py-1.5 text-red-600 hover:bg-red-50 transition-colors duration-200">
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        )}

        {tab === "verifications" && (
          <div>
          <div className="flex flex-col sm:flex-row gap-2 mb-4" data-testid="verifications-filters">
            <input value={verifFilter.q} onChange={(e) => setVerifFilter({ ...verifFilter, q: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") loadVerifications(); }}
              placeholder="Search name, email or company" data-testid="verifications-search"
              className="flex-1 h-10 px-3 border border-zinc-300 rounded-md text-sm" />
            <select value={verifFilter.status} onChange={(e) => { const f = { ...verifFilter, status: e.target.value }; setVerifFilter(f); loadVerifications(f); }}
              data-testid="verifications-filter-status" className="sm:w-44 h-10 px-2 border border-zinc-300 rounded-md text-sm bg-white">
              <option value="">All statuses</option>
              <option value="pending">Pending review</option>
              <option value="none">Not submitted</option>
              <option value="verified">Verified</option>
              <option value="rejected">Rejected</option>
            </select>
            <select value={verifFilter.role} onChange={(e) => { const f = { ...verifFilter, role: e.target.value }; setVerifFilter(f); loadVerifications(f); }}
              data-testid="verifications-filter-role" className="sm:w-40 h-10 px-2 border border-zinc-300 rounded-md text-sm bg-white">
              <option value="">Everyone</option>
              <option value="employer">Employers</option>
              <option value="candidate">Jobseekers</option>
            </select>
            <button onClick={() => loadVerifications()} data-testid="verifications-filter-apply"
              className="h-10 px-4 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200">Apply</button>
          </div>
          {verifError ? (
            mfaRequired ? (
              <MfaSetupCard />
            ) : (
            <div className="border border-red-200 bg-red-50/50 rounded-md p-10 text-center" data-testid="verifications-error">
              <p className="text-sm font-semibold text-red-700">Couldn't load verifications</p>
              <p className="text-xs text-red-600 mt-1">Refresh the page to try again.</p>
            </div>
            )
          ) : verifications.length === 0 && !tabLoading ? (
            <p className="text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-md p-10 text-center" data-testid="no-verifications">
              Nothing waiting for review.
            </p>
          ) : (
            <div className="space-y-3" data-testid="verifications-list">
              {pageSlice(verifications, verifPage).map((u) => {
                const v = u.verification || {};
                return (
                  <div key={u.id} className="border border-zinc-200 rounded-md p-5 bg-white" data-testid={`verification-${u.id}`}>
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-display font-bold text-zinc-950">{u.company_name || u.name}</p>
                          <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${
                            v.status === "verified" ? "bg-green-50 text-green-700" :
                            v.status === "pending" ? "bg-amber-50 text-amber-700" :
                            v.status === "rejected" ? "bg-red-50 text-red-700" : "bg-zinc-100 text-zinc-500"}`}
                            data-testid={`verification-status-${u.id}`}>
                            {v.status === "none" || !v.status ? "Not submitted" : v.status}
                          </span>
                          <span className="text-xs text-zinc-400">{u.role}</span>
                        </div>
                        <p className="text-sm text-zinc-500 mt-1">{u.name} · {u.email}</p>
                        <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid={`verification-meta-${u.id}`}>
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${u.email_verified ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>Email {u.email_verified ? "verified" : "unverified"}</span>
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${u.phone_verified ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>SMS {u.phone_verified ? "verified" : "unverified"}</span>
                          {!u.is_active && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-100 text-red-700">Deactivated</span>}
                          {u.role === "employer" && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">{u.jobs_count || 0} job{(u.jobs_count || 0) === 1 ? "" : "s"}</span>}
                          {u.ch_company_number && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">CH {u.ch_company_number}{u.ch_status ? ` · ${u.ch_status}` : ""}</span>}
                        </div>
                        <p className="text-[11px] text-zinc-400 mt-1.5">
                          Joined {u.created_at ? new Date(u.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"} · last active {u.last_seen ? timeAgo(u.last_seen) : "never"}
                          {v.reviewed_at ? ` · reviewed ${new Date(v.reviewed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ""}
                          {v.chased_at ? ` · chased ${new Date(v.chased_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ""}
                        </p>
                        {v.note && <p className="text-xs text-zinc-600 mt-1.5 border-l-2 border-zinc-200 pl-3" data-testid={`verification-note-${u.id}`}>Note: {v.note}</p>}
                        {u.role === "employer" && (
                          <div className="text-sm text-zinc-600 mt-2 space-y-0.5">
                            <p><span className="font-medium text-zinc-800">Company No:</span> {v.company_number || "-"}</p>
                            <p><span className="font-medium text-zinc-800">Registered address:</span> {v.registered_address || "-"}</p>
                            <p><span className="font-medium text-zinc-800">Director:</span> {v.director_name || "-"}</p>
                          </div>
                        )}
                        {(v.documents || []).length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {v.documents.map((d) => (
                              /\.(jpe?g|png|webp)$/i.test(d.filename || "") ? (
                                <button key={d.path} type="button" onClick={() => setDocViewer({ src: fileUrl(d.path), alt: d.filename })}
                                  data-testid={`vdoc-${u.id}-${d.filename}`}
                                  className="text-xs font-medium text-blue-700 border border-blue-200 bg-blue-50 rounded-md px-3 py-1.5 hover:bg-blue-100 transition-colors duration-200">
                                  {d.filename}
                                </button>
                              ) : (
                                <a key={d.path} href={fileUrl(d.path)} target="_blank" rel="noopener noreferrer"
                                  data-testid={`vdoc-${u.id}-${d.filename}`}
                                  className="text-xs font-medium text-blue-700 border border-blue-200 bg-blue-50 rounded-md px-3 py-1.5 hover:bg-blue-100 transition-colors duration-200">
                                  {d.filename}
                                </a>
                              )
                            ))}
                          </div>
                        )}
                        {v.submitted_at && (
                          <p className="text-xs text-zinc-400 mt-2">Submitted {new Date(v.submitted_at).toLocaleString("en-GB")}</p>
                        )}
                        {docViewer && <ImageLightbox src={docViewer.src} alt={docViewer.alt} testid="vdoc-viewer" onClose={() => setDocViewer(null)} />}
                      </div>
                      <div className="flex gap-2 shrink-0 flex-wrap">
                        {v.status === "pending" && (
                          <>
                            <button onClick={() => reviewVerification(u.id, "verified")} data-testid={`verify-approve-${u.id}`}
                              className="inline-flex items-center gap-1.5 h-9 px-4 bg-green-600 text-white text-xs font-semibold rounded-md hover:bg-green-700 transition-colors duration-200">
                              <Check className="h-3.5 w-3.5" /> Verify
                            </button>
                            <button onClick={() => reviewVerification(u.id, "rejected")} data-testid={`verify-reject-${u.id}`}
                              className="inline-flex items-center gap-1.5 h-9 px-4 bg-red-600 text-white text-xs font-semibold rounded-md hover:bg-red-700 transition-colors duration-200">
                              <X className="h-3.5 w-3.5" /> Reject
                            </button>
                          </>
                        )}
                        <button onClick={() => startEditVerif(u)} data-testid={`verify-edit-${u.id}`}
                          title="Edit the submitted details (admin only)"
                          className="inline-flex items-center gap-1.5 h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md text-zinc-700 hover:border-zinc-500 transition-colors duration-200">
                          <Pencil className="h-3.5 w-3.5" /> Edit details
                        </button>
                        <button onClick={() => startEditUser(u)} data-testid={`verify-edit-user-${u.id}`}
                          title="Edit the account itself (name, email, location, role, status)"
                          className="inline-flex items-center gap-1.5 h-9 px-4 border border-zinc-300 rounded-md text-xs font-semibold text-zinc-700 hover:border-zinc-500 transition-colors duration-200">
                          <Pencil className="h-3.5 w-3.5" /> Edit account
                        </button>
                        <select value={v.status || "none"} onChange={(e) => { if (e.target.value !== (v.status || "none")) reviewVerification(u.id, e.target.value); }}
                          data-testid={`verify-set-status-${u.id}`} title="Set verification status directly"
                          className="h-9 px-2 border border-zinc-300 rounded-md text-xs bg-white">
                          <option value="pending">Pending review</option>
                          <option value="none">Not submitted</option>
                          <option value="verified">Verified</option>
                          <option value="rejected">Rejected</option>
                        </select>
                        {v.status !== "verified" && (
                          <button onClick={() => chaseVerification(u)} data-testid={`verify-chase-${u.id}`}
                            title="Email them asking for their verification documents"
                            className="inline-flex items-center gap-1.5 h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md text-zinc-700 hover:border-zinc-500 transition-colors duration-200">
                            <Send className="h-3.5 w-3.5" /> Chase documents
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <Pager page={verifPage} total={verifications.length} onChange={setVerifPage} testid="verifications-pager" />
            </div>
          )}
          </div>
        )}

        {tab === "verifications" && chReview && (
          <>
          <div className="mt-8 border-t border-zinc-200 pt-6" data-testid="ch-join-requests-section">
            <p className="font-display font-bold text-zinc-950">Company access requests</p>
            <p className="text-xs text-zinc-500 mt-1 mb-4">
              Recruiters who registered with an existing Companies House number. They have no company access until approved here.
            </p>
            {chJoins.length === 0 ? (
              <p className="text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-md p-8 text-center" data-testid="ch-join-empty">
                No pending access requests.
              </p>
            ) : (
              <div className="space-y-3" data-testid="ch-join-list">
                {chJoins.map((jr) => (
                  <div key={jr.id} className="border border-zinc-200 rounded-md p-5 bg-white" data-testid={`ch-join-${jr.id}`}>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 justify-between">
                      <div>
                        <p className="font-semibold text-zinc-950">{jr.name} <span className="text-zinc-500 font-normal">{jr.email}</span></p>
                        <p className="text-xs text-zinc-500 mt-0.5">
                          Asks to join <strong>{jr.company_name}</strong> ({jr.company_number}) · owner {jr.company_owner} · {jr.company_live_jobs} live jobs
                          {jr.requested_at && ` · ${new Date(jr.requested_at).toLocaleString("en-GB")}`}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => chJoinApprove(jr.id)} disabled={chBusy === `join-${jr.id}`} data-testid={`ch-join-approve-${jr.id}`}
                          className="inline-flex items-center gap-1.5 h-9 px-4 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50 transition-colors duration-200">
                          <Check className="h-3.5 w-3.5" /> {chBusy === `join-${jr.id}` ? "Working…" : "Approve"}
                        </button>
                        <button onClick={() => chJoinDecline(jr.id)} disabled={chBusy === `join-${jr.id}`} data-testid={`ch-join-decline-${jr.id}`}
                          className="inline-flex items-center gap-1.5 h-9 px-4 bg-red-600 text-white text-xs font-semibold rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors duration-200">
                          <X className="h-3.5 w-3.5" /> Decline
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-8 border-t border-zinc-200 pt-6" data-testid="ch-companies-section">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <p className="font-display font-bold text-zinc-950">Company records</p>
                <p className="text-xs text-zinc-500 mt-1">
                  {chCompanies ? `${chCompanies.total} companies, each shown once. Duplicate company numbers: ${chCompanies.duplicate_numbers}.` : "Loading…"}
                </p>
              </div>
              <button onClick={chConsolidate} disabled={chBusy === "consolidate"} data-testid="ch-consolidate"
                className="inline-flex items-center gap-1.5 h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 disabled:opacity-50 transition-colors duration-200">
                <RefreshCw className={`h-3.5 w-3.5 ${chBusy === "consolidate" ? "animate-spin" : ""}`} /> Consolidate duplicates
              </button>
            </div>
            <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="ch-companies-list">
              {(chCompanies?.companies || []).map((c) => (
                <div key={c.id} className="border border-zinc-200 rounded-md px-4 py-3 bg-white flex flex-wrap items-center gap-x-4 gap-y-1 justify-between" data-testid={`ch-company-${c.id}`}>
                  <div className="min-w-0">
                    <p className="font-semibold text-zinc-950 text-sm truncate">{c.company_name || "(no company name)"}
                      {c.company_number && <span className="text-zinc-500 font-normal"> · {c.company_number}</span>}
                    </p>
                    <p className="text-xs text-zinc-500">
                      owner {c.owner}{c.members.length > 0 && ` · +${c.members.length} recruiter${c.members.length > 1 ? "s" : ""}: ${c.members.map((m) => m.email).join(", ")}`}
                    </p>
                  </div>
                  <span className="text-xs font-semibold text-zinc-600 shrink-0">{c.live_jobs} live jobs</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 border-t border-zinc-200 pt-6" data-testid="ch-review-section">
            <p className="font-display font-bold text-zinc-950">Companies House</p>
            <p className="text-xs text-zinc-500 mt-1 mb-4">
              {chReview.linked} of {chReview.total_employers} employer accounts are linked to an official record.
              Everything below needs a decision: unlinked companies, inactive records, mismatches, duplicates or API failures.
            </p>
            {chReview.review.length === 0 ? (
              <p className="text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-md p-8 text-center" data-testid="ch-review-empty">
                Nothing to review, every linked record matches Companies House.
              </p>
            ) : (
              <div className="space-y-3" data-testid="ch-review-list">
                {chReview.review.map((c) => (
                  <div key={c.id} className="border border-zinc-200 rounded-md p-5 bg-white" data-testid={`ch-review-${c.id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-display font-bold text-zinc-950">{c.company_name || c.name}</p>
                      {c.flags.map((f) => (
                        <span key={f} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                          f === "duplicate_number" || f === "api_error" ? "bg-red-100 text-red-700" :
                          f === "inactive_company" ? "bg-amber-100 text-amber-700" : "bg-zinc-100 text-zinc-600"}`}
                          data-testid={`ch-flag-${c.id}-${f}`}>
                          {f.replace(/_/g, " ")}
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-zinc-500 mt-1">{c.name} · {c.email}</p>
                    {c.company_number && (
                      <p className="text-sm text-zinc-600 mt-1.5">
                        <span className="font-medium text-zinc-800">CH:</span> {c.company_number} · {c.ch_status || "?"}
                        {c.ch_type && ` · ${c.ch_type}`}
                        {c.ch_incorporated && ` · inc. ${c.ch_incorporated}`}
                        {c.ch_sic?.length > 0 && ` · SIC ${c.ch_sic.join(", ")}`}
                      </p>
                    )}
                    {c.audit.length > 0 && (
                      <ul className="mt-2 space-y-1" data-testid={`ch-audit-${c.id}`}>
                        {c.audit.map((a) => (
                          <li key={`${a.at}-${a.action}`} className="text-xs text-zinc-500">
                            {a.at ? new Date(a.at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                            {" · "}{a.actor} · {a.action}{a.reason ? ` · ${a.reason}` : ""}
                            {Object.keys(a.changes || {}).length > 0 && ` · ${Object.entries(a.changes).map(([f, ch]) => `${f}: "${ch.old ?? "—"}" → "${ch.new ?? "—"}"`).join("; ")}`}
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {c.company_number && (
                        <button onClick={() => chRefresh(c.id)} disabled={chBusy === `refresh-${c.id}`} data-testid={`ch-refresh-${c.id}`}
                          className="h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200 disabled:opacity-50">
                          {chBusy === `refresh-${c.id}` ? "Refreshing…" : "Refresh from Companies House"}
                        </button>
                      )}
                      {c.flags.includes("manual_review") && (
                        <button onClick={() => chApprove(c.id)} disabled={chBusy === `approve-${c.id}`} data-testid={`ch-approve-${c.id}`}
                          className="h-9 px-4 bg-green-600 text-white text-xs font-semibold rounded-md hover:bg-green-700 transition-colors duration-200 disabled:opacity-50">
                          Approve exception
                        </button>
                      )}
                      <button onClick={() => { setChCorrectFor(chCorrectFor === c.id ? null : c.id); setChCorrectForm({ field: "company_name", value: "", reason: "" }); }}
                        data-testid={`ch-correct-open-${c.id}`}
                        className="h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                        Authorised correction
                      </button>
                    </div>
                    {chCorrectFor === c.id && (
                      <div className="mt-3 border border-zinc-200 rounded-md p-4 bg-zinc-50/50 space-y-2" data-testid={`ch-correct-form-${c.id}`}>
                        <div className="flex flex-wrap gap-2">
                          <select value={chCorrectForm.field} onChange={(e) => setChCorrectForm({ ...chCorrectForm, field: e.target.value })}
                            data-testid={`ch-correct-field-${c.id}`} className="h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white">
                            <option value="company_name">Legal name</option>
                            <option value="company_address">Registered office</option>
                            <option value="company_reg_number">Company number</option>
                            <option value="ch_status">Status</option>
                            <option value="ch_type">Company type</option>
                          </select>
                          <input value={chCorrectForm.value} placeholder="New value" data-testid={`ch-correct-value-${c.id}`}
                            onChange={(e) => setChCorrectForm({ ...chCorrectForm, value: e.target.value })}
                            className="flex-1 min-w-[12rem] h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                        </div>
                        <input value={chCorrectForm.reason} placeholder="Reason (recorded in the audit history)" required
                          data-testid={`ch-correct-reason-${c.id}`}
                          onChange={(e) => setChCorrectForm({ ...chCorrectForm, reason: e.target.value })}
                          className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                        <div className="flex gap-2">
                          <button onClick={() => chCorrect(c.id)} disabled={chBusy === `correct-${c.id}` || !chCorrectForm.value.trim() || !chCorrectForm.reason.trim()}
                            data-testid={`ch-correct-submit-${c.id}`}
                            className="h-9 px-4 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50 transition-colors duration-200">
                            {chBusy === `correct-${c.id}` ? "Saving…" : "Save correction"}
                          </button>
                          <button onClick={() => setChCorrectFor(null)} data-testid={`ch-correct-cancel-${c.id}`}
                            className="h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          </>
        )}

        {mfaRequired && <div className="mb-6" data-testid="ct-mfa-banner"><MfaSetupCard /></div>}
        {tab === "ops" && <Ops />}
        {tab === "security" && <SecurityAbuse />}

        {tab === "livetraffic" && <LiveTraffic />}

        {tab === "campaigns" && (
          <div data-testid="campaigns-tab">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <p className="text-sm text-zinc-500 max-w-xl">Seasonal and one-off homepage campaigns, launch countdowns, snow at Christmas, fireworks, balloons and more. One live campaign shows on the homepage at a time; switch them on and off anytime.</p>
              <button onClick={() => setCampEdit({ name: "", headline: "", subtext: "", effect: "none", countdown_target: "", starts_at: "", ends_at: "", cta_label: "", cta_url: "", active: false })}
                data-testid="campaign-new"
                className="h-10 px-5 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 shrink-0">
                New campaign
              </button>
            </div>
            {campaigns.length === 0 ? (
              <p className="border border-dashed border-zinc-300 rounded-md p-10 text-center text-sm text-zinc-500" data-testid="campaigns-empty">
                No campaigns yet. Create your first one, the launch countdown is a good start.
              </p>
            ) : (
              <div className="border border-zinc-200 rounded-md overflow-hidden" data-testid="campaigns-list">
                {pageSlice(campaigns, campPage).map((c, i) => {
                  const st = campStatus(c);
                  return (
                    <div key={c.id} className={`px-4 py-3.5 bg-white flex items-center justify-between gap-3 flex-wrap ${i > 0 ? "border-t border-zinc-100" : ""}`} data-testid={`campaign-row-${i}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-zinc-950 truncate">{c.name}</p>
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${st.cls}`} data-testid={`campaign-status-${i}`}>{st.label}</span>
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">{(CAMPAIGN_EFFECTS.find((e) => e.id === c.effect) || CAMPAIGN_EFFECTS[0]).label}</span>
                        </div>
                        <p className="text-xs text-zinc-500 truncate mt-0.5">
                          {c.headline}
                          {c.countdown_target ? ` · counts down to ${new Date(c.countdown_target).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}
                          {c.starts_at || c.ends_at ? ` · window ${c.starts_at ? new Date(c.starts_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "now"} → ${c.ends_at ? new Date(c.ends_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "open"}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button onClick={() => toggleCampaign(c)} data-testid={`campaign-toggle-${i}`}
                          className={`h-8 px-3 text-xs font-semibold rounded-md border transition-colors duration-200 ${c.active ? "border-red-200 text-red-600 hover:bg-red-50" : "border-green-200 text-green-700 hover:bg-green-50"}`}>
                          {c.active ? "Switch off" : "Switch on"}
                        </button>
                        <button onClick={() => setCampEdit({ ...c, countdown_target: campLocal(c.countdown_target), starts_at: campLocal(c.starts_at), ends_at: campLocal(c.ends_at) })}
                          data-testid={`campaign-edit-${i}`}
                          className="h-8 px-3 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">Edit</button>
                        <button onClick={() => deleteCampaign(c)} data-testid={`campaign-delete-${i}`}
                          className="h-8 px-3 border border-zinc-300 text-xs font-semibold rounded-md text-red-600 hover:bg-red-50 transition-colors duration-200">Delete</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <Pager page={campPage} total={campaigns.length} onChange={setCampPage} testid="campaigns-pager" />
          </div>
        )}

        <Dialog open={!!campEdit} onOpenChange={() => setCampEdit(null)}>
          <DialogContent className="bg-white sm:max-w-lg" data-testid="campaign-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-xl font-bold">{campEdit?.id ? "Edit campaign" : "New campaign"}</DialogTitle>
            </DialogHeader>
            {campEdit && (
              <form onSubmit={saveCampaign} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Campaign name *</label>
                  <input required data-testid="campaign-form-name" value={campEdit.name} onChange={(e) => setCampEdit({ ...campEdit, name: e.target.value })}
                    placeholder="e.g. Official launch, Christmas 2026"
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Headline on the homepage *</label>
                  <input required data-testid="campaign-form-headline" value={campEdit.headline} onChange={(e) => setCampEdit({ ...campEdit, headline: e.target.value })}
                    placeholder="e.g. HiredToday officially launches in…"
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Subtext</label>
                  <input data-testid="campaign-form-subtext" value={campEdit.subtext} onChange={(e) => setCampEdit({ ...campEdit, subtext: e.target.value })}
                    placeholder="One supporting line under the headline"
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Homepage effect</label>
                  <select value={campEdit.effect} onChange={(e) => setCampEdit({ ...campEdit, effect: e.target.value })}
                    data-testid="campaign-form-effect"
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600">
                    {CAMPAIGN_EFFECTS.map((ef) => <option key={ef.id} value={ef.id}>{ef.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Countdown to (optional)</label>
                  <input type="datetime-local" data-testid="campaign-form-countdown" value={campEdit.countdown_target}
                    onChange={(e) => setCampEdit({ ...campEdit, countdown_target: e.target.value })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                  <p className="text-xs text-zinc-400 mt-1">Shows a live days/hours/mins/seconds timer. Leave empty for no countdown.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-zinc-700 block mb-1.5">Show from (optional)</label>
                    <input type="datetime-local" data-testid="campaign-form-starts" value={campEdit.starts_at}
                      onChange={(e) => setCampEdit({ ...campEdit, starts_at: e.target.value })}
                      className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-zinc-700 block mb-1.5">Show until (optional)</label>
                    <input type="datetime-local" data-testid="campaign-form-ends" value={campEdit.ends_at}
                      onChange={(e) => setCampEdit({ ...campEdit, ends_at: e.target.value })}
                      className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-zinc-700 block mb-1.5">Button label (optional)</label>
                    <input data-testid="campaign-form-cta-label" value={campEdit.cta_label} onChange={(e) => setCampEdit({ ...campEdit, cta_label: e.target.value })}
                      placeholder="e.g. See what's new"
                      className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-zinc-700 block mb-1.5">Button link</label>
                    <input data-testid="campaign-form-cta-url" value={campEdit.cta_url} onChange={(e) => setCampEdit({ ...campEdit, cta_url: e.target.value })}
                      placeholder="/jobs or https://…"
                      className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 cursor-pointer">
                  <input type="checkbox" checked={campEdit.active} onChange={(e) => setCampEdit({ ...campEdit, active: e.target.checked })}
                    data-testid="campaign-form-active" className="h-4 w-4 accent-zinc-900" />
                  Live on the homepage (respects the show-from/until window)
                </label>
                <button type="submit" disabled={campSaving} data-testid="campaign-form-save"
                  className="w-full h-11 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-60 transition-colors duration-200">
                  {campSaving ? "Saving…" : campEdit.id ? "Save changes" : "Create campaign"}
                </button>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {tab === "messages" && (
          messages.length === 0 ? (
            <p className="text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-md p-10 text-center" data-testid="no-messages">
              No messages yet.
            </p>
          ) : (
            <div className="space-y-3" data-testid="admin-messages-list">
              {pageSlice(messages, msgPage).map((m) => (
                <div key={m.id} className={`border rounded-md p-5 bg-white flex items-start justify-between gap-4 ${m.read ? "border-zinc-200" : "border-blue-200 bg-blue-50/30"}`}
                  data-testid={`message-${m.id}`}>
                  <div>
                    <p className="font-semibold text-zinc-950">{m.name} <span className="font-normal text-zinc-500">&lt;{m.email}&gt;</span></p>
                    <p className="mt-2 text-sm text-zinc-700 whitespace-pre-line">{m.message}</p>
                    <p className="mt-2 text-xs text-zinc-400">{new Date(m.created_at).toLocaleString("en-GB")}</p>
                  </div>
                  {!m.read && (
                    <button onClick={() => markRead(m.id)} data-testid={`mark-read-${m.id}`}
                      className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold border border-zinc-300 rounded-md px-3 py-1.5 hover:border-zinc-500 transition-colors duration-200">
                      <MailOpen className="h-3.5 w-3.5" /> Mark read
                    </button>
                  )}
                </div>
              ))}
              <Pager page={msgPage} total={messages.length} onChange={setMsgPage} testid="admin-messages-pager" />
            </div>
          )
        )}
      </div>

      {/* Add user dialog */}
      <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold">Add a user</DialogTitle>
          </DialogHeader>
          <form onSubmit={createUser} className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Full name</label>
              <input required data-testid="add-user-name" value={addUserForm.name}
                onChange={(e) => setAddUserForm({ ...addUserForm, name: e.target.value })}
                className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Email</label>
              <input required type="email" data-testid="add-user-email" value={addUserForm.email}
                onChange={(e) => setAddUserForm({ ...addUserForm, email: e.target.value })}
                className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Temporary password (optional)</label>
              <input type="text" minLength={8} data-testid="add-user-password" value={addUserForm.password}
                onChange={(e) => setAddUserForm({ ...addUserForm, password: e.target.value })}
                placeholder="Leave blank and they'll set their own"
                className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              <p className="text-xs text-zinc-400 mt-1.5">Either way, we email them an invite with a secure link to set their own password.</p>
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Account type</label>
              <select data-testid="add-user-role" value={addUserForm.role}
                onChange={(e) => setAddUserForm({ ...addUserForm, role: e.target.value })}
                className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm bg-white">
                <option value="candidate">Candidate</option>
                <option value="employer">Employer</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            {addUserForm.role === "employer" && (
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Company name</label>
                <input data-testid="add-user-company" value={addUserForm.company_name}
                  onChange={(e) => setAddUserForm({ ...addUserForm, company_name: e.target.value })}
                  className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
            )}
            <button type="submit" disabled={addUserSaving} data-testid="add-user-submit"
              className="w-full h-11 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
              {addUserSaving ? "Creating…" : "Create account"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {manageAppId && (
        <CandidatePanel applicationId={manageAppId} onClose={() => setManageAppId(null)} onChanged={loadAnalytics} />
      )}
      {reportJobId && <JobReport jobId={reportJobId} onClose={() => setReportJobId(null)} />}

      {/* Edit verification details dialog */}
      <Dialog open={!!editVerif} onOpenChange={() => setEditVerif(null)}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="edit-verif-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold">Verification details</DialogTitle>
          </DialogHeader>
          {editVerifForm && (
            <form onSubmit={saveEditVerif} className="space-y-4 mt-2">
              <p className="text-xs text-zinc-500">Only you can see or change these. They are never shown publicly.</p>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Companies House number</label>
                <input data-testid="edit-verif-company-number" value={editVerifForm.company_number}
                  onChange={(e) => setEditVerifForm({ ...editVerifForm, company_number: e.target.value })}
                  className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Registered address</label>
                <input data-testid="edit-verif-address" value={editVerifForm.registered_address}
                  onChange={(e) => setEditVerifForm({ ...editVerifForm, registered_address: e.target.value })}
                  className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Director's full name</label>
                <input data-testid="edit-verif-director" value={editVerifForm.director_name}
                  onChange={(e) => setEditVerifForm({ ...editVerifForm, director_name: e.target.value })}
                  className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Internal / review note</label>
                <textarea data-testid="edit-verif-note" value={editVerifForm.note} rows={2}
                  onChange={(e) => setEditVerifForm({ ...editVerifForm, note: e.target.value })}
                  placeholder="Shown to the user when rejecting; otherwise an internal note"
                  className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
              <button type="submit" disabled={editVerifSaving} data-testid="edit-verif-save"
                className="w-full h-11 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                {editVerifSaving ? "Saving…" : "Save details"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit user dialog */}
      <Dialog open={!!editReview} onOpenChange={() => setEditReview(null)}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="edit-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold">Edit review</DialogTitle>
          </DialogHeader>
          {editReviewForm && editReview && (
            <form onSubmit={saveReviewEdit} className="space-y-4 mt-2">
              <p className="text-xs text-zinc-500">
                {editReview.company_name || "Unknown company"} · {editReview.candidate_name || "Candidate"}
                {editReview.created_at ? ` · ${new Date(editReview.created_at).toLocaleString("en-GB")}` : ""}
              </p>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Rating</label>
                <select data-testid="edit-review-rating" value={editReviewForm.rating}
                  onChange={(e) => setEditReviewForm({ ...editReviewForm, rating: Number(e.target.value) })}
                  className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm bg-white">
                  {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} / 5</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Review text</label>
                <textarea required rows={5} data-testid="edit-review-text" value={editReviewForm.text}
                  onChange={(e) => setEditReviewForm({ ...editReviewForm, text: e.target.value })}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
              <button type="submit" data-testid="edit-review-save"
                className="w-full h-11 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200">
                Save changes
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold">Edit user</DialogTitle>
          </DialogHeader>
          {editUserForm && (
            <form onSubmit={saveEditUser} className="space-y-4 mt-2">
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Full name</label>
                <input required data-testid="edit-user-name" value={editUserForm.name}
                  onChange={(e) => setEditUserForm({ ...editUserForm, name: e.target.value })}
                  className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Email</label>
                <input required type="email" data-testid="edit-user-email" value={editUserForm.email}
                  onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
                  className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Company name</label>
                <input data-testid="edit-user-company" value={editUserForm.company_name}
                  onChange={(e) => setEditUserForm({ ...editUserForm, company_name: e.target.value })}
                  className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Location</label>
                <input data-testid="edit-user-location" value={editUserForm.location || ""}
                  onChange={(e) => setEditUserForm({ ...editUserForm, location: e.target.value })}
                  placeholder="e.g. Leeds"
                  className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Role</label>
                  <select data-testid="edit-user-role" value={editUserForm.role}
                    onChange={(e) => setEditUserForm({ ...editUserForm, role: e.target.value })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm bg-white">
                    <option value="candidate">Candidate</option>
                    <option value="employer">Employer</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Status</label>
                  <select data-testid="edit-user-active" value={editUserForm.is_active ? "1" : "0"}
                    onChange={(e) => setEditUserForm({ ...editUserForm, is_active: e.target.value === "1" })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm bg-white">
                    <option value="1">Active</option>
                    <option value="0">Deactivated</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Team of (company owner email)</label>
                <input data-testid="edit-user-team-owner" value={editUserForm.parent_employer_email ?? ""}
                  onChange={(e) => setEditUserForm({ ...editUserForm, parent_employer_email: e.target.value })}
                  placeholder="e.g. owner@company.co.uk. Links this account to that company's jobs and applicants."
                  className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                {editUser?.parent_employer_id && editUserForm.parent_employer_email === null && (
                  <button type="button" onClick={() => setEditUserForm({ ...editUserForm, parent_employer_email: "" })}
                    data-testid="edit-user-unlink"
                    className="text-xs font-semibold text-red-600 hover:text-red-800 mt-1 transition-colors duration-200">
                    Currently linked to a company team. Unlink
                  </button>
                )}
                {editUserForm.parent_employer_email === "" && (
                  <p className="text-xs text-amber-600 mt-1">Saving now will unlink this account from its company team.</p>
                )}
              </div>
              <button type="submit" disabled={editUserSaving} data-testid="edit-user-save"
                className="w-full h-11 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                {editUserSaving ? "Saving…" : "Save changes"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editJob} onOpenChange={() => setEditJob(null)}>
        <DialogContent className="bg-white sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="admin-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold">Edit job (admin)</DialogTitle>
          </DialogHeader>
          {editForm && (
            <form onSubmit={saveEdit} className="space-y-4 mt-2">
              {editJob && (
                <div className="border border-zinc-200 rounded-md p-4 bg-zinc-50/60 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-xs" data-testid="admin-edit-meta">
                  <p className="text-zinc-500">Slug<br /><span className="font-mono text-zinc-800 break-all">{editJob.slug}</span></p>
                  <p className="text-zinc-500">Job ID<br /><span className="font-mono text-zinc-800 break-all">{editJob.id}</span></p>
                  <p className="text-zinc-500">Posted<br /><span className="font-medium text-zinc-800">{editJob.created_at ? new Date(editJob.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</span></p>
                  <p className="text-zinc-500">Went live<br /><span className="font-medium text-zinc-800">{editJob.live_at ? new Date(editJob.live_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</span></p>
                  <p className="text-zinc-500">Expires<br /><span className="font-medium text-zinc-800">{editJob.expires_at ? new Date(editJob.expires_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</span></p>
                  <p className="text-zinc-500">Partner distribution<br /><span className="font-medium text-zinc-800">{editJob.partner_distribution ? `On${editJob.partner_distribution_at ? ` since ${new Date(editJob.partner_distribution_at).toLocaleDateString("en-GB")}` : ""}${editJob.partner_distribution_by ? ` by ${editJob.partner_distribution_by}` : ""}` : "Off"}</span></p>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Title</label>
                  <input data-testid="admin-edit-title" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm" />
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Company</label>
                  <input data-testid="admin-edit-company" value={editForm.company_name} onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm" />
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Location</label>
                  <input data-testid="admin-edit-location" value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm" />
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Category</label>
                  <input data-testid="admin-edit-category" value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm" />
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Job type</label>
                  <select data-testid="admin-edit-job-type" value={editForm.job_type} onChange={(e) => setEditForm({ ...editForm, job_type: e.target.value })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm bg-white">
                    {["Full-time", "Part-time", "Contract", "Temporary", "Apprenticeship"].map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Workplace</label>
                  <select data-testid="admin-edit-workplace" value={editForm.workplace} onChange={(e) => setEditForm({ ...editForm, workplace: e.target.value })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm bg-white">
                    {["On-site", "Hybrid", "Remote"].map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Salary from (£)</label>
                  <input type="number" min="0" data-testid="admin-edit-salary-min" value={editForm.salary_min} onChange={(e) => setEditForm({ ...editForm, salary_min: e.target.value })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm" />
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Salary to (£)</label>
                  <input type="number" min="0" data-testid="admin-edit-salary-max" value={editForm.salary_max} onChange={(e) => setEditForm({ ...editForm, salary_max: e.target.value })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm" />
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Salary period</label>
                  <select data-testid="admin-edit-salary-period" value={editForm.salary_period} onChange={(e) => setEditForm({ ...editForm, salary_period: e.target.value })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm bg-white">
                    {[["year", "per year"], ["month", "per month"], ["week", "per week"], ["day", "per day"], ["hour", "per hour"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Application email</label>
                  <input type="email" data-testid="admin-edit-application-email" value={editForm.application_email} onChange={(e) => setEditForm({ ...editForm, application_email: e.target.value })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm" />
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Apply method</label>
                  <select data-testid="admin-edit-apply-method" value={editForm.apply_method} onChange={(e) => setEditForm({ ...editForm, apply_method: e.target.value })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm bg-white">
                    <option value="platform">On HiredToday</option>
                    <option value="external">HiredToday + employer website</option>
                  </select>
                </div>
                {editForm.apply_method === "external" && (
                  <div>
                    <label className="text-sm font-medium text-zinc-700 block mb-1.5">External apply URL</label>
                    <input data-testid="admin-edit-external-url" value={editForm.external_apply_url} onChange={(e) => setEditForm({ ...editForm, external_apply_url: e.target.value })}
                      className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm" />
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Company website</label>
                  <input data-testid="admin-edit-company-website" value={editForm.company_website} onChange={(e) => setEditForm({ ...editForm, company_website: e.target.value })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm" />
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Tier</label>
                  <select data-testid="admin-edit-tier" value={editForm.tier} onChange={(e) => setEditForm({ ...editForm, tier: e.target.value })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm bg-white">
                    <option value="free">Free</option>
                    <option value="standard">Standard</option>
                    <option value="featured">Featured</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Status</label>
                  <select data-testid="admin-edit-status" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm bg-white">
                    <option value="awaiting_payment">Awaiting payment</option>
                    <option value="pending_review">In review</option>
                    <option value="live">Live</option>
                    <option value="rejected">Rejected</option>
                    <option value="expired">Expired</option>
                    <option value="closed">Closed</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Note to employer</label>
                  <input data-testid="admin-edit-note" value={editForm.admin_note} onChange={(e) => setEditForm({ ...editForm, admin_note: e.target.value })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm" />
                </div>
              </div>
              <div className="flex flex-wrap gap-4 border border-zinc-200 rounded-md p-4" data-testid="admin-edit-flags">
                {[
                  ["urgent", "Urgent label", "admin-edit-urgent"],
                  ["featured", "Featured", "admin-edit-featured"],
                  ["show_salary", "Show salary publicly", "admin-edit-show-salary"],
                  ["partner_distribution", "Partner-board distribution", "admin-edit-partner-distribution"],
                ].map(([k, label, tid]) => (
                  <label key={k} className="inline-flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
                    <input type="checkbox" checked={!!editForm[k]} onChange={(e) => setEditForm({ ...editForm, [k]: e.target.checked })}
                      data-testid={tid} className="h-4 w-4 accent-zinc-900" />
                    {label}
                  </label>
                ))}
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Description</label>
                <textarea rows={6} data-testid="admin-edit-description" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="w-full px-3 py-2.5 border border-zinc-300 rounded-md text-sm resize-y" />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Company description</label>
                <textarea rows={3} data-testid="admin-edit-company-description" value={editForm.company_description} onChange={(e) => setEditForm({ ...editForm, company_description: e.target.value })}
                  className="w-full px-3 py-2.5 border border-zinc-300 rounded-md text-sm resize-y" />
              </div>
              <div className="border border-zinc-200 rounded-md p-4" data-testid="admin-edit-boost">
                <p className="text-sm font-semibold text-zinc-950">Location Boost areas</p>
                <p className="text-xs text-zinc-500 mt-0.5">This job also appears in searches for these areas (up to 10).</p>
                {(editForm.boost_locations || []).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3" data-testid="admin-edit-boost-chips">
                    {editForm.boost_locations.map((l) => (
                      <span key={l} className="inline-flex items-center gap-1.5 bg-zinc-100 text-zinc-800 text-xs font-medium px-2.5 py-1.5 rounded-full"
                        data-testid={`admin-boost-chip-${l.replace(/\s+/g, "-").toLowerCase()}`}>
                        {l}
                        <button type="button" onClick={() => setEditForm({ ...editForm, boost_locations: editForm.boost_locations.filter((x) => x !== l) })}
                          aria-label={`Remove ${l}`} data-testid={`admin-boost-remove-${l.replace(/\s+/g, "-").toLowerCase()}`}
                          className="text-zinc-400 hover:text-red-600 transition-colors duration-200">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  <input value={adminBoostInput} onChange={(e) => setAdminBoostInput(e.target.value)} data-testid="admin-boost-input"
                    placeholder="Add an area (e.g. Dagenham)" maxLength={60}
                    className="flex-1 h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                  <button type="button" data-testid="admin-boost-add" disabled={!adminBoostInput.trim()}
                    onClick={() => {
                      const v = adminBoostInput.trim();
                      if (!v) return;
                      setEditForm((f) => ({ ...f, boost_locations: [...new Set([...(f.boost_locations || []), v])].slice(0, 10) }));
                      setAdminBoostInput("");
                    }}
                    className="h-10 px-4 border border-zinc-300 text-xs font-semibold rounded-md text-zinc-700 hover:border-zinc-500 disabled:opacity-50 transition-colors duration-200">
                    Add
                  </button>
                </div>
              </div>
              <button type="submit" disabled={saving} data-testid="admin-edit-save"
                className="h-11 px-8 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50">
                {saving ? "Saving…" : "Save changes"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}

const CONTROL_TOWER_ROLES = ["admin"];

export default function ControlTower() {
  return (
    <ProtectedRoute roles={CONTROL_TOWER_ROLES}>
      <ControlTowerInner />
    </ProtectedRoute>
  );
}
